import { makeApi, Zodios, type ZodiosOptions } from '@zodios/core'
import { z } from 'zod'

const EndUserIdField = z.string()
const IdentityIdField = z.string()
const TypeBb3Enum = z.enum(['STABLE', 'BETA', 'ELEVEN_LABS'])
const OwnerTypeEnum = z.enum(['DEMO', 'CUSTOMER'])
const AddAIPhoneLineViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    number: z.string(),
    type: TypeBb3Enum,
    owner_type: OwnerTypeEnum,
    name: z.string().optional(),
  })
  .passthrough()
const EmptyResponseSerializerEmptyResponse = z
  .object({ status: z.string().default('ok') })
  .partial()
  .passthrough()
const ProcessIdField = z.string()
const AddAIProcessSourceViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    process_id: ProcessIdField.uuid(),
    description: z.string().optional(),
  })
  .passthrough()
const AddFAQSourceViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    question: z.string(),
    answer: z.string(),
  })
  .passthrough()
const AddGISLayerSourceViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    name: z.string(),
    description: z.string(),
  })
  .passthrough()
const DifficultyEnum = z.enum(['easy', 'medium', 'hard', 'deflect'])
const AddQuestionViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    questions: z.unknown(),
    category: z.string().nullable(),
    difficulty: DifficultyEnum,
  })
  .passthrough()
const AITestQuestionIdField = z.string()
const AddQuestionViewResponseData = z.object({ id: AITestQuestionIdField.uuid() }).passthrough()
const AddTransferLineSourceViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    number: z.string(),
    extension: z.string().optional(),
    name: z.string(),
    description: z.string(),
    transfer_confirmation_prompt: z.string().optional(),
  })
  .passthrough()
const ConversationInitiationViewRequestData = z
  .object({
    caller_id: z.string(),
    agent_id: z.string(),
    called_number: z.string(),
    call_sid: z.string(),
  })
  .passthrough()
const ELDynamicVariables = z
  .object({
    chat_id: z.string(),
    caller_id: z.string(),
    called_number: z.string(),
    call_sid: z.string(),
    organization_name: z.string(),
    organization_id: z.string(),
    current_date_and_time: z.string(),
  })
  .passthrough()
const ELPromptConfig = z.object({ prompt: z.string() }).passthrough()
const ELVoiceConfig = z.object({ voice_id: z.string() }).passthrough()
const ELAgentConfig = z
  .object({
    prompt: ELPromptConfig.optional(),
    tts: ELVoiceConfig.optional(),
    first_message: z.string().nullable(),
    language: z.string().nullish(),
  })
  .passthrough()
const ELConversationConfigOverride = z
  .object({ agent: ELAgentConfig, tts: ELVoiceConfig })
  .partial()
  .passthrough()
const ConversationInitiationViewResponseData = z
  .object({
    dynamic_variables: ELDynamicVariables,
    conversation_config_override: ELConversationConfigOverride,
  })
  .passthrough()
const OrganizationIdField = z.string()
const CrawlDomainViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization: OrganizationIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    domain_crawl: z.string().url(),
    non_xml_sitemap: z.string().optional(),
  })
  .passthrough()
const CrawlDomainsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    domains: z.array(z.string().url()),
    cadence: z.string().optional(),
  })
  .passthrough()
const CrawlTaskIdField = z.string()
const CrawlTaskLogsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    crawl_task_ids: z.array(CrawlTaskIdField),
    show_links: z.boolean().optional().default(false),
  })
  .passthrough()
const ActionEnum = z.enum(['Add', 'Edit', 'Archive', 'Other'])
const ScrapeStatusEnum = z.enum(['pending', 'in_progress', 'finished', 'failed'])
const QuerySourcesLog = z
  .object({
    id: z.string().uuid().optional(),
    timestamp: z.string().datetime({ offset: true }),
    links_added: z.array(z.string()).optional(),
    links_removed: z.array(z.string()).optional(),
    links_scraped: z.array(z.string()).optional(),
    action: ActionEnum.optional(),
    description: z.string().nullish(),
    status: ScrapeStatusEnum.nullish(),
    failure_message: z.string().nullish(),
    source: z.string().uuid().nullish(),
    user: z.string().uuid().nullish(),
    crawl_task: z.string().uuid().nullish(),
  })
  .passthrough()
const CrawlTaskLogsViewResponseData = z.object({ logs: z.array(QuerySourcesLog) }).passthrough()
const InteractionTypeB42Enum = z.enum(['SEARCH', 'CHAT', 'VOICE', 'SMS', 'EMAIL'])
const CreateChatViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_id: OrganizationIdField.uuid(),
    interaction_type: InteractionTypeB42Enum,
  })
  .passthrough()
const ChatIdField = z.string()
const CreateChatViewResponseData = z.object({ chat_id: ChatIdField.uuid() }).passthrough()
const AIPhoneLineIdField = z.string()
const DeleteAIPhoneLineViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    ai_phone_line_id: AIPhoneLineIdField.uuid(),
  })
  .passthrough()
const DeleteCrawlTaskViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), crawl_task_id: CrawlTaskIdField.uuid() })
  .passthrough()
const FAQSourceIdField = z.string()
const DeleteFAQSourceViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    faq_source_id: FAQSourceIdField.uuid(),
  })
  .passthrough()
const GISLayerSourceIdField = z.string()
const DeleteGISLayerSourceViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    gis_layer_id: GISLayerSourceIdField.uuid(),
  })
  .passthrough()
const TransferLineSourceIdField = z.string()
const DeleteTransferLineSourceViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    transfer_line_source_ids: z.array(TransferLineSourceIdField),
  })
  .passthrough()
const InteractionTypeA02Enum = z.enum(['SEARCH', 'CHAT', 'VOICE', 'SMS', 'EMAIL'])
const ChatFilter = z
  .object({
    chat_ids: z.array(z.string().uuid()).nullable(),
    organization_ids: z.array(z.string().uuid()).nullable(),
    user_ids: z.array(z.string().uuid()).nullable(),
    created_after: z.string().datetime({ offset: true }).nullable(),
    created_before: z.string().datetime({ offset: true }).nullable(),
    case_ids: z.array(z.string().uuid()).nullable(),
    query: z.string().nullable(),
    interaction_type: InteractionTypeA02Enum.nullable(),
    impression: z.boolean().nullable(),
  })
  .partial()
  .passthrough()
const DownloadChatsViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), filter: ChatFilter })
  .passthrough()
const DownloadChatsViewResponseData = z
  .object({ email_recipient: z.string().email() })
  .passthrough()
const DownloadQuerySourcesLogsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    start: z.string().datetime({ offset: true }),
    end: z.string().datetime({ offset: true }),
  })
  .passthrough()
const DownloadQuerySourcesLogsViewResponseData = z
  .object({ email_recipient: z.string().email() })
  .passthrough()
const EditAIPhoneLineViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    ai_phone_line_id: AIPhoneLineIdField.uuid(),
    number: z.string(),
    type: TypeBb3Enum,
    owner_type: OwnerTypeEnum,
    name: z.string().optional(),
  })
  .passthrough()
const AIProcessSourceIdField = z.string()
const EditAIProcessSourceViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    ai_process_source_id: AIProcessSourceIdField.uuid(),
    description: z.string().optional(),
    enabled_as_source: z.boolean().optional(),
  })
  .passthrough()
const EditFAQSourceViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    faq_source_id: FAQSourceIdField.uuid(),
    question: z.string().optional(),
    answer: z.string().optional(),
  })
  .passthrough()
const EditGISLayerSourceViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    gis_layer_id: GISLayerSourceIdField.uuid(),
    name: z.string(),
    description: z.string(),
  })
  .passthrough()
const EditOrganizationAiSettingsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    organization_id: OrganizationIdField.uuid(),
    bg_color: z.string().nullish(),
    font_color: z.string().nullish(),
    name: z.string().nullish(),
    search_url: z.string().nullish(),
    banner: z.string().nullish(),
    phone: z.string().nullish(),
  })
  .passthrough()
const Organization = z
  .object({
    id: OrganizationIdField.uuid(),
    identity: IdentityIdField.uuid(),
    parent: OrganizationIdField.uuid(),
    name: z.string().nullable(),
    image: z.string().nullable(),
    logo: z.string().nullable(),
    information: z.string(),
    banner: z.string().nullish(),
    font_color: z.string(),
    bg_color: z.string(),
    search_url: z.string().nullish(),
    phone_number: z.string().nullable(),
  })
  .passthrough()
const EditOrganizationAiSettingsViewResponseData = z
  .object({ organization: Organization })
  .passthrough()
const PageIdField = z.string()
const EditPageViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    page_id: PageIdField.uuid(),
    title: z.string().optional(),
    external_link: z.boolean().nullish(),
    external_source: z.boolean().nullish(),
    archived: z.boolean().nullish(),
    tags: z.array(z.string()).optional(),
  })
  .passthrough()
const EditQuestionViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    id: AITestQuestionIdField.uuid(),
    questions: z.unknown().optional(),
    category: z.string().nullish(),
    difficulty: DifficultyEnum.optional(),
  })
  .passthrough()
const EditTransferLineSourceViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    transfer_line_source_id: TransferLineSourceIdField.uuid(),
    number: z.string(),
    extension: z.string().optional(),
    name: z.string(),
    description: z.string(),
    transfer_confirmation_prompt: z.string().optional(),
  })
  .passthrough()
const FindProcessViewRequestData = z
  .object({ organization_id: OrganizationIdField.uuid(), query: z.string() })
  .passthrough()
const ELProcessInfo = z
  .object({
    organization_name: z.string(),
    process_name: z.string(),
    process_url: z.string(),
    process_description: z.string(),
    chunk_id: z.string(),
    source_id: z.string(),
  })
  .passthrough()
const FindProcessViewResponseData = z
  .object({ process: ELProcessInfo.nullable() })
  .partial()
  .passthrough()
const FindTransferLineViewRequestData = z
  .object({ organization_id: OrganizationIdField.uuid(), query: z.string() })
  .passthrough()
const ELTransferLineInfo = z
  .object({
    organization_name: z.string(),
    name: z.string(),
    phone_number: z.string(),
    transfer_confirmation_prompt: z.string(),
    chunk_id: z.string(),
    source_id: z.string(),
  })
  .passthrough()
const FindTransferLineViewResponseData = z
  .object({ transfer_line: ELTransferLineInfo.nullable() })
  .partial()
  .passthrough()
const GeoPoint = z.object({ lon: z.number(), lat: z.number() }).passthrough()
const GenerateResponseViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid().optional(),
    organization_id: OrganizationIdField.uuid(),
    chat_id: ChatIdField.uuid().optional(),
    interaction_type: InteractionTypeB42Enum,
    query: z.string().optional(),
    raw_text: z.boolean().optional().default(false),
    location: GeoPoint.nullish(),
  })
  .passthrough()
const QueryTypeEnum = z.enum([
  'KEYWORD',
  'QUESTION',
  'GEOSPATIAL_QUERY',
  'CONVERSATION',
  'EMERGENCY',
])
const QueryAnalysis = z
  .object({
    query_type: QueryTypeEnum,
    is_context_dependent: z.boolean(),
    primary_keyword: z.string(),
    rewritten_query: z.string(),
    target_organization_references: z.array(z.string()),
    relevant_gis_layers_references: z.array(z.string()),
    target_organization_ids: z.array(z.string()).optional(),
    relevant_gis_layers: z.array(z.string()).optional(),
    requires_location_input: z.boolean().optional(),
  })
  .passthrough()
const ResponseIdField = z.string()
const ResponseTypeEnum = z.enum([
  'ANSWER',
  'CALL_RECEIVED',
  'CALL_TRANSFERRED',
  'CALL_ENDED',
  'TOOL_CALL',
  'TOOL_RESULTS',
  'LANGUAGE_CHANGE',
  'BACKUP_CALL',
  'SMS_SENT',
  'REQUEST_LOCATION',
  'ACTION',
])
const ChatDisplaySource = z
  .object({
    organization_name: z.string(),
    title: z.string(),
    text: z.string(),
    relevance_score: z.number(),
    source_type: z.string(),
    external_link: z.boolean(),
    external_source: z.boolean(),
    is_linked_organization: z.boolean(),
    source_id: z.string(),
    page_url: z.string().nullish(),
    page_gui_url: z.string().nullish(),
    page_is_poli_process: z.boolean().nullish(),
    transfer_line_name: z.string().nullish(),
    transfer_line_number: z.string().nullish(),
    transfer_line_extension: z.string().nullish(),
    faq_question: z.string().nullish(),
    faq_answer: z.string().nullish(),
  })
  .passthrough()
const GenerateResponseViewResponseData = z
  .object({
    user_id: EndUserIdField.uuid().optional(),
    chat_id: ChatIdField.uuid(),
    query_analysis: QueryAnalysis.optional(),
    response_id: ResponseIdField.uuid(),
    response_text: z.string(),
    response_type: ResponseTypeEnum,
    sources: z.array(ChatDisplaySource),
  })
  .passthrough()
const GenerateResponseStreamViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid().optional(),
    organization_id: OrganizationIdField.uuid(),
    chat_id: ChatIdField.uuid().optional(),
    interaction_type: InteractionTypeB42Enum,
    query: z.string().optional(),
    raw_text: z.boolean().optional().default(false),
  })
  .passthrough()
const GenerateResponseStreamViewResponseData = z
  .object({
    user_id: EndUserIdField.uuid().optional(),
    chat_id: ChatIdField.uuid(),
    message: z.string().optional(),
    query_analysis: QueryAnalysis.optional(),
    response_id: ResponseIdField.uuid().optional(),
    response_text: z.string().optional(),
    sources: z.array(ChatDisplaySource).optional(),
  })
  .passthrough()
const GetAIInteractionLogViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), chat_id: ChatIdField.uuid() })
  .passthrough()
const Query = z
  .object({
    id: z.string().uuid().optional(),
    metadata: z.unknown().nullish(),
    body: z.string(),
    language: z.string().max(10).nullish(),
    english_text: z.string().nullish(),
    created: z.string().datetime({ offset: true }).optional(),
    chat: z.string().uuid(),
  })
  .passthrough()
const Type01dEnum = z.enum([
  'ANSWER',
  'CALL_RECEIVED',
  'CALL_TRANSFERRED',
  'CALL_ENDED',
  'TOOL_CALL',
  'TOOL_RESULTS',
  'LANGUAGE_CHANGE',
  'BACKUP_CALL',
  'SMS_SENT',
  'REQUEST_LOCATION',
  'ACTION',
])
const Response = z
  .object({
    id: z.string().uuid().optional(),
    metadata: z.unknown().nullish(),
    body: z.string(),
    language: z.string().max(10).nullish(),
    english_text: z.string().nullish(),
    created: z.string().datetime({ offset: true }).optional(),
    impression: z.boolean().nullish(),
    type: Type01dEnum.optional(),
    chat: z.string().uuid(),
  })
  .passthrough()
const SourceIdField = z.string()
const Page = z
  .object({
    id: z.string().uuid().optional(),
    metadata: z.unknown().nullish(),
    url: z.string(),
    title: z.string(),
    scrape_status: ScrapeStatusEnum.optional(),
    scraped: z.string().datetime({ offset: true }).nullish(),
    reducto_job: z.string().nullish(),
    cadence: z.string().nullish(),
    next_scrape: z.string().datetime({ offset: true }).nullish(),
    archived: z.boolean().optional(),
    flagged: z.boolean().optional(),
    published_at: z.string().datetime({ offset: true }).nullish(),
    gui_url: z.string().max(200).url().nullish(),
    tags: z.unknown().nullish(),
    organization: z.string().uuid(),
    crawl_task: z.string().uuid().nullish(),
  })
  .passthrough()
const Chunk = z
  .object({
    id: z.string().uuid().optional(),
    source: SourceIdField.uuid(),
    page: Page.nullish(),
    metadata: z.unknown().nullish(),
    order: z.number().int().gte(-9223372036854776000).lte(9223372036854776000).nullish(),
    text: z.string(),
    tokens: z.number().int().gte(-9223372036854776000).lte(9223372036854776000),
    chunk_type: z.number().int().gte(-2147483648).lte(2147483647).optional(),
  })
  .passthrough()
const Response1 = z.object({ response: Response, sources: z.array(Chunk) }).passthrough()
const VoiceAICallInfo = z
  .object({
    call_sid: z.string(),
    call_direction: z.string(),
    call_status: z.string(),
    call_duration: z.number().int(),
    to_phone_number: z.string(),
    from_phone_number: z.string(),
    voice_implementation: z.string(),
  })
  .passthrough()
const AIInteractionLog = z
  .object({
    queries: z.array(Query),
    responses: z.array(Response1),
    voice_ai_call_info: VoiceAICallInfo.nullish(),
    chat_id: ChatIdField.uuid(),
    created: z.string().datetime({ offset: true }),
    interaction_type: InteractionTypeA02Enum,
    organization_name: z.string(),
    organization_image: z.string().nullable(),
    phone_line_type: z.string().nullable(),
    user_name: z.string().nullable(),
    case_name: z.string().nullable(),
    caller_id: z.string().nullable(),
  })
  .passthrough()
const GetAIInteractionLogViewResponseData = z
  .object({ interaction_log: AIInteractionLog })
  .passthrough()
const GetAIProcessSourcesCountViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    query: z.string().optional(),
  })
  .passthrough()
const GetAIProcessSourcesCountViewResponseData = z.object({ count: z.number().int() }).passthrough()
const GetChatCountViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), filter: ChatFilter })
  .passthrough()
const GetChatCountViewResponseData = z.object({ count: z.number().int() }).passthrough()
const GetFAQSourcesCountViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    query: z.string().optional(),
  })
  .passthrough()
const GetFAQSourcesCountViewResponseData = z.object({ count: z.number().int() }).passthrough()
const GetGISLayerSourceViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    gis_layer_id: GISLayerSourceIdField.uuid(),
  })
  .passthrough()
const GISLayerSource = z
  .object({
    id: z.string().uuid().optional(),
    source: SourceIdField.uuid(),
    num_polygons: z.number().int(),
    metadata: z.unknown().nullish(),
    name: z.string().max(255),
    description: z.string().nullish(),
    polygon_description_legend: z.unknown().nullish(),
    line_description_legend: z.unknown().nullish(),
    point_description_legend: z.unknown().nullish(),
  })
  .passthrough()
const GetGISLayerSourceViewResponseData = z.object({ gis_layer: GISLayerSource }).passthrough()
const GetGISLayerSourcesCountViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    query: z.string().optional(),
  })
  .passthrough()
const GetGISLayerSourcesCountViewResponseData = z.object({ count: z.number().int() }).passthrough()
const GetOrganizationAiSettingsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_id: OrganizationIdField.uuid(),
    organization_handle: z.string().nullable(),
  })
  .partial()
  .passthrough()
const OrganizationAISettings0 = z
  .object({
    organization: Organization,
    chat_name: z.string().nullish(),
    chat_icon: z.string().nullish(),
    chat_greeting: z.string().nullish(),
    organization_type: z.string().nullish(),
    disclaimer_text: z.string().nullish(),
    upvote_custom_text: z.string().nullish(),
    downvote_custom_text: z.string().nullish(),
  })
  .passthrough()
const GetOrganizationAiSettingsViewResponseData = z
  .object({ organization: OrganizationAISettings0 })
  .passthrough()
const GetPageCountViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid().optional(),
    organization_identity_id: IdentityIdField.uuid(),
    organization: OrganizationIdField.uuid().optional(),
    query: z.string().optional(),
    source_type: z.string().optional(),
    archived: z.boolean(),
  })
  .passthrough()
const GetPageCountViewResponseData = z.object({ data: z.number().int() }).passthrough()
const GetTransferLineSourcesCountViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    query: z.string().optional(),
  })
  .passthrough()
const GetTransferLineSourcesCountViewResponseData = z
  .object({ count: z.number().int() })
  .passthrough()
const ImportGISPolygonsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    gis_layer_id: GISLayerSourceIdField.uuid(),
    asset_key: z.string(),
    clear_existing: z.boolean().optional().default(false),
  })
  .passthrough()
const ImportGISPolygonsViewResponseData = z
  .object({
    imported_count: z.number().int(),
    skipped_count: z.number().int(),
    errors: z.array(z.string()).optional(),
  })
  .passthrough()
const ListAIInteractionSummariesViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    filter: ChatFilter,
    limit: z.number().int().optional(),
    offset: z.number().int().optional(),
    sort_ascending: z.boolean().optional(),
  })
  .passthrough()
const LastUserMessage = z
  .object({
    query: z.string(),
    language: z.string().optional(),
    language_name: z.string().optional(),
  })
  .passthrough()
const AIInteractionSummary = z
  .object({
    last_user_message: LastUserMessage,
    chat_id: ChatIdField.uuid(),
    created: z.string().datetime({ offset: true }),
    interaction_type: InteractionTypeA02Enum,
    phone_line_type: z.string().nullable(),
    user_name: z.string().nullable(),
    case_name: z.string().nullable(),
    caller_id: z.string().nullable(),
  })
  .passthrough()
const ListAIInteractionSummariesViewResponseData = z
  .object({ interaction_summaries: z.array(AIInteractionSummary) })
  .passthrough()
const ListAIPhoneLinesViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), organization_identity_id: IdentityIdField.uuid() })
  .passthrough()
const AIPhoneLine = z
  .object({
    id: AIPhoneLineIdField.uuid(),
    type: TypeBb3Enum,
    organization_ai_settings: OrganizationIdField.uuid(),
    number: z.string().max(128),
    owner_type: OwnerTypeEnum.optional(),
    name: z.string().nullish(),
    voice: z.string().max(50).nullish(),
  })
  .passthrough()
const ListAIPhoneLinesViewResponseData = z
  .object({ ai_phone_lines: z.array(AIPhoneLine) })
  .passthrough()
const ListAIProcessSourcesViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    query: z.string().optional(),
    ai_process_source_id: AIProcessSourceIdField.uuid().optional(),
    limit: z.number().int().optional(),
    offset: z.number().int().optional(),
  })
  .passthrough()
const AIProcessSource = z
  .object({
    id: z.string().uuid().optional(),
    process: ProcessIdField.uuid(),
    source: SourceIdField.uuid(),
    metadata: z.unknown().nullish(),
    ai_description: z.string(),
    enabled_as_source: z.boolean().optional(),
  })
  .passthrough()
const ListAIProcessSourcesViewResponseData = z
  .object({ ai_process_sources: z.array(AIProcessSource) })
  .passthrough()
const ByFieldEnum = z.enum(['crawled', 'url'])
const DirectionEnum = z.enum(['asc', 'desc'])
const CrawlTaskSort = z.object({ by_field: ByFieldEnum, direction: DirectionEnum }).passthrough()
const CrawlTasksListArgs = z
  .object({
    filter_organization: OrganizationIdField.nullable(),
    filter_query: z.string().nullable(),
    limit: z.number().int().nullable(),
    offset: z.number().int().nullable(),
    sort: CrawlTaskSort.nullable(),
  })
  .partial()
  .passthrough()
const ListCrawlTasksViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    crawl_tasks_list_args: CrawlTasksListArgs,
  })
  .passthrough()
const CrawlTask = z
  .object({
    metadata: z.unknown().nullish(),
    id: CrawlTaskIdField.uuid(),
    url: z.string().max(200).url(),
    organization: OrganizationIdField.uuid(),
    cadence: z.string().nullish(),
    next_crawl: z.string().datetime({ offset: true }).nullish(),
    firecrawl_id: z.string().max(255).nullish(),
    allow_backward_links: z.boolean().optional(),
    active: z.boolean().nullish(),
    ignore_sitemap: z.boolean().nullish(),
    limit: z.number().int().gte(-2147483648).lte(2147483647).nullish(),
    exclude_paths: z.string().nullish(),
    include_only_paths: z.string().nullish(),
    include_tags: z.string().nullish(),
    exclude_tags: z.string().nullish(),
    max_depth: z.number().int().gte(-2147483648).lte(2147483647).nullish(),
    crawl: z.boolean().nullish(),
    sitemap: z.string().nullish(),
    status: ScrapeStatusEnum.nullish(),
    crawled: z.string().datetime({ offset: true }).nullish(),
    last_run_status: ScrapeStatusEnum.nullish(),
    last_run_time: z.string().datetime({ offset: true }).nullish(),
  })
  .passthrough()
const ListCrawlTasksViewResponseData = z.object({ data: z.array(CrawlTask) }).passthrough()
const ListFAQSourcesViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    query: z.string().optional(),
    faq_source_id: FAQSourceIdField.uuid().optional(),
    limit: z.number().int().optional(),
    offset: z.number().int().optional(),
  })
  .passthrough()
const FAQSource = z
  .object({
    id: z.string().uuid().optional(),
    source: SourceIdField.uuid(),
    metadata: z.unknown().nullish(),
    question: z.string(),
    answer: z.string(),
  })
  .passthrough()
const ListFAQSourcesViewResponseData = z.object({ faq_sources: z.array(FAQSource) }).passthrough()
const ListGISLayerSourcesViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    query: z.string().optional(),
    gis_layer_id: GISLayerSourceIdField.uuid().optional(),
    limit: z.number().int().optional(),
    offset: z.number().int().optional(),
  })
  .passthrough()
const ListGISLayerSourcesViewResponseData = z
  .object({ gis_layers: z.array(GISLayerSource) })
  .passthrough()
const ListPagesViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    pgla: z.object({}).partial().passthrough(),
  })
  .passthrough()
const ListPagesViewResponseData = z
  .object({ data: z.array(z.object({}).partial().passthrough()) })
  .passthrough()
const ListSourcesViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid().optional(),
    organization_id: OrganizationIdField.uuid(),
    query: z.string(),
    offset: z.number().int().optional(),
    limit: z.number().int().optional(),
    threshold: z.number().optional(),
  })
  .passthrough()
const ListSourcesViewResponseData = z.object({ sources: z.array(ChatDisplaySource) }).passthrough()
const ListSourcesCountViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid().optional(),
    organization_id: OrganizationIdField.uuid(),
    query: z.string(),
    threshold: z.number().optional(),
  })
  .passthrough()
const ListSourcesCountViewResponseData = z.object({ count: z.number().int() }).passthrough()
const ListSuggestionsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid().optional(),
    organization_id: OrganizationIdField.uuid(),
  })
  .passthrough()
const SuggestionIdField = z.string()
const Suggestion = z
  .object({
    id: SuggestionIdField.uuid(),
    organization: OrganizationIdField.uuid(),
    text: z.string(),
    order: z.number().int().gte(-9223372036854776000).lte(9223372036854776000),
  })
  .passthrough()
const ListSuggestionsViewResponseData = z.object({ suggestions: z.array(Suggestion) }).passthrough()
const ListQuestionsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_id: OrganizationIdField.uuid(),
    category: z.string().optional(),
    search: z.string().optional(),
    limit: z.number().int().optional(),
    offset: z.number().int().optional(),
  })
  .passthrough()
const AITestQuestion = z
  .object({
    id: AITestQuestionIdField.uuid(),
    organization: OrganizationIdField.uuid(),
    questions: z.unknown(),
    category: z.string().max(255).nullish(),
    difficulty: DifficultyEnum,
    created: z.string().datetime({ offset: true }).optional(),
    archived: z.boolean().optional(),
  })
  .passthrough()
const ListQuestionsViewResponseData = z
  .object({ questions: z.array(AITestQuestion), total_count: z.number().int() })
  .passthrough()
const ListTransferLineSourcesViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    query: z.string().optional(),
    transfer_line_source_id: TransferLineSourceIdField.uuid().optional(),
    limit: z.number().int().optional(),
    offset: z.number().int().optional(),
  })
  .passthrough()
const TransferLineSource = z
  .object({
    id: z.string().uuid().optional(),
    source: SourceIdField.uuid(),
    to_organization: OrganizationIdField.nullish(),
    metadata: z.unknown().nullish(),
    name: z.string(),
    description: z.string(),
    transfer_confirmation_prompt: z.string().nullish(),
    number: z.string().max(128),
    extension: z.number().int().gte(-9223372036854776000).lte(9223372036854776000).nullish(),
  })
  .passthrough()
const ListTransferLineSourcesViewResponseData = z
  .object({ transfer_lines: z.array(TransferLineSource) })
  .passthrough()
const LoadQuestionsFromTemplateViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_id: OrganizationIdField.uuid(),
    template_organization_id: OrganizationIdField.uuid(),
  })
  .passthrough()
const LoadQuestionsFromTemplateViewResponseData = z
  .object({ questions: z.array(AITestQuestion) })
  .passthrough()
const ELToolCall = z
  .object({
    request_id: z.string(),
    tool_name: z.string(),
    params_as_json: z.string(),
    tool_has_been_called: z.boolean(),
  })
  .passthrough()
const ELToolResult = z
  .object({
    request_id: z.string(),
    tool_name: z.string(),
    result_value: z.string(),
    is_error: z.boolean(),
    tool_has_been_called: z.boolean(),
  })
  .passthrough()
const ELConversationTurn = z
  .object({
    message: z.string().nullish(),
    tool_calls: z.array(ELToolCall),
    tool_results: z.array(ELToolResult),
    conversation_turn_metrics: z.unknown().nullish(),
    feedback: z.string().nullish(),
    role: z.string(),
    time_in_call_secs: z.number().int(),
  })
  .passthrough()
const ELTranscriptData = z
  .object({
    transcript: z.array(ELConversationTurn),
    agent_id: z.string(),
    conversation_id: z.string(),
    status: z.string(),
    metadata: z.object({}).partial().passthrough(),
    analysis: z.object({}).partial().passthrough(),
    conversation_initiation_client_data: z.object({}).partial().passthrough(),
  })
  .passthrough()
const LogConversationViewRequestData = z
  .object({ type: z.string(), event_timestamp: z.number().int(), data: ELTranscriptData })
  .passthrough()
const RemoveGISPolygonsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    gis_layer_id: GISLayerSourceIdField.uuid(),
  })
  .passthrough()
const RemoveQuestionViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), id: AITestQuestionIdField.uuid() })
  .passthrough()
const RetrieveSourcesViewRequestData = z
  .object({ organization_id: OrganizationIdField.uuid(), query: z.string() })
  .passthrough()
const ELChunkInfo = z
  .object({
    organization_name: z.string(),
    title: z.string(),
    text: z.string(),
    chunk_id: z.string(),
  })
  .passthrough()
const RetrieveSourcesViewResponseData = z
  .object({ source_information: z.array(ELChunkInfo) })
  .passthrough()
const ScrapePagesViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization: OrganizationIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    pages: z.array(z.string()),
  })
  .passthrough()
const SetImpressionViewRequestData = z
  .object({ response_id: ResponseIdField.uuid(), impression: z.boolean().nullable() })
  .passthrough()
const SetSuggestionsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_id: OrganizationIdField.uuid(),
    suggestions: z.array(z.string().nullable()).max(4),
  })
  .passthrough()
const SetSuggestionsViewResponseData = z.object({ suggestions: z.array(Suggestion) }).passthrough()
const TranslateTextViewRequestData = z
  .object({ text: z.string(), source_language: z.string(), target_language: z.string() })
  .passthrough()
const TranslateTextViewResponseData = z.object({ translated_text: z.string() }).passthrough()
const UpdateCrawlTaskViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), crawl_task_id: CrawlTaskIdField.uuid() })
  .passthrough()
const UploadDocumentsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    organization_id: OrganizationIdField.uuid(),
    documents: z.array(z.string().url()),
  })
  .passthrough()
const UploadDocumentsViewResponseData = z
  .object({ success: z.boolean(), uploadedDocuments: z.array(z.string()) })
  .passthrough()
const UpsertPageViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    organization_id: OrganizationIdField.uuid(),
    url: z.string().url(),
    title: z.string().optional(),
    content: z.string().optional(),
    external_link: z.boolean().optional(),
    external_source: z.boolean().optional(),
    cadence: z.string().optional(),
    next_scrape: z.string().datetime({ offset: true }).optional(),
    archived: z.boolean().optional(),
    flagged: z.boolean().optional(),
    crawl_task: CrawlTaskIdField.uuid().optional(),
    scrape_status: ScrapeStatusEnum.optional(),
    scraped: z.string().datetime({ offset: true }).optional(),
  })
  .passthrough()
const UpsertPageViewResponseData = z.object({ data: Page }).passthrough()
const CaseIdField = z.string()
const ItemIdField = z.string()
const AddCaseItemViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    case_id: CaseIdField.uuid(),
    item_id: ItemIdField.uuid(),
  })
  .passthrough()
const AddCaseMemberViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    case_id: CaseIdField.uuid(),
    member_id: IdentityIdField.uuid(),
  })
  .passthrough()
const LogIdField = z.string()
const AddCityworksCommentViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), log_id: LogIdField.uuid() })
  .passthrough()
const FormIdField = z.string()
const InputIdField = z.string()
const CommentThreadIdField = z.string()
const AddCommentViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    content: z.string(),
    author_id: IdentityIdField.uuid(),
    form_id: FormIdField.uuid(),
    input_id: InputIdField.uuid().optional(),
    thread_id: CommentThreadIdField.uuid().optional(),
  })
  .passthrough()
const CommentIdField = z.string()
const Comment = z
  .object({
    id: CommentIdField.uuid(),
    comment_thread: CommentThreadIdField.uuid(),
    commenter: IdentityIdField.nullish(),
    comment: z.string(),
    time: z.string().datetime({ offset: true }).optional(),
    commenter_name: z.string().nullish(),
    commenter_profile: z.string().nullish(),
    commenter_email: z.string().email().nullish(),
  })
  .passthrough()
const AddCommentViewResponseData = z.object({ comment: Comment }).passthrough()
const AttachProcessViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    process_id: ProcessIdField.uuid(),
    case_id: CaseIdField.uuid(),
  })
  .passthrough()
const NotificationIdField = z.string()
const BumpNotificationViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), notification_id: NotificationIdField.uuid() })
  .passthrough()
const CopyProcessViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    new_owner_id: IdentityIdField.uuid(),
    old_process_id: ProcessIdField.uuid(),
    name: z.string(),
    description: z.string(),
    keep_assignees: z.boolean(),
  })
  .passthrough()
const CopyProcessViewResponseData = z.object({ process_id: ProcessIdField.uuid() }).passthrough()
const SectionStepIdField = z.string()
const FormStepIdField = z.string()
const CopySectionSubStepViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    section_step_id: SectionStepIdField.uuid(),
    section_id: SectionStepIdField.nullish(),
    form_step_id: FormStepIdField.uuid().optional(),
    order: z.number().int(),
  })
  .passthrough()
const InputStepIdField = z.string()
const ValueTypeEnum = z.enum(['bool', 'datetime', 'file', 'identity', 'location', 'number', 'text'])
const DatetimeStep = z
  .object({
    id: z.string().uuid().optional(),
    input_step: InputStepIdField.uuid(),
    type: ValueTypeEnum,
    request_time: z.boolean(),
  })
  .passthrough()
const IdentityStep = z
  .object({
    id: z.string().uuid().optional(),
    input_step: InputStepIdField.uuid(),
    type: ValueTypeEnum,
    require_me: z.boolean(),
    show_address: z.boolean(),
    require_address: z.boolean(),
    show_date_of_birth: z.boolean(),
    require_date_of_birth: z.boolean(),
    show_email: z.boolean(),
    require_email: z.boolean(),
    verify_email: z.boolean(),
    show_name: z.boolean(),
    require_name: z.boolean(),
    show_phone: z.boolean(),
    require_phone: z.boolean(),
    add_contact: z.boolean(),
  })
  .passthrough()
const InputStepSub0 = z
  .object({
    datetime_step_sub: DatetimeStep.nullish(),
    identity_step_sub: IdentityStep.nullish(),
    type: ValueTypeEnum,
  })
  .passthrough()
const BoolValueIdField = z.string()
const ValueIdField = z.string()
const BoolValue0 = z
  .object({ id: BoolValueIdField.uuid(), value: ValueIdField.uuid(), checked: z.boolean() })
  .passthrough()
const DatetimeValueIdField = z.string()
const DatetimeValue0 = z
  .object({
    id: DatetimeValueIdField.uuid(),
    value: ValueIdField.uuid(),
    datetime: z.string().datetime({ offset: true }),
  })
  .passthrough()
const FileValueIdField = z.string()
const FileValue0 = z
  .object({ id: FileValueIdField.uuid(), value: ValueIdField.uuid(), file: z.string() })
  .passthrough()
const IdentityValueIdField = z.string()
const IdentityValue0 = z
  .object({
    id: IdentityValueIdField.uuid(),
    value: ValueIdField.uuid(),
    identity: IdentityIdField.uuid(),
  })
  .passthrough()
const LocationValueIdField = z.string()
const LocationIdField = z.string()
const AddressIdField = z.string()
const LocationValue0 = z
  .object({
    id: LocationValueIdField.uuid(),
    value: ValueIdField.uuid(),
    location: LocationIdField.uuid(),
    address: AddressIdField.nullish(),
    street: z.string(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
  })
  .passthrough()
const NumberValueIdField = z.string()
const NumberValue0 = z
  .object({
    id: NumberValueIdField.uuid(),
    value: ValueIdField.uuid(),
    number: z.string().regex(/^-?\d{0,18}(?:\.\d{0,12})?$/),
  })
  .passthrough()
const TextValueIdField = z.string()
const TextValue0 = z
  .object({ id: TextValueIdField.uuid(), value: ValueIdField.uuid(), text: z.string() })
  .passthrough()
const Value0 = z
  .object({
    bool_value: BoolValue0.nullish(),
    datetime_value: DatetimeValue0.nullish(),
    file_value: FileValue0.nullish(),
    identity_value: IdentityValue0.nullish(),
    location_value: LocationValue0.nullish(),
    number_value: NumberValue0.nullish(),
    text_value: TextValue0.nullish(),
    type: ValueTypeEnum,
  })
  .passthrough()
const InputStepChoice1 = z
  .object({ value: Value0, step: z.string().uuid(), order: z.number().int() })
  .passthrough()
const StyleEnum = z.enum([
  'RawInputStyle',
  'DropdownInputStyle',
  'RadioInputStyle',
  'LongTextInputStyle',
])
const InputSubstep2 = z
  .object({
    sub: InputStepSub0,
    choices: z.array(InputStepChoice1),
    template: Value0.nullish(),
    form: FormStepIdField.uuid(),
    id: z.string().uuid(),
    type: ValueTypeEnum,
    process: z.string().uuid(),
    code: z.string(),
    name: z.string(),
    description: z.string(),
    order: z.number().int(),
    style: StyleEnum,
    section: z.string().uuid().nullable(),
    required: z.boolean(),
    external: z.boolean(),
    sla: z.string().nullable(),
    due: z.string().nullable(),
    pinned: z.boolean(),
    added_at: z.string().datetime({ offset: true }).nullable(),
    removed_at: z.string().datetime({ offset: true }).nullable(),
  })
  .passthrough()
const CopySectionSubStepViewResponseData = z
  .object({
    section_step_id: SectionStepIdField.uuid(),
    copied_sections: z.record(SectionStepIdField),
    copied_inputs: z.record(InputSubstep2),
  })
  .passthrough()
const StepCreate = z
  .object({ process_id: ProcessIdField.uuid(), order: z.number().int() })
  .passthrough()
const CreateBillStepViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), step_create: StepCreate })
  .passthrough()
const BillStepIdField = z.string()
const StepIdField = z.string()
const CreateBillStepViewResponseData = z
  .object({ bill_step_id: BillStepIdField.uuid(), step_id: StepIdField.uuid() })
  .passthrough()
const StatusesEnum = z.enum(['Unsubmitted', 'Open', 'Closed', 'Deleted', 'Hidden'])
const CreateCaseViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    name: z.string(),
    status: StatusesEnum,
    organization_identity_id: IdentityIdField.uuid(),
  })
  .passthrough()
const CreateCaseViewResponseData = z
  .object({ case_id: CaseIdField.uuid(), handle: z.string().nullable() })
  .passthrough()
const CreateCasesTabViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    code: z.string(),
    name: z.string(),
    order: z.number().int(),
  })
  .passthrough()
const CasesTabIdField = z.string()
const CreateCasesTabViewResponseData = z.object({ tab_id: CasesTabIdField.uuid() }).passthrough()
const CreateCityworksSRViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), case_id: CaseIdField.uuid() })
  .passthrough()
const CreateDefaultCasesTabsViewRequestData = z
  .object({ user_id: EndUserIdField.uuid() })
  .passthrough()
const CreateExpressionViewRequestDataTypeEnum = z.enum([
  'case_creator',
  'constant',
  'input_value',
  'operator',
])
const CreateExpressionViewRequestDataOperatorEnum = z.enum([
  'addition',
  'and',
  'conditional',
  'equals',
  'greater_than',
  'less_than',
  'maximum',
  'minimum',
  'multiplication',
  'or',
  'subtraction',
])
const ExpressionIdField = z.string()
const CreateExpressionViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    type: CreateExpressionViewRequestDataTypeEnum,
    process_id: ProcessIdField.uuid().optional(),
    value_id: ValueIdField.uuid().optional(),
    input_step_id: InputStepIdField.uuid().optional(),
    operator: CreateExpressionViewRequestDataOperatorEnum.optional(),
    parameter_one: ExpressionIdField.uuid().optional(),
    parameter_two: ExpressionIdField.uuid().optional(),
    parameter_three: ExpressionIdField.uuid().optional(),
    name: z.string().optional(),
  })
  .passthrough()
const ExpressionOperatorEnum = z.enum([
  'addition',
  'and',
  'conditional',
  'equals',
  'greater_than',
  'less_than',
  'maximum',
  'minimum',
  'multiplication',
  'or',
  'subtraction',
])
const Expression = z
  .object({
    id: ExpressionIdField.uuid(),
    process: ProcessIdField.uuid(),
    type: ValueTypeEnum,
    case_creator: z.boolean().nullish(),
    name: z.string().nullish(),
    operator: ExpressionOperatorEnum.nullish(),
    parameter_one_type: ValueTypeEnum.nullish(),
    parameter_two_type: ValueTypeEnum.nullish(),
    parameter_three_type: ValueTypeEnum.nullish(),
    constant: z.string().uuid().nullish(),
    input_value: z.string().uuid().nullish(),
    parameter_one: z.string().uuid().nullish(),
    parameter_two: z.string().uuid().nullish(),
    parameter_three: z.string().uuid().nullish(),
  })
  .passthrough()
const CreateExpressionViewResponseData = z.object({ expression: Expression }).passthrough()
const CreateFormStepViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), step_create: StepCreate })
  .passthrough()
const CreateFormStepViewResponseData = z
  .object({ form_step_id: FormIdField.uuid(), step_id: StepIdField.uuid() })
  .passthrough()
const CreateFormStyleElementViewRequestDataTypeEnum = z.literal('page_break')
const CreateFormStyleElementViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    form_step_id: FormStepIdField.uuid(),
    order: z.number().int(),
    type: CreateFormStyleElementViewRequestDataTypeEnum,
  })
  .passthrough()
const FormStyleElementIdField = z.string()
const FormStyleElementTypeEnum = z.literal('page_break')
const FormStyleElement = z
  .object({
    id: FormStyleElementIdField.uuid(),
    order: z.number().int().gte(-9223372036854776000).lte(9223372036854776000),
    type: FormStyleElementTypeEnum,
    form_step: z.string().uuid(),
  })
  .passthrough()
const CreateFormStyleElementViewResponseData = z
  .object({ form_style_element: FormStyleElement })
  .passthrough()
const ValueCreate = z
  .object({
    number_value: z
      .string()
      .regex(/^-?\d{0,18}(?:\.\d{0,12})?$/)
      .nullable(),
    bool_value: z.boolean().nullable(),
    datetime_value: z.string().datetime({ offset: true }).nullable(),
    file_value: z.string().nullable(),
    identity_value: IdentityIdField.nullable(),
    location_value: LocationIdField.nullable(),
    text_value: z.string().nullable(),
  })
  .partial()
  .passthrough()
const InputCreate = z
  .object({
    value: ValueCreate.nullish(),
    form_id: FormIdField.uuid(),
    step: InputStepIdField.uuid(),
    type: ValueTypeEnum,
    input_id: InputIdField.nullish(),
  })
  .passthrough()
const CreateInputViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), input_create: InputCreate })
  .passthrough()
const CreateInputViewResponseData = z.object({ input_id: InputIdField.nullable() }).passthrough()
const CreateInputStepViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    value_type: ValueTypeEnum,
    name: z.string(),
    description: z.string(),
    template: ValueIdField.nullable(),
    style: StyleEnum,
    required: z.boolean(),
    section: SectionStepIdField.nullable(),
    form: FormStepIdField.uuid(),
    pinned: z.boolean(),
    order: z.number().int(),
    process_id: ProcessIdField.uuid(),
  })
  .passthrough()
const CreateInputStepViewResponseData = z.object({ input_step: InputSubstep2 }).passthrough()
const MediumEnum = z.enum(['ViaSite', 'ViaPhone', 'ViaEmail', 'ViaInPerson'])
const Attachment = z
  .object({ name: z.string(), url: z.string(), access_url: z.string().nullish() })
  .passthrough()
const CreateMessageViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    author_identity_id: IdentityIdField.uuid(),
    recipients: z.array(IdentityIdField),
    case_id: CaseIdField.uuid(),
    medium: MediumEnum,
    body: z.string(),
    attachments: z.array(Attachment),
  })
  .passthrough()
const MessageIdField = z.string()
const CreateMessageViewResponseData = z
  .object({ message_id: MessageIdField.uuid(), item_id: ItemIdField.uuid() })
  .passthrough()
const Status2efEnum = z.enum(['draft', 'live', 'archived'])
const CreateProcessViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    name: z.string(),
    description: z.string(),
    status: Status2efEnum,
    appliable: z.boolean(),
    copyable: z.boolean(),
  })
  .passthrough()
const CreateProcessViewResponseData = z.object({ process_id: ProcessIdField.uuid() }).passthrough()
const CreateRelationViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    from_case_id: CaseIdField.uuid(),
    to_case_id: CaseIdField.uuid(),
  })
  .passthrough()
const CreateSectionStepViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    name: z.string(),
    description: z.string(),
    section: SectionStepIdField.nullable(),
    form: FormStepIdField.uuid(),
    order: z.number().int(),
    button_name: z.string().nullable(),
    min: z.number().int(),
    max: z.number().int(),
    render: ExpressionIdField.nullable(),
  })
  .passthrough()
const CreateSectionStepViewResponseData = z
  .object({ section_step_id: SectionStepIdField.uuid() })
  .passthrough()
const CreateTaskViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    case_id: CaseIdField.uuid(),
    assignees: z.array(IdentityIdField),
    name: z.string(),
    description: z.string(),
    due: z.string().datetime({ offset: true }).optional(),
  })
  .passthrough()
const TaskIdField = z.string()
const CreateTaskViewResponseData = z.object({ task_id: TaskIdField.uuid() }).passthrough()
const CreateTestCaseViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    name: z.string(),
    owner: IdentityIdField.uuid(),
    process_id: ProcessIdField.uuid(),
  })
  .passthrough()
const CreateTestCaseViewResponseData = z
  .object({ case_id: CaseIdField.uuid(), handle: z.string() })
  .passthrough()
const CreateValueViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    type: ValueTypeEnum,
    bool: z.boolean().optional(),
    datetime: z.string().datetime({ offset: true }).optional(),
    file: z.string().optional(),
    identity: IdentityIdField.uuid().optional(),
    location: LocationIdField.uuid().optional(),
    number: z
      .string()
      .regex(/^-?\d{0,18}(?:\.\d{0,12})?$/)
      .optional(),
    text: z.string().optional(),
  })
  .passthrough()
const CreateValueViewResponseData = z.object({ value_id: ValueIdField.uuid() }).passthrough()
const DeleteAssignmentViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), notification_id: NotificationIdField.uuid() })
  .passthrough()
const DeleteBillStepViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), bill_step_id: BillStepIdField.uuid() })
  .passthrough()
const DeleteCaseViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    case_ids: z.array(CaseIdField).optional(),
    process_id: ProcessIdField.uuid().optional(),
  })
  .passthrough()
const DeleteCasesTabViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), tab_id: CasesTabIdField.uuid() })
  .passthrough()
const DeleteCommentViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    comment_id: CommentIdField.uuid().optional(),
    thread_id: CommentThreadIdField.uuid().optional(),
  })
  .passthrough()
const DeleteDraftsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    case_id: CaseIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
  })
  .passthrough()
const DeleteExpressionsViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), expression_ids: z.array(ExpressionIdField) })
  .passthrough()
const DeleteFormStepViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), form_step_id: FormStepIdField.uuid() })
  .passthrough()
const DeleteFormStyleElementViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), form_style_element_id: FormStyleElementIdField.uuid() })
  .passthrough()
const DeleteInputSubstepViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), input_step_id: InputStepIdField.uuid() })
  .passthrough()
const SectionIdField = z.string()
const DeleteSectionViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), section_id: SectionIdField.uuid() })
  .passthrough()
const DeleteSectionSubstepViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), section_substep_id: SectionStepIdField.uuid() })
  .passthrough()
const DeleteStepAssignmentViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    step_id: StepIdField.uuid(),
    user_expression_id: ExpressionIdField.uuid(),
  })
  .passthrough()
const MessageTemplateIdField = z.string()
const DeleteTemplateViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), template_id: MessageTemplateIdField.uuid() })
  .passthrough()
const ProcessStatusesEnum = z.enum(['draft', 'live', 'archived'])
const NumberComparisonOperatorEnum = z.enum(['=', '>', '<', '>=', '<='])
const NumberComparison = z
  .object({ value: z.string(), operator: NumberComparisonOperatorEnum.optional() })
  .passthrough()
const CaseFilter = z
  .object({
    assignees: z.array(z.string().uuid()).nullable(),
    no_manager: z.boolean().nullable(),
    item_assignee_ids: z.array(z.string().uuid()).nullable(),
    owner_ids: z.array(z.string().uuid()).nullable(),
    governing_units: z.array(z.string().uuid()).nullable(),
    requester_ids: z.array(z.string().uuid()).nullable(),
    statuses: z.array(StatusesEnum).nullable(),
    case_ids: z.array(z.string().uuid()).nullable(),
    tag_ids: z.array(z.string().uuid()).nullable(),
    process_ids: z.array(z.string().uuid()).nullable(),
    process_statuses: z.array(ProcessStatusesEnum).nullable(),
    created_after: z.string().datetime({ offset: true }).nullable(),
    created_before: z.string().datetime({ offset: true }).nullable(),
    next_due_after: z.string().datetime({ offset: true }).nullable(),
    next_due_before: z.string().datetime({ offset: true }).nullable(),
    process_step_ids: z.array(z.string().uuid().nullable()).nullable(),
    input_step_assignees: z.record(z.array(z.string().uuid())),
    input_choices: z.record(z.string()),
    input_text: z.record(z.string()),
    input_bool: z.record(z.boolean()),
    input_number: z.record(NumberComparison),
  })
  .partial()
  .passthrough()
const DownloadCasesViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), filter: CaseFilter })
  .passthrough()
const DownloadCasesViewResponseData = z
  .object({ email_recipient: z.string().email() })
  .passthrough()
const ChargeSubStepCreate = z
  .object({ name: z.string(), description: z.string(), dollars: ExpressionIdField.uuid() })
  .passthrough()
const EditBillStepViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    bill_step_id: BillStepIdField.uuid(),
    name: z.string().optional(),
    description: z.string().optional(),
    order: z.number().int().optional(),
    charges: z.array(ChargeSubStepCreate).optional(),
    account: z.string().nullish(),
    distribution: z.string().nullish(),
  })
  .passthrough()
const ChargeSubstepIdField = z.string()
const ChargeSubstep = z
  .object({
    id: ChargeSubstepIdField.uuid(),
    bill: BillStepIdField.uuid(),
    dollars: ExpressionIdField.uuid(),
    code: z.string().regex(/^[a-z0-9-]+$/),
    name: z.string(),
    description: z.string(),
    dollars_type: ValueTypeEnum,
  })
  .passthrough()
const TagIdField = z.string()
const CalculatedTag = z
  .object({ tag: TagIdField.uuid(), expression: ExpressionIdField.nullish() })
  .passthrough()
const BillStep1 = z
  .object({
    id: BillStepIdField.uuid(),
    charges: z.array(ChargeSubstep),
    calculated_tags: z.array(CalculatedTag),
    process: z.string().uuid(),
    code: z.string(),
    step: z.string().uuid(),
    name: z.string(),
    description: z.string(),
    installments: z.number().int(),
    order: z.number().int(),
    account: z.string().nullable(),
    distribution: z.string().nullable(),
    set_status: StatusesEnum.nullable(),
    added_at: z.string().datetime({ offset: true }).nullable(),
    removed_at: z.string().datetime({ offset: true }).nullable(),
    allow_ach: z.boolean(),
  })
  .passthrough()
const EditBillStepViewResponseData = z.object({ bill_step: BillStep1 }).passthrough()
const EditCaseViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    case_id: CaseIdField.uuid(),
    owner: IdentityIdField.uuid().optional(),
    assignee: IdentityIdField.nullish(),
    requester: IdentityIdField.nullish(),
    name: z.string().optional(),
    address: AddressIdField.nullish(),
  })
  .passthrough()
const EditCasesTabViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    tab_id: CasesTabIdField.uuid(),
    code: z.string().optional(),
    name: z.string().optional(),
    order: z.number().int().optional(),
    page_size: z.number().int().optional(),
  })
  .passthrough()
const DatetimeStepIdField = z.string()
const EditDateTimeStepViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    datetime_step_id: DatetimeStepIdField.uuid(),
    request_time: z.boolean(),
  })
  .passthrough()
const EditDueDateViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    item_ids: z.array(ItemIdField),
    due_date: z.string().datetime({ offset: true }).optional(),
  })
  .passthrough()
const EditExpressionNameViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    expression_id: ExpressionIdField.uuid(),
    name: z.string(),
  })
  .passthrough()
const EditFormStepViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    form_step_id: FormStepIdField.uuid(),
    name: z.string().optional(),
    description: z.string().optional(),
    order: z.number().int().optional(),
    external: z.boolean().optional(),
    sla: z.string().nullish(),
    due: z.string().nullish(),
    button_name: z.string().nullish(),
  })
  .passthrough()
const EditFormStyleElementViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    form_style_element_id: FormStyleElementIdField.uuid(),
    order: z.number().int().optional(),
  })
  .passthrough()
const EditFormStyleElementViewResponseData = z
  .object({ form_style_element: FormStyleElement })
  .passthrough()
const IdentityStepIdField = z.string()
const EditIdentityStepViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    identity_step_id: IdentityStepIdField.uuid(),
    add_contact: z.boolean().optional(),
    require_me: z.boolean().optional(),
    show_address: z.boolean().optional(),
    require_address: z.boolean().optional(),
    show_date_of_birth: z.boolean().optional(),
    require_date_of_birth: z.boolean().optional(),
    show_email: z.boolean().optional(),
    require_email: z.boolean().optional(),
    verify_email: z.boolean().optional(),
    show_name: z.boolean().optional(),
    require_name: z.boolean().optional(),
    show_phone: z.boolean().optional(),
    require_phone: z.boolean().optional(),
  })
  .passthrough()
const EditInputStepViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    input_step_id: InputStepIdField.uuid(),
    name: z.string().optional(),
    description: z.string().optional(),
    type: ValueTypeEnum.optional(),
    order: z.number().int().optional(),
    style: StyleEnum.optional(),
    choices: z.array(ValueIdField).optional(),
    template: ValueIdField.nullish(),
    required: z.boolean().optional(),
    pinned: z.boolean().optional(),
    section: SectionStepIdField.nullish(),
  })
  .passthrough()
const EditProcessViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    process_id: ProcessIdField.uuid(),
    owner: IdentityIdField.uuid().optional(),
    name: z.string().optional(),
    description: z.string().optional(),
    status: Status2efEnum.optional(),
    appliable: z.boolean().optional(),
    copyable: z.boolean().optional(),
    velosimo_integrated: z.boolean().optional(),
    review_steps_enabled: z.boolean().optional(),
  })
  .passthrough()
const EditSectionSubStepViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    section_step_id: SectionStepIdField.uuid(),
    section: SectionStepIdField.nullish(),
    name: z.string().optional(),
    description: z.string().optional(),
    order: z.number().int().optional(),
    button_name: z.string().nullish(),
    min: z.number().int().optional(),
    max: z.number().int().optional(),
    render: ExpressionIdField.nullish(),
  })
  .passthrough()
const EditStepViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    step_id: StepIdField.uuid(),
    set_status: StatusesEnum.nullish(),
    calculated_tags: z.array(CalculatedTag).optional(),
  })
  .passthrough()
const EditTaskViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    task_id: TaskIdField.uuid(),
    name: z.string().optional(),
    description: z.string().optional(),
    due: z.string().datetime({ offset: true }).optional(),
    completed: z.boolean().optional(),
    assignees: z.array(IdentityIdField).optional(),
  })
  .passthrough()
const DocumentStepIdField = z.string()
const GenerateDocumentViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    case_id: CaseIdField.uuid(),
    document_step_id: DocumentStepIdField.uuid(),
  })
  .passthrough()
const GenerateDocumentViewResponseData = z.object({ document_url: z.string() }).passthrough()
const GetBillStepViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), bill_step_id: BillStepIdField.uuid() })
  .passthrough()
const GetBillStepViewResponseData = z.object({ bill_step: BillStep1 }).passthrough()
const GetCaseViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), case_id: CaseIdField.uuid() })
  .passthrough()
const ContactIdField = z.string()
const Identity0 = z
  .object({
    name: z.string(),
    id: IdentityIdField.uuid(),
    contact_id: ContactIdField.uuid(),
    handle: z.string(),
    image: z.string().nullable(),
    stripe_id: z.string().nullable(),
    email: z.string().nullable(),
  })
  .passthrough()
const Address0 = z
  .object({
    id: z.string().uuid(),
    location_id: z.string().uuid(),
    contact_id: z.string().uuid().nullable(),
    street: z.string(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
    location: GeoPoint,
  })
  .passthrough()
const Location0 = z
  .object({ id: LocationIdField.uuid(), location: GeoPoint, address: Address0 })
  .passthrough()
const ContactEntryIdField = z.string()
const ContactEmailIdField = z.string()
const EmailAddressIdField = z.string()
const ContactEmail0 = z
  .object({
    signature: z.string(),
    address: z.string(),
    id: ContactEmailIdField.uuid(),
    email: EmailAddressIdField.uuid(),
    contact: ContactIdField.uuid(),
    primary: z.boolean(),
    verified: z.boolean(),
    ses_verified: z.boolean(),
  })
  .passthrough()
const NoteIdField = z.string()
const TargetIdField = z.string()
const Note0 = z
  .object({
    id: NoteIdField.uuid(),
    target: TargetIdField.uuid(),
    note: z.string(),
    created: z.string().datetime({ offset: true }),
    modified: z.string().datetime({ offset: true }),
    author: Identity0,
    owner: IdentityIdField.uuid(),
  })
  .passthrough()
const Identity2 = z
  .object({
    id: IdentityIdField.uuid(),
    contact_id: ContactIdField.uuid(),
    contact_entry_id: ContactEntryIdField.nullish(),
    emails: z.array(ContactEmail0),
    notes: z.array(Note0),
    first_name: z.string(),
    last_name: z.string(),
    owner: z.string().nullable(),
    handle: z.string(),
    name: z.string(),
    date_of_birth: z.string().nullable(),
    image: z.string().nullable(),
    is_contact: z.boolean(),
    phones: z.array(z.string()),
    addresses: z.array(z.string()),
  })
  .passthrough()
const Tag = z
  .object({
    id: TagIdField.uuid(),
    owner: IdentityIdField.uuid(),
    name: z.string(),
    color: z.string(),
    code: z.string().regex(/^[a-z0-9-]+$/),
    description: z.string().nullish(),
  })
  .passthrough()
const Case2 = z
  .object({
    creator: Identity0.nullable(),
    location: Location0.nullable(),
    assignee: Identity0.nullable(),
    requester: Identity2.nullable(),
    tags: z.array(Tag),
    id: z.string().uuid(),
    handle: z.string(),
    target_id: z.string().uuid(),
    owner_identity_id: z.string().uuid(),
    process: z.string().uuid().nullable(),
    status: StatusesEnum,
    title: z.string(),
    created_time: z.string().datetime({ offset: true }),
    message: z.string().nullable(),
    message_time: z.string().datetime({ offset: true }).nullable(),
    unread: z.boolean(),
    unverified_apply: z.boolean(),
    velosimo_integrated: z.boolean(),
  })
  .passthrough()
const GetCaseViewResponseData = z.object({ case: Case2 }).passthrough()
const GetCaseCountViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), filter: CaseFilter })
  .passthrough()
const GetCaseCountViewResponseData = z
  .object({ total: z.number().int(), unread: z.number().int() })
  .passthrough()
const GetCaseIdViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), handle: z.string() })
  .passthrough()
const GetCaseIdViewResponseData = z.object({ case_id: CaseIdField.uuid() }).passthrough()
const GetCaseOwnerViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), case_id: CaseIdField.uuid() })
  .passthrough()
const GetCaseOwnerViewResponseData = z.object({ owner: Identity0 }).partial().passthrough()
const GetDocumentStepsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    case_id: CaseIdField.uuid(),
  })
  .passthrough()
const DocumentTemplateIdField = z.string()
const DocumentTemplate = z
  .object({
    id: DocumentTemplateIdField.uuid(),
    owner: IdentityIdField.uuid(),
    harbour_template_id: z.string(),
    name: z.string(),
  })
  .passthrough()
const DocumentStep = z
  .object({
    id: DocumentStepIdField.uuid(),
    step: StepIdField.uuid(),
    document_template: DocumentTemplate,
    field_values: z.unknown(),
  })
  .passthrough()
const GetDocumentStepsViewResponseData = z
  .object({ document_steps: z.array(DocumentStep) })
  .passthrough()
const GetDocumentTemplateFieldsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    harbour_template_id: z.string(),
  })
  .passthrough()
const GetDocumentTemplateFieldsViewResponseData = z
  .object({ data: z.object({}).partial().passthrough() })
  .passthrough()
const GetFormStepCountViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    step_id: FormStepIdField.uuid(),
    complete: z.boolean().optional().default(false),
    after: z.string().datetime({ offset: true }).nullish(),
    before: z.string().datetime({ offset: true }).nullish(),
    completion_time: z.string().nullish(),
  })
  .passthrough()
const GetFormStepCountViewResponseData = z.object({ count: z.number().int() }).passthrough()
const FormStepTimeFilter = z
  .object({
    step_id: FormStepIdField.uuid(),
    after_created: z.string().datetime({ offset: true }).nullish(),
    before_created: z.string().datetime({ offset: true }).nullish(),
    after_completed: z.string().datetime({ offset: true }).nullish(),
    before_completed: z.string().datetime({ offset: true }).nullish(),
  })
  .passthrough()
const GetFormStepTimeViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), form_step_time_filter: FormStepTimeFilter })
  .passthrough()
const GetFormStepTimeViewResponseData = z
  .object({ average_duration: z.string().nullable() })
  .partial()
  .passthrough()
const ProcessInfoArgs = z
  .object({
    process_id: ProcessIdField.nullable(),
    owner_handle: z.string().nullable(),
    process_code: z.string().nullable(),
  })
  .partial()
  .passthrough()
const GetProcessViewRequestData = z.object({ process_info_args: ProcessInfoArgs }).passthrough()
const FormStep0 = z
  .object({
    id: FormStepIdField.uuid(),
    description: z.string(),
    calculated_tags: z.array(CalculatedTag),
    process: z.string().uuid(),
    code: z.string(),
    step: z.string().uuid(),
    name: z.string(),
    order: z.number().int(),
    external: z.boolean(),
    due: z.string().nullable(),
    sla: z.string().nullable(),
    button_name: z.string().nullable(),
    set_status: StatusesEnum.nullable(),
    added_at: z.string().datetime({ offset: true }).nullable(),
    removed_at: z.string().datetime({ offset: true }).nullable(),
  })
  .passthrough()
const SectionSubstep0 = z
  .object({
    id: z.string().uuid(),
    process: z.string().uuid(),
    name: z.string(),
    description: z.string(),
    order: z.number().int(),
    form: z.string().uuid(),
    section: z.string().uuid().nullable(),
    button_name: z.string().nullable(),
    added_at: z.string().datetime({ offset: true }).nullable(),
    removed_at: z.string().datetime({ offset: true }).nullable(),
    multiple: z.boolean(),
    min: z.number().int(),
    max: z.number().int(),
    render: z.string().uuid().nullable(),
  })
  .passthrough()
const ApplyWithoutVerificationEnum = z.enum(['none', 'email'])
const Process3 = z
  .object({
    bill_steps: z.array(BillStep1),
    form_steps: z.array(FormStep0),
    inputs: z.array(InputSubstep2),
    subsections: z.array(SectionSubstep0),
    owner: Identity0,
    page_breaks: z.array(FormStyleElement),
    id: z.string().uuid(),
    code: z.string(),
    name: z.string(),
    description: z.string(),
    status: Status2efEnum,
    appliable: z.boolean(),
    copyable: z.boolean(),
    velosimo_integrated: z.boolean(),
    apply_without_verification: ApplyWithoutVerificationEnum.nullable(),
    icon: z.string().nullable(),
    button_name: z.string().nullable(),
    pinned: z.boolean().nullable(),
    review_steps_enabled: z.boolean().optional(),
  })
  .passthrough()
const GetProcessViewResponseData = z.object({ process: Process3 }).passthrough()
const ProcessCaseListArgs = z
  .object({
    process_id: ProcessIdField.nullable(),
    case_ids: z.array(z.string().uuid()).nullable(),
    case_handle: z.string().nullable(),
    managed_only: z.boolean(),
    limit: z.number().int().nullable(),
    offset: z.number().int().nullable(),
    status: StatusesEnum.nullable(),
  })
  .partial()
  .passthrough()
const GetProcessCaseCountViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), process_case_list_args: ProcessCaseListArgs })
  .passthrough()
const GetProcessCaseCountViewResponseData = z.object({ count: z.number().int() }).passthrough()
const GetTaskViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), task_id: TaskIdField.uuid() })
  .passthrough()
const Task1 = z
  .object({
    id: z.string().uuid(),
    owner: z.string().uuid(),
    name: z.string(),
    description: z.string(),
    created: z.string().datetime({ offset: true }),
    creator: Identity0.nullable(),
    complete: z.boolean(),
    completed: z.string().datetime({ offset: true }).nullable(),
    due: z.string().datetime({ offset: true }).nullable(),
    case_id: z.string().uuid().nullable(),
    item_id: z.string().uuid(),
  })
  .passthrough()
const GetTaskViewResponseData = z.object({ task: Task1 }).passthrough()
const ImportEmailViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    internet_message_id: z.string(),
    subject: z.string(),
    sender: z.string(),
    recipients: z.array(z.string()),
    message: z.string(),
  })
  .passthrough()
const ImportEmailViewResponseData = z.object({ message_id: MessageIdField.uuid() }).passthrough()
const InstantiateStepsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    step_ids: z.array(StepIdField),
    case_id: CaseIdField.uuid(),
  })
  .passthrough()
const ListAnnotatedDocumentsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    document_step_id: DocumentStepIdField.uuid(),
    case_id: CaseIdField.uuid(),
  })
  .passthrough()
const AnnotatedDocumentIdField = z.string()
const AnnotatedDocument = z
  .object({
    id: AnnotatedDocumentIdField.uuid(),
    case: CaseIdField.uuid(),
    document_step: DocumentStepIdField.uuid(),
    created: z.string().datetime({ offset: true }),
    aws_url: z.string(),
    aws_access_url: z.string().nullable(),
  })
  .passthrough()
const ListAnnotatedDocumentsViewResponseData = z
  .object({ annotated_documents: z.array(AnnotatedDocument) })
  .passthrough()
const ListCaseAutomationEventLogsViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), case_id: CaseIdField.uuid() })
  .passthrough()
const CaseAutomationEventLogIdField = z.string()
const EventTypeEnum = z.enum(['AddCaseMember', 'FormReview'])
const CaseAutomationEventLog = z
  .object({
    id: CaseAutomationEventLogIdField.uuid(),
    case: CaseIdField.uuid(),
    event_type: EventTypeEnum,
    trigger: z.string(),
    log: z.string(),
    time: z.string().datetime({ offset: true }),
    metadata: z.unknown().nullish(),
  })
  .passthrough()
const ListCaseAutomationEventLogsViewResponseData = z
  .object({ logs: z.array(CaseAutomationEventLog) })
  .passthrough()
const ListCaseMembersViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), case_id: CaseIdField.uuid() })
  .passthrough()
const ListCaseMembersViewResponseData = z.object({ members: z.array(Identity2) }).passthrough()
const CaseStatusChangeIdField = z.string()
const ListCaseStatusChangesViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    case_ids: z.array(CaseIdField),
    exclude_ids: z.array(CaseStatusChangeIdField).optional(),
  })
  .passthrough()
const CaseStatusChange0 = z
  .object({
    setter: Identity0.nullable(),
    id: z.string().uuid(),
    case: z.string().uuid(),
    status: StatusesEnum,
    time: z.string().datetime({ offset: true }),
  })
  .passthrough()
const ListCaseStatusChangesViewResponseData = z
  .object({ status_changes: z.array(CaseStatusChange0) })
  .passthrough()
const SortEnum = z.enum([
  'by_last_action_asc',
  'by_last_action_desc',
  'by_next_due',
  'by_last_message',
  'by_created_asc',
  'by_created_desc',
])
const ListCasesViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    filter: CaseFilter,
    sort: SortEnum,
    offset: z.number().int(),
    limit: z.number().int(),
  })
  .passthrough()
const ListCasesViewResponseData = z.object({ data: z.array(Case2) }).passthrough()
const ListCasesTabsViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), tabs: z.array(CasesTabIdField).optional() })
  .passthrough()
const CasesTab = z
  .object({
    id: z.string().uuid().optional(),
    code: z.string().regex(/^[a-z0-9-]+$/),
    name: z.string(),
    order: z.number().int().gte(-9223372036854776000).lte(9223372036854776000),
    page_size: z.number().int().gte(-2147483648).lte(2147483647).nullish(),
    custom_filters: z.unknown().nullish(),
    owner: z.string().uuid(),
  })
  .passthrough()
const CasesConfigTypeEnum = z.literal('saved')
const CasesConfig = z
  .object({
    id: z.string().uuid().optional(),
    type: CasesConfigTypeEnum.optional().default('saved'),
    created_after: z.string().datetime({ offset: true }).nullish(),
    created_before: z.string().datetime({ offset: true }).nullish(),
    next_due_after: z.string().datetime({ offset: true }).nullish(),
    next_due_before: z.string().datetime({ offset: true }).nullish(),
    no_manager: z.boolean().nullish(),
    tab: z.string().uuid(),
  })
  .passthrough()
const CasesConfig1 = z
  .object({
    config: CasesConfig,
    assignee: z.array(z.string().uuid()).nullable(),
    manager: z.array(z.string().uuid()).nullable(),
    owner: z.array(z.string().uuid()).nullable(),
    process: z.array(z.string().uuid()).nullable(),
    status: z.array(StatusesEnum).nullable(),
    tag: z.array(z.string().uuid()).nullable(),
  })
  .passthrough()
const CasesTab1 = z
  .object({
    tab: CasesTab,
    config: CasesConfig1.nullish(),
    input_step_assignees: z.record(z.array(Identity0)).nullish(),
    process_step_ids: z.array(z.string().uuid()).nullish(),
    input_choices: z.record(z.string()).nullish(),
    input_text: z.record(z.string()).nullish(),
    input_bool: z.record(z.boolean()).nullish(),
    input_number: z.record(NumberComparison).nullish(),
  })
  .passthrough()
const ListCasesTabsViewResponseData = z.object({ tabs: z.array(CasesTab1) }).passthrough()
const ListCasesWithLocationViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), filter: CaseFilter })
  .passthrough()
const CaseWithLocation = z
  .object({
    id: z.string().uuid(),
    title: z.string(),
    handle: z.string(),
    owner_identity_id: z.string().uuid(),
    location: Location0,
  })
  .passthrough()
const ListCasesWithLocationViewResponseData = z
  .object({ cases: z.array(CaseWithLocation) })
  .passthrough()
const ListCommentsViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), form_id: FormIdField.uuid() })
  .passthrough()
const CommentThread = z
  .object({
    id: CommentThreadIdField.uuid(),
    resolved: z.boolean().optional(),
    form: FormIdField.uuid(),
    input: z.string().uuid().nullish(),
    comments: z.array(Comment),
  })
  .passthrough()
const ListCommentsViewResponseData = z.object({ comments: z.array(CommentThread) }).passthrough()
const ListDraftsViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), case_id: CaseIdField.uuid().optional() })
  .passthrough()
const Draft = z
  .object({
    message_id: MessageIdField.uuid(),
    author: Identity0,
    attachments: z.array(Attachment),
    case: z.string(),
    text: z.string(),
    body: z.string(),
    time: z.string().datetime({ offset: true }),
  })
  .passthrough()
const ListDraftsViewResponseData = z.object({ drafts: z.array(Draft) }).passthrough()
const ListExpressionsViewRequestData = z
  .object({
    expression_ids: z.array(ExpressionIdField),
    process_id: ProcessIdField.uuid(),
    input_step_ids: z.array(InputStepIdField),
  })
  .partial()
  .passthrough()
const ListExpressionsViewResponseData = z.object({ data: z.array(Expression) }).passthrough()
const ListFormStepsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    form_step_id: FormStepIdField.uuid().optional(),
    process_ids: z.array(ProcessIdField).optional(),
    has_sla: z.boolean().optional().default(false),
    managed_only: z.boolean().optional().default(false),
  })
  .passthrough()
const ListFormStepsViewResponseData = z.object({ form_steps: z.array(FormStep0) }).passthrough()
const ListFormsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    form_ids: z.array(FormIdField).optional(),
    item_ids: z.array(ItemIdField).optional(),
    case_ids: z.array(CaseIdField).optional(),
    step_ids: z.array(StepIdField).optional(),
  })
  .passthrough()
const Form0 = z
  .object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string(),
    created: z.string().datetime({ offset: true }),
    completed: z.string().datetime({ offset: true }).nullable(),
    completer: z.string().uuid().nullable(),
    completer_name: z.string().nullable(),
    process_id: z.string().uuid(),
    case_id: z.string().uuid(),
    form_step_id: z.string().uuid(),
    step_id: z.string().uuid(),
    order: z.number().int(),
    due: z.string().datetime({ offset: true }).nullable(),
    item: z.string().uuid(),
    action: z.string().uuid(),
    in_review: z.boolean(),
  })
  .passthrough()
const ListFormsViewResponseData = z.object({ forms: z.array(Form0) }).passthrough()
const InputStepFilter = z
  .object({
    input_step_ids: z.array(z.string().uuid()).nullable(),
    process_ids: z.array(z.string().uuid()).nullable(),
    form_step_ids: z.array(z.string().uuid()).nullable(),
    pinned_only: z.boolean(),
    has_sla: z.boolean(),
    managed_only: z.boolean(),
  })
  .partial()
  .passthrough()
const ListInputSubStepsViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), input_step_filter: InputStepFilter })
  .passthrough()
const ListInputSubStepsViewResponseData = z
  .object({ input_substeps: z.array(InputSubstep2) })
  .passthrough()
const InputFilter = z
  .object({
    input_id: InputIdField.nullable(),
    form_ids: z.array(z.string().uuid()).nullable(),
    case_ids: z.array(z.string().uuid()).nullable(),
    process_id: ProcessIdField.nullable(),
    input_step_ids: z.array(z.string().uuid()).nullable(),
    complete: z.boolean().nullable(),
    external_only: z.boolean(),
    pinned_only: z.boolean(),
  })
  .partial()
  .passthrough()
const ListInputsViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), input_filter: InputFilter })
  .passthrough()
const Input1 = z
  .object({
    value: Value0.nullish(),
    template: Value0.nullish(),
    id: z.string().uuid(),
    name: z.string(),
    description: z.string(),
    type: ValueTypeEnum,
    value_id: z.string().uuid().nullable(),
    template_id: z.string().uuid().nullable(),
    setter: z.string().uuid().nullable(),
    complete: z.boolean(),
    created: z.string().datetime({ offset: true }),
    completed: z.string().datetime({ offset: true }).nullable(),
    process_id: z.string().uuid(),
    case_id: z.string().uuid(),
    step_id: z.string().uuid(),
    form_id: z.string().uuid(),
    item_id: z.string().uuid(),
    order: z.number().int().nullable(),
    due: z.string().datetime({ offset: true }).nullable(),
    section: z.string().uuid().nullable(),
  })
  .passthrough()
const ListInputsViewResponseData = z.object({ inputs: z.array(Input1) }).passthrough()
const ListItemsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    item_ids: z.array(ItemIdField).optional(),
    complete: z.boolean().optional(),
    case_ids: z.array(CaseIdField).optional(),
  })
  .passthrough()
const BillIdField = z.string()
const ActionIdField = z.string()
const Bill0 = z
  .object({
    id: BillIdField.uuid(),
    receiver_id: IdentityIdField.uuid(),
    code: z.string(),
    name: z.string(),
    description: z.string(),
    payer: IdentityIdField.nullable(),
    installments: z.number().int(),
    editable: z.boolean(),
    complete: z.boolean(),
    created: z.string().datetime({ offset: true }),
    due: z.string().datetime({ offset: true }).nullable(),
    completed: z.string().datetime({ offset: true }).nullable(),
    process_id: ProcessIdField.uuid(),
    bill_step_id: BillStepIdField.uuid(),
    allow_ach: z.boolean(),
    step_id: StepIdField.uuid(),
    case_id: CaseIdField.uuid(),
    order: z.number().int().nullable(),
    action_id: ActionIdField.uuid(),
    item_id: ItemIdField.uuid(),
  })
  .passthrough()
const Message0 = z
  .object({
    id: z.string().uuid(),
    body: z.string(),
    text: z.string(),
    author_id: z.string().uuid(),
    creator_id: z.string().uuid(),
    time: z.string().datetime({ offset: true }),
    due: z.string().datetime({ offset: true }).nullable(),
    medium: MediumEnum,
    email_message_id: z.string().nullable(),
    email_subject: z.string().nullable(),
    html: z.string().nullable(),
    completed: z.string().datetime({ offset: true }).nullable(),
    case: z.string().uuid().nullable(),
    item_id: z.string().uuid(),
  })
  .passthrough()
const ListItemsViewResponseData = z
  .object({
    bills: z.array(Bill0),
    forms: z.array(Form0),
    messages: z.array(Message0),
    tasks: z.array(Task1),
  })
  .passthrough()
const ListMessagesViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    message_id: MessageIdField.uuid().optional(),
    item_ids: z.array(ItemIdField).optional(),
    case_handles: z.array(z.string()).optional(),
    case_ids: z.array(CaseIdField).optional(),
    participant_ids: z.array(ContactIdField).optional(),
    start: z.string().datetime({ offset: true }).optional(),
    end: z.string().datetime({ offset: true }).optional(),
    only_my_mailbox: z.boolean().optional().default(false),
    email_message_ids: z.array(z.string()).optional(),
    excludes: z.array(MessageIdField).optional().default([]),
    limit: z.number().int().optional(),
  })
  .passthrough()
const HandleIdField = z.string()
const Case = z
  .object({
    id: CaseIdField.uuid(),
    name: z.string(),
    location: LocationIdField.nullish(),
    status: StatusesEnum,
    owner_id: IdentityIdField.uuid(),
    requester_id: IdentityIdField.nullish(),
    assignee_id: IdentityIdField.nullish(),
    created: z.string().datetime({ offset: true }).optional(),
    creator_id: EndUserIdField.nullish(),
    process_id: ProcessIdField.nullish(),
    handle_id: HandleIdField.nullish(),
    target_id: TargetIdField.nullish(),
  })
  .passthrough()
const Log1 = z
  .object({
    case: Case.optional(),
    attachments: z.array(Attachment),
    author: Identity2,
    creator: Identity2,
    id: z.string().uuid(),
    body: z.string(),
    text: z.string(),
    participant_ids: z.array(z.string().uuid()),
    time: z.string().datetime({ offset: true }),
    medium: MediumEnum,
    completed: z.string().datetime({ offset: true }).nullable(),
    item_id: z.string().uuid(),
  })
  .passthrough()
const EmailAddress0 = z
  .object({ id: EmailAddressIdField.uuid(), address: z.string() })
  .passthrough()
const EmailIdField = z.string()
const EmailParticipant0TypeEnum = z.enum(['to', 'cc', 'bcc', 'from'])
const EmailParticipant0 = z
  .object({
    address: EmailAddress0,
    email: EmailIdField.uuid(),
    type: EmailParticipant0TypeEnum,
    name: z.string(),
    message_id: MessageIdField.uuid(),
  })
  .passthrough()
const Email1 = z
  .object({
    participants: z.array(EmailParticipant0),
    case: Case.nullish(),
    attachments: z.array(Attachment),
    body: z.string(),
    text: z.string(),
    time: z.string().datetime({ offset: true }),
    medium: MediumEnum,
    message_id: z.string().nullable(),
    subject: z.string(),
    html: z.string().nullable(),
    completed: z.string().datetime({ offset: true }).nullable(),
    item_id: ItemIdField.uuid(),
    id: MessageIdField.uuid(),
  })
  .passthrough()
const ListMessagesViewResponseData = z
  .object({ logs: z.array(Log1), emails: z.array(Email1), limit: z.boolean() })
  .passthrough()
const ListNearbyCasesViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), location_id: LocationIdField.uuid() })
  .passthrough()
const ListNearbyCasesViewResponseData = z.object({ cases: z.array(Case2) }).passthrough()
const NotificationFilter = z
  .object({
    notification_id: NotificationIdField.nullable(),
    item_ids: z.array(z.string().uuid()).nullable(),
    case_ids: z.array(z.string().uuid()).nullable(),
  })
  .partial()
  .passthrough()
const ListNotificationsViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), notification_filter: NotificationFilter })
  .passthrough()
const Notification1 = z
  .object({
    assignee: Identity0,
    id: z.string().uuid(),
    item_id: z.string().uuid(),
    after: z.string().datetime({ offset: true }),
    assigned: z.boolean(),
    read: z.boolean(),
  })
  .passthrough()
const ListNotificationsViewResponseData = z
  .object({ notifications: z.array(Notification1) })
  .passthrough()
const ListProcessCasesViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), process_case_list_args: ProcessCaseListArgs })
  .passthrough()
const ProcessCase3 = z
  .object({
    process: Process3,
    case: Case2,
    case_id: z.string().uuid(),
    name: z.string(),
    bill_actions: z.array(Bill0),
    form_actions: z.array(Form0),
    page_breaks: z.array(z.string().uuid()),
    inputs: z.array(Input1),
  })
  .passthrough()
const ListProcessCasesViewResponseData = z
  .object({ process_case_3s: z.array(ProcessCase3) })
  .passthrough()
const ListProcessSummariesViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), case_ids: z.array(CaseIdField) })
  .passthrough()
const ProcessSummary = z
  .object({
    id: z.string().uuid(),
    case_id: z.string().uuid(),
    name: z.string(),
    status: Status2efEnum,
    current_step_id: z.string().uuid().nullable(),
    current_step_name: z.string().nullable(),
    total_steps: z.number().int(),
    completed_steps: z.number().int(),
    unverified_apply: z.boolean().optional(),
  })
  .passthrough()
const ListProcessSummariesViewResponseData = z
  .object({ processes: z.array(ProcessSummary) })
  .passthrough()
const PhoneNumberIdField = z.string()
const ProcessFilter = z
  .object({
    owners: z.array(z.string().uuid()).nullable(),
    governing_units: z.array(z.string().uuid()).nullable(),
    has_bill: z.boolean(),
    code: z.string().nullable(),
    owner_handle: z.string().nullable(),
    process_ids: z.array(z.string().uuid()).nullable(),
    phone_line: PhoneNumberIdField.nullable(),
    has_phone: z.boolean(),
    managed_only: z.boolean(),
    copyable_only: z.boolean(),
    appliable_only: z.boolean(),
    status: z.array(Status2efEnum).nullable(),
    query: z.string().nullable(),
  })
  .partial()
  .passthrough()
const ListProcessesViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), process_filter: ProcessFilter })
  .passthrough()
const Process0 = z
  .object({
    description: z.string(),
    id: z.string().uuid(),
    owner: Identity0,
    code: z.string(),
    name: z.string(),
    status: Status2efEnum,
    appliable: z.boolean(),
    copyable: z.boolean(),
    velosimo_integrated: z.boolean(),
    apply_without_verification: ApplyWithoutVerificationEnum.nullable(),
    icon: z.string().nullable(),
    button_name: z.string().nullable(),
    pinned: z.boolean().nullable(),
    phone_line: z.string().nullable(),
  })
  .passthrough()
const ListProcessesViewResponseData = z.object({ processes: z.array(Process0) }).passthrough()
const ListProcessesStatsViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), process_filter: ProcessFilter })
  .passthrough()
const Process1 = z
  .object({
    description: z.string(),
    id: z.string().uuid(),
    owner: Identity0,
    code: z.string(),
    name: z.string(),
    status: Status2efEnum,
    appliable: z.boolean(),
    copyable: z.boolean(),
    velosimo_integrated: z.boolean(),
    apply_without_verification: ApplyWithoutVerificationEnum.nullable(),
    icon: z.string().nullable(),
    button_name: z.string().nullable(),
    pinned: z.boolean().nullable(),
    total: z.number().int(),
    completed: z.number().int(),
  })
  .passthrough()
const ListProcessesStatsViewResponseData = z.object({ processes: z.array(Process1) }).passthrough()
const ListRelationsViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), case_id: CaseIdField.uuid() })
  .passthrough()
const RelationIdField = z.string()
const Relation = z
  .object({ id: RelationIdField.uuid(), from_field: CaseIdField.uuid(), to: CaseIdField.uuid() })
  .passthrough()
const ListRelationsViewResponseData = z.object({ relations: z.array(Relation) }).passthrough()
const ListSectionSubStepsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    section_step_ids: z.array(SectionStepIdField).optional(),
    process_ids: z.array(ProcessIdField).optional(),
    expression_ids: z.array(ExpressionIdField).optional(),
  })
  .passthrough()
const ListSectionSubStepsViewResponseData = z
  .object({ section_substeps: z.array(SectionSubstep0) })
  .passthrough()
const ListSectionsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    section_ids: z.array(SectionIdField).optional(),
    form_ids: z.array(FormIdField).optional(),
    case_ids: z.array(CaseIdField).optional(),
    process_id: ProcessIdField.uuid().optional(),
    section_step_ids: z.array(SectionStepIdField).optional(),
    complete: z.boolean().optional(),
    external_only: z.boolean().optional().default(false),
  })
  .passthrough()
const Section = z
  .object({
    id: SectionIdField.uuid(),
    form: FormIdField.uuid(),
    substep: SectionStepIdField.uuid(),
    form_step: FormStepIdField.uuid(),
    section: SectionIdField.nullish(),
    created: z.string().datetime({ offset: true }).optional(),
    order: z.number().int().gte(-9223372036854776000).lte(9223372036854776000).optional(),
  })
  .passthrough()
const ListSectionsViewResponseData = z.object({ sections: z.array(Section) }).passthrough()
const ListStepAssignmentsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    step_ids: z.array(StepIdField).optional(),
    expression_ids: z.array(ExpressionIdField).optional(),
  })
  .passthrough()
const StepNotificationIdField = z.string()
const StepNotification = z
  .object({
    id: StepNotificationIdField.uuid(),
    step: StepIdField.uuid(),
    assigned: z.boolean(),
    user: EndUserIdField.uuid(),
    user_type: ValueTypeEnum,
    step_name: z.string(),
  })
  .passthrough()
const ListStepAssignmentsViewResponseData = z
  .object({ step_assignments: z.array(StepNotification) })
  .passthrough()
const ListTasksViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    task_id: TaskIdField.uuid().optional(),
    item_ids: z.array(ItemIdField).optional(),
    case_ids: z.array(CaseIdField).optional(),
  })
  .passthrough()
const ListTasksViewResponseData = z.object({ tasks: z.array(Task1) }).passthrough()
const ListTemplatesViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    template_id: MessageTemplateIdField.uuid().optional(),
    owner_ids: z.array(IdentityIdField).optional(),
    organization_code: z.string().optional(),
    template_code: z.string().optional(),
    query: z.string().optional(),
    limit: z.number().int().optional(),
  })
  .passthrough()
const MessageTemplate0 = z
  .object({
    owner: Identity0,
    attachments: z.array(Attachment),
    name: z.string(),
    body: z.string(),
    id: z.string().uuid(),
    code: z.string(),
    modified: z.string().datetime({ offset: true }),
  })
  .passthrough()
const ListTemplatesViewResponseData = z
  .object({ templates: z.array(MessageTemplate0) })
  .passthrough()
const ListValuesViewRequestData = z
  .object({ value_ids: z.array(ValueIdField), type: ValueTypeEnum.optional() })
  .passthrough()
const ListValuesViewResponseData = z.object({ values: z.array(Value0) }).passthrough()
const MarkCaseReadViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), case_id: CaseIdField.uuid() })
  .passthrough()
const NotifyArgs = z
  .object({
    item_id: ItemIdField.uuid(),
    assignee: IdentityIdField.uuid(),
    assigned: z.boolean(),
    send: z.boolean(),
    after: z.string().datetime({ offset: true }).nullish(),
  })
  .passthrough()
const NotifyViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), notify_args: NotifyArgs })
  .passthrough()
const RemoveCaseMemberViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    case_id: CaseIdField.uuid(),
    identity_id: IdentityIdField.uuid(),
  })
  .passthrough()
const ResolveThreadViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    thread_id: CommentThreadIdField.uuid(),
    is_resolved: z.boolean().optional().default(true),
  })
  .passthrough()
const Type6b9Enum = z.enum(['input', 'section', 'step'])
const FormDataNode = z
  .object({
    value: ValueCreate.nullish(),
    parent_section: z.string().nullish(),
    type: Type6b9Enum,
    id: z.string(),
  })
  .passthrough()
const FormNodeCreate = z
  .object({
    id: z.string().uuid(),
    input: InputSubstep2.nullish(),
    section: SectionSubstep0.nullish(),
    form_step: FormStep0.nullish(),
    instances: z.array(FormDataNode),
    type: Type6b9Enum,
    children: z.array(z.string().uuid()).nullable(),
    parent: z.string().uuid().nullish(),
    name: z.string(),
    description: z.string().nullable(),
    order: z.number().int(),
    required: z.boolean().nullable(),
    shouldRender: z.boolean(),
    siblings: z.array(z.string()).nullable(),
  })
  .passthrough()
const SaveOrSubmitFormViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    mark_form_complete: z.boolean(),
    form_nodes: z.array(FormNodeCreate).optional(),
    form_id: FormIdField.uuid().optional(),
  })
  .passthrough()
const SearchCasesViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    query: z.string(),
    offset: z.number().int(),
    limit: z.number().int(),
  })
  .passthrough()
const SearchCasesViewResponseData = z.object({ cases: z.array(Case2) }).passthrough()
const SearchCasesCountViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), query: z.string() })
  .passthrough()
const SearchCasesCountViewResponseData = z.object({ count: z.number().int() }).passthrough()
const SearchProcessesViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    query: z.string().optional(),
    has_bill: z.boolean(),
    limit: z.number().int().optional(),
    status: z.array(Status2efEnum).optional(),
  })
  .passthrough()
const SearchProcessesViewResponseData = z.object({ processes: z.array(Process0) }).passthrough()
const VelosimoEventEnum = z.enum([
  'add_note',
  'update_status',
  'submit_form',
  'sync_case',
  'update_form',
])
const SendVelosimoCaseViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    case_id: CaseIdField.uuid(),
    velosimo_event: VelosimoEventEnum,
  })
  .passthrough()
const SetActionCompleteViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    action_id: ActionIdField.uuid(),
    complete: z.boolean(),
    completed: z.string().datetime({ offset: true }).optional(),
  })
  .passthrough()
const SetActionInReviewViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    action_id: ActionIdField.uuid(),
    in_review: z.boolean(),
  })
  .passthrough()
const SetCaseStatusViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), case_id: CaseIdField.uuid(), status: StatusesEnum })
  .passthrough()
const StepAssignmentCreate = z
  .object({
    step_id: StepIdField.uuid(),
    user_expression_id: ExpressionIdField.uuid(),
    assigned: z.boolean(),
  })
  .passthrough()
const SetStepAssignmentsViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), step_assignments: z.array(StepAssignmentCreate) })
  .passthrough()
const SetStepAssignmentsViewResponseData = z
  .object({ step_assignments: z.array(StepNotification) })
  .passthrough()
const StartProcessViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid().optional(),
    email: z.string().email().optional(),
    process_id: ProcessIdField.uuid(),
    token: z.string().optional(),
  })
  .passthrough()
const StartProcessViewResponseData = z
  .object({
    user_id: EndUserIdField.uuid().optional(),
    case_id: CaseIdField.uuid(),
    handle: z.string(),
  })
  .passthrough()
const CasesConfigUpsert = z
  .object({
    tab_id: CasesTabIdField.uuid(),
    created_after: z.string().datetime({ offset: true }).nullish(),
    created_before: z.string().datetime({ offset: true }).nullish(),
    next_due_after: z.string().datetime({ offset: true }).nullish(),
    next_due_before: z.string().datetime({ offset: true }).nullish(),
    no_manager: z.boolean().nullish(),
    assignee: z.array(z.string().uuid()).nullish(),
    manager: z.array(z.string().uuid()).nullish(),
    owner: z.array(z.string().uuid()).nullish(),
    process: z.array(z.string().uuid()).nullish(),
    status: z.array(StatusesEnum).nullish(),
    tag: z.array(z.string().uuid()).nullish(),
    count: z.number().int().nullish(),
    process_step_ids: z.array(z.string().uuid()).nullish(),
    input_step_assignees: z.record(z.array(z.string().uuid())).optional(),
    input_choices: z.record(z.string()).optional(),
    input_text: z.record(z.string()).optional(),
    input_bool: z.record(z.boolean()).optional(),
    input_number: z.record(NumberComparison).optional(),
  })
  .passthrough()
const UpsertCasesConfigViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), config: CasesConfigUpsert })
  .passthrough()
const UpsertTemplateViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    template_id: MessageTemplateIdField.uuid().optional(),
    name: z.string(),
    body: z.string(),
    attachments: z.array(Attachment),
  })
  .passthrough()
const UpsertTemplateViewResponseData = z
  .object({ template_id: MessageTemplateIdField.uuid() })
  .passthrough()
const AddAddressViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    address_id: AddressIdField.uuid(),
    contact_id: ContactIdField.uuid().optional(),
  })
  .passthrough()
const AddPhoneNumberViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    contact_id: ContactIdField.uuid().optional(),
    phone_number: z.string(),
  })
  .passthrough()
const ContactFilter = z
  .object({
    book: IdentityIdField.nullable(),
    tags: z.array(z.string().uuid()).nullable(),
    points: z.array(GeoPoint).nullable(),
    name: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    address_street: z.string().nullable(),
    attribute_name: z.string().nullable(),
    attribution_value: z.string().nullable(),
    entries: z.array(z.string().uuid()).nullable(),
  })
  .partial()
  .passthrough()
const CountContactsViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), contact_filter: ContactFilter })
  .passthrough()
const CountContactsViewResponseData = z.object({ num_contacts: z.number().int() }).passthrough()
const CreateAddressViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    street: z.string(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
    location: GeoPoint,
  })
  .passthrough()
const CreateAddressViewResponseData = z.object({ address: Address0 }).passthrough()
const AttributeIdField = z.string()
const AttributionCreate = z
  .object({ name: z.string(), value: z.string(), attribute_id: AttributeIdField.nullable() })
  .passthrough()
const CreateContactViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    title: z.string().optional(),
    first_name: z.string().optional(),
    middle_name: z.string().optional(),
    last_name: z.string().optional(),
    suffix: z.string().optional(),
    date_of_birth: z.string().nullish(),
    emails: z.array(z.string()),
    phones: z.array(z.string()),
    addresses: z.array(AddressIdField),
    tags: z.array(TagIdField),
    attributions: z.array(AttributionCreate),
    is_organization: z.boolean().optional(),
  })
  .passthrough()
const CreateContactViewResponseData = z
  .object({
    contact_entry_id: ContactEntryIdField.uuid().optional(),
    identity_id: IdentityIdField.uuid().optional(),
    target_id: TargetIdField.uuid().optional(),
    email_address_conflict: z.boolean(),
  })
  .passthrough()
const CreateContactFromCaseViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    case_id: CaseIdField.uuid(),
    identity_id: IdentityIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
  })
  .passthrough()
const CreateContactFromCaseViewResponseData = z
  .object({ contact_entry_id: ContactEntryIdField.uuid() })
  .passthrough()
const CreateEmailAddressViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    address: z.string(),
    verify: z.boolean(),
    contact_id: ContactIdField.uuid().optional(),
    with_force: z.boolean().optional().default(false),
  })
  .passthrough()
const CreateRelatedContactViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    from_contact_entry_id: ContactEntryIdField.uuid(),
    to_contact_entry_id: ContactEntryIdField.uuid(),
    name: z.string(),
  })
  .passthrough()
const DeleteContactViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), contact_entry_id: ContactEntryIdField.uuid() })
  .passthrough()
const DeleteRelatedContactViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    from_contact_entry_id: ContactEntryIdField.uuid(),
    to_contact_entry_id: ContactEntryIdField.uuid(),
  })
  .passthrough()
const DownloadContactsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    contact_filter: ContactFilter,
    excludes: z.array(ContactEntryIdField).optional(),
    query: z.string().optional(),
  })
  .passthrough()
const DownloadContactsViewResponseData = z
  .object({ email_recipient: z.string().email() })
  .passthrough()
const DraftEmailViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    from_id: EmailAddressIdField.uuid(),
    to_ids: z.array(EmailAddressIdField),
    bcc_ids: z.array(EmailAddressIdField),
    subject: z.string(),
    body: z.string(),
    attachments: z.array(Attachment),
    case_id: CaseIdField.uuid(),
  })
  .passthrough()
const DraftEmailViewResponseData = z.object({ email_id: EmailIdField.uuid() }).passthrough()
const VerifyAdmin = z.object({ identity: Identity0 }).passthrough()
const VerifyApply = z.object({ process: Process3 }).passthrough()
const OrganizationTypeEnum = z.enum([
  'OrganizationCity',
  'OrganizationCountry',
  'OrganizationCounty',
  'OrganizationDepartment',
  'OrganizationNGO',
  'OrganizationPoli',
  'OrganizationState',
])
const Identity1 = z
  .object({
    id: z.string().uuid(),
    contact_id: z.string().uuid(),
    handle: z.string(),
    name: z.string(),
    image: z.string().nullable(),
    stripe_id: z.string().nullable(),
    email: z.string().nullable(),
    contacts_owner_identity_id: z.string().uuid(),
    phone: z.string().nullable(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    birthday: z.string().nullable(),
    auto_payout: z.boolean().nullable(),
    organization_type: OrganizationTypeEnum.nullable(),
  })
  .passthrough()
const VerifyPayment = z.object({ identity: Identity1, code: z.string() }).passthrough()
const VerifyStandard = z.object({ uri: z.string().nullable() }).passthrough()
const VerifyThread = z
  .object({ identity: Identity0, title: z.string(), handle: z.string() })
  .passthrough()
const VerifyOptionsTypeEnum = z.enum([
  'apply',
  'admin',
  'payment',
  'signup',
  'standard',
  'thread',
  'login',
])
const VerifyLogin = z.object({ redirect_uri: z.string().nullable() }).passthrough()
const VerifyOptions = z
  .object({
    admin: VerifyAdmin.nullish(),
    apply: VerifyApply.nullish(),
    payment: VerifyPayment.nullish(),
    standard: VerifyStandard.nullish(),
    thread: VerifyThread.nullish(),
    type: VerifyOptionsTypeEnum,
    login: VerifyLogin.nullish(),
  })
  .passthrough()
const EmailSignupViewRequestData = z
  .object({ email: z.string(), verify_options: VerifyOptions })
  .passthrough()
const EmailSignupViewResponseData = z
  .object({ contact_email_id: ContactEmailIdField.uuid() })
  .partial()
  .passthrough()
const GetContactEntryViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), contact_entry_id: ContactEntryIdField.uuid() })
  .passthrough()
const ContactEntry0 = z
  .object({
    id: ContactEntryIdField.uuid(),
    owner: z.string(),
    owner_id: IdentityIdField.uuid(),
    handle: z.string(),
    target: TargetIdField.uuid(),
    name: z.string(),
    image: z.string().nullable(),
    contact: ContactIdField.uuid(),
    is_organization: z.boolean(),
  })
  .passthrough()
const GetContactEntryViewResponseData = z.object({ contact_entry: ContactEntry0 }).passthrough()
const GetContactEntryExpandedViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), owner_handle: z.string(), identity_handle: z.string() })
  .passthrough()
const PhoneNumber0 = z
  .object({
    phone_number_id: PhoneNumberIdField.uuid(),
    contact_id: ContactIdField.uuid(),
    phone_number: z.string(),
  })
  .passthrough()
const Attribute = z
  .object({
    id: z.string().uuid().optional(),
    owner_id: IdentityIdField.uuid(),
    code: z.string().regex(/^[a-z0-9-]+$/),
    name: z.string(),
    info: z.string(),
  })
  .passthrough()
const Attribution = z
  .object({
    id: z.string().uuid().optional(),
    target: z.string().uuid(),
    attribute: Attribute,
    value: z.string(),
  })
  .passthrough()
const ContactEntry2 = z
  .object({
    emails: z.array(ContactEmail0),
    phones: z.array(PhoneNumber0),
    addresses: z.array(Address0),
    tags: z.array(Tag),
    attributions: z.array(Attribution),
    id: ContactEntryIdField.uuid(),
    owner: z.string(),
    handle: z.string(),
    name: z.string(),
    image: z.string().nullable(),
    is_organization: z.boolean(),
  })
  .passthrough()
const Person = z
  .object({
    first_name: z.string(),
    middle_name: z.string().nullish(),
    last_name: z.string(),
    date_of_birth: z.string().nullish(),
    title: z.string().nullish(),
    suffix: z.string().nullish(),
  })
  .passthrough()
const GetContactEntryExpandedViewResponseData = z
  .object({ contact_entry: ContactEntry2, notes: z.array(Note0), person: Person.nullish() })
  .passthrough()
const GetContactEntryIdViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), owner_handle: z.string(), identity_handle: z.string() })
  .passthrough()
const GetContactEntryIdViewResponseData = z
  .object({ contact_entry_id: ContactEntryIdField.uuid() })
  .passthrough()
const GetContactEntryIdentityIdsViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), owner_handle: z.string(), identity_handle: z.string() })
  .passthrough()
const GetContactEntryIdentityIdsViewResponseData = z
  .object({
    identity_id: IdentityIdField.uuid(),
    contact_id: ContactIdField.uuid(),
    target_id: TargetIdField.uuid(),
  })
  .passthrough()
const GetContactsHeaderViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), file_url: z.string() })
  .passthrough()
const GetContactsHeaderViewResponseData = z
  .object({ contacts_header: z.array(z.string()) })
  .passthrough()
const GetMyLocationViewRequestData = z.object({ user_id: EndUserIdField.uuid() }).passthrough()
const GetMyLocationViewResponseData = z.object({ point: GeoPoint.nullable() }).passthrough()
const GetOrganizationLocationViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid().optional(),
    organization_identity_id: IdentityIdField.uuid(),
  })
  .passthrough()
const GetOrganizationLocationViewResponseData = z
  .object({ point: GeoPoint.nullable() })
  .passthrough()
const HandleInboundEmailViewRequestData = z
  .object({ token: z.string(), Records: z.array(z.object({}).partial().passthrough()).optional() })
  .passthrough()
const NameImportConfig = z
  .object({
    title: z.string().nullable(),
    first_name: z.string(),
    middle_name: z.string().nullable(),
    last_name: z.string(),
    suffix: z.string().nullable(),
  })
  .passthrough()
const AddressImportConfig = z
  .object({
    street: z.string(),
    unit: z.string().nullable(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
    longitude: z.string().nullable(),
    latitude: z.string().nullable(),
  })
  .passthrough()
const ImportContactsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    owner: IdentityIdField.uuid(),
    name: NameImportConfig,
    date_of_birth: z.string(),
    emails: z.array(z.string()),
    phones: z.array(z.string()),
    one_line_addresses: z.array(z.string()),
    structured_addresses: z.array(AddressImportConfig),
    attributions: z.array(z.array(z.string()).min(2).max(2)),
    tags: z.array(z.string()),
    title_flag: z.boolean(),
    update_contact_via_email_address_flag: z.boolean(),
    url: z.string(),
  })
  .passthrough()
const ImportContactsViewResponseData = z
  .object({ email_address_conflict: z.boolean() })
  .passthrough()
const ListAddressesViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    address_ids: z.array(AddressIdField).optional(),
    contact_ids: z.array(ContactIdField).optional(),
    location_ids: z.array(LocationIdField).optional(),
  })
  .passthrough()
const ListAddressesViewResponseData = z.object({ addresses: z.array(Address0) }).passthrough()
const ListContactEmailsViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), contact_filter: ContactFilter })
  .passthrough()
const ListContactEmailsViewResponseData = z
  .object({ contact_emails: z.array(ContactEmail0) })
  .passthrough()
const ListContactEntriesViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    contact_filter: ContactFilter,
    excludes: z.array(ContactEntryIdField).optional(),
    limit: z.number().int().gte(0).optional(),
    offset: z.number().int().gte(0).optional(),
  })
  .passthrough()
const ListContactEntriesViewResponseData = z
  .object({ contact_entries: z.array(ContactEntry2) })
  .passthrough()
const ListContactEntriesAddressPointsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    contact_filter: ContactFilter,
    excludes: z.array(ContactEntryIdField).optional(),
    limit: z.number().int().gte(0).optional(),
  })
  .passthrough()
const ListContactEntriesAddressPointsViewResponseData = z
  .object({ address_points: z.array(GeoPoint) })
  .passthrough()
const ListContactIdentitiesViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), contact_entry_id: ContactEntryIdField.uuid() })
  .passthrough()
const ListContactIdentitiesViewResponseData = z
  .object({ identities: z.array(IdentityIdField) })
  .passthrough()
const ListContactOwnersViewRequestData = z.object({ user_id: EndUserIdField.uuid() }).passthrough()
const ListContactOwnersViewResponseData = z
  .object({ owners: z.array(IdentityIdField) })
  .passthrough()
const ListContactRelationsViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), contact_entry_id: ContactEntryIdField.uuid() })
  .passthrough()
const RelatedContactEntry2 = z
  .object({
    emails: z.array(ContactEmail0),
    phones: z.array(PhoneNumber0),
    addresses: z.array(Address0),
    tags: z.array(Tag),
    attributions: z.array(Attribution),
    id: ContactEntryIdField.uuid(),
    owner: z.string(),
    handle: z.string(),
    name: z.string(),
    image: z.string().nullable(),
    is_organization: z.boolean(),
    relation: z.string(),
  })
  .passthrough()
const ListContactRelationsViewResponseData = z
  .object({ related_contacts: z.array(RelatedContactEntry2) })
  .passthrough()
const ListEmailAddressesViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    contact_ids: z.array(ContactIdField).optional(),
    identity_ids: z.array(IdentityIdField).optional(),
  })
  .passthrough()
const ListEmailAddressesViewResponseData = z
  .object({ contact_emails: z.array(ContactEmail0) })
  .passthrough()
const EmailFilter = z
  .object({
    only_my_mailbox: z.boolean(),
    message_id: z.array(z.string()).nullable(),
    internal_message_id: z.array(z.string().uuid()).nullable(),
  })
  .passthrough()
const ListEmailsViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), email_filter: EmailFilter })
  .passthrough()
const ListEmailsViewResponseData = z
  .object({ emails: z.array(Email1), has_more: z.boolean() })
  .passthrough()
const ListPhoneNumbersViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), contact_ids: z.array(ContactIdField).optional() })
  .passthrough()
const ListPhoneNumbersViewResponseData = z
  .object({ phone_numbers: z.array(PhoneNumber0) })
  .passthrough()
const ObtainEmailAddressViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), email: z.string() })
  .passthrough()
const ObtainEmailAddressViewResponseData = z
  .object({
    contact_email_id: ContactEmailIdField.uuid(),
    identity_id: IdentityIdField.uuid(),
    email_address_id: EmailAddressIdField.uuid(),
  })
  .passthrough()
const QuickCreateAddressViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), address: z.string() })
  .passthrough()
const QuickCreateAddressViewResponseData = z.object({ address: Address0 }).passthrough()
const RemoveAddressViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    address_id: AddressIdField.uuid(),
    contact_id: ContactIdField.uuid().optional(),
  })
  .passthrough()
const RemoveEmailAddressViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    contact_email_id: ContactEmailIdField.uuid(),
    contact_id: ContactIdField.uuid().optional(),
  })
  .passthrough()
const RemovePhoneNumberViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    phone_number_id: PhoneNumberIdField.uuid(),
    contact_id: ContactIdField.uuid().optional(),
  })
  .passthrough()
const ReverseSearchAddressesViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), location: GeoPoint })
  .passthrough()
const ReverseSearchAddressesViewResponseData = z
  .object({ addresses: z.array(z.string()) })
  .passthrough()
const SearchAddressesViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), address: z.string(), location: GeoPoint.optional() })
  .passthrough()
const SearchAddressesViewResponseData = z.object({ addresses: z.array(z.string()) }).passthrough()
const SearchContactEntriesViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    book_identity_id: IdentityIdField.uuid(),
    query: z.string(),
    contact_filter: ContactFilter,
    limit: z.number().int().optional(),
    excludes: z.array(ContactEntryIdField).optional(),
  })
  .passthrough()
const SearchContactEntriesViewResponseData = z
  .object({ contact_entries: z.array(ContactEntry2), has_more: z.boolean() })
  .passthrough()
const SearchContactsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    query: z.string(),
    names: z.boolean(),
    emails: z.boolean(),
    phones: z.boolean(),
    messages: z.boolean(),
    limit: z.number().int().optional(),
  })
  .passthrough()
const SearchContactsViewResponseData = z.object({ contacts: z.array(Identity2) }).passthrough()
const SearchEmailsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    query: z.string().min(2),
    limit: z.number().int().gte(1).lte(100).optional().default(20),
  })
  .passthrough()
const EmailAddressResult = z
  .object({
    id: EmailAddressIdField.uuid(),
    address: z.string(),
    contact_name: z.string().nullable(),
  })
  .passthrough()
const SearchEmailsViewResponseData = z
  .object({ results: z.array(EmailAddressResult) })
  .passthrough()
const SendEmailViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    from_id: EmailAddressIdField.uuid(),
    to_ids: z.array(EmailAddressIdField),
    cc_ids: z.array(EmailAddressIdField),
    bcc_ids: z.array(EmailAddressIdField),
    subject: z.string(),
    body: z.string(),
    attachments: z.array(Attachment),
    case_id: CaseIdField.uuid(),
  })
  .passthrough()
const SendEmailViewResponseData = z.object({ email_id: EmailIdField.uuid() }).passthrough()
const SendMassEmailViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    from_id: EmailAddressIdField.uuid(),
    to_ids: z.array(EmailAddressIdField),
    subject: z.string(),
    body: z.string(),
    attachments: z.array(Attachment),
    case_id: CaseIdField.uuid(),
  })
  .passthrough()
const SendVerificationEmailViewRequestData = z
  .object({
    contact_email_id: ContactEmailIdField.uuid(),
    email: z.string().email(),
    redirect_url: z.string(),
    verify_options: VerifyOptions,
  })
  .partial()
  .passthrough()
const SendVerificationEmailViewResponseData = z.object({ success: z.boolean() }).passthrough()
const SetEmailSignatureViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    contact_email_id: ContactEmailIdField.uuid(),
    signature: z.string(),
  })
  .passthrough()
const SetPrimaryEmailAddressViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), contact_email_id: ContactEmailIdField.uuid() })
  .passthrough()
const SetReplyToViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    email_address_id: EmailAddressIdField.uuid().optional(),
  })
  .passthrough()
const VerifyEmailViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    contact_email_id: ContactEmailIdField.uuid(),
    verify_options: VerifyOptions,
  })
  .passthrough()
const VerifyLoginViewRequestData = z
  .object({ contact_email_id: ContactEmailIdField.uuid(), verification_code: z.string() })
  .passthrough()
const VerifyLoginViewResponseData = z
  .object({
    user_id: EndUserIdField.uuid(),
    send_to_invite_flow: z.boolean(),
    default_organization_handle: z.string().nullish(),
  })
  .passthrough()
const RoleIdField = z.string()
const AddAdminViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    admin_id: IdentityIdField.uuid(),
    role_id: RoleIdField.uuid(),
  })
  .passthrough()
const AddPermissionViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    role_id: RoleIdField.uuid(),
    admin: z.boolean(),
    staff: z.boolean(),
  })
  .passthrough()
const AddRoleViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    role_name: z.string(),
  })
  .passthrough()
const AddRoleViewResponseData = z.object({ role_id: RoleIdField.uuid() }).passthrough()
const BreakdownContactTagsViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), organization_identity_id: IdentityIdField.uuid() })
  .passthrough()
const Tag0 = z.object({ tag: Tag, count: z.number().int() }).passthrough()
const BreakdownContactTagsViewResponseData = z.object({ tags: z.array(Tag0) }).passthrough()
const BreakdownTagsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    include_phone: z.boolean(),
    include_email: z.boolean(),
    include_in_person: z.boolean(),
    start_time: z.string().datetime({ offset: true }).optional(),
    end_time: z.string().datetime({ offset: true }).optional(),
    tag_ids: z.array(TagIdField).optional(),
  })
  .passthrough()
const BreakdownTagsViewResponseData = z.object({ tags: z.array(Tag0) }).passthrough()
const ChangePasswordViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    current_password: z.string(),
    new_password: z.string(),
  })
  .passthrough()
const CreateAttributionViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    target_id: TargetIdField.uuid(),
    attribute_id: AttributeIdField.uuid().optional(),
    name: z.string(),
    value: z.string(),
  })
  .passthrough()
const CreateDepartmentViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: OrganizationIdField.uuid(),
    suffix: z.string(),
    name: z.string(),
  })
  .passthrough()
const CreateDepartmentViewResponseData = z
  .object({ organization_id: OrganizationIdField.uuid() })
  .passthrough()
const CreateNoteViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    target_id: TargetIdField.uuid(),
    note: z.string(),
  })
  .passthrough()
const CreateNoteViewResponseData = z.object({ note_id: NoteIdField.uuid() }).passthrough()
const CreateTagViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    name: z.string(),
    color: z.string(),
    description: z.string().optional(),
  })
  .passthrough()
const CreateTagViewResponseData = z.object({ tag: Tag }).passthrough()
const DeleteNoteViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), note_id: NoteIdField.uuid() })
  .passthrough()
const DeleteTagViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), tag_id: TagIdField.uuid() })
  .passthrough()
const EditContactBirthdayViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    contact_id: ContactIdField.uuid().optional(),
    birthday: z.string(),
  })
  .passthrough()
const EditContactImageViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    contact_id: ContactIdField.uuid().optional(),
    identity_id: IdentityIdField.uuid().optional(),
    image: z.string(),
  })
  .passthrough()
const EditContactImageViewResponseData = z.object({ image: z.string() }).passthrough()
const EditContactNameViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    contact_id: ContactIdField.uuid().optional(),
    name: z.string(),
    title: z.string().optional(),
    first: z.string().optional(),
    middle: z.string().optional(),
    last: z.string().optional(),
    suffix: z.string().optional(),
  })
  .passthrough()
const EditRoleViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), role_id: RoleIdField.uuid(), name: z.string() })
  .passthrough()
const EditTagViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    tag_id: TagIdField.uuid(),
    name: z.string().optional(),
    color: z.string().optional(),
    description: z.string().optional(),
  })
  .passthrough()
const GetAccountViewRequestData = z.object({ user_id: EndUserIdField.uuid() }).passthrough()
const DigestFrequencyEnum = z.enum(['Never', 'Daily', 'Weekly'])
const GetAccountViewResponseData = z
  .object({
    title: z.string().nullable(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    profile: z.string().nullable(),
    notify: z.boolean(),
    digest_frequency: DigestFrequencyEnum.nullable(),
    auto_cc_case_emails: z.boolean(),
  })
  .passthrough()
const GetIdentityViewRequestData = z
  .object({ identity_id: IdentityIdField.uuid(), handle: z.string() })
  .partial()
  .passthrough()
const GetIdentityViewResponseData = z.object({ identity: Identity1 }).passthrough()
const GetIdentityBasicViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), contact_ids: z.array(ContactIdField) })
  .passthrough()
const GetIdentityBasicViewResponseData = z
  .object({ name: z.string(), profile: z.string().nullable(), birthday: z.string().nullable() })
  .passthrough()
const GetNoteViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), note_id: NoteIdField.uuid() })
  .passthrough()
const GetNoteViewResponseData = z.object({ note: Note0 }).passthrough()
const GetUserViewRequestData = z.object({ user_id: EndUserIdField.uuid() }).passthrough()
const SkinEnum = z.enum(['Casual', 'Professional'])
const GetUserViewResponseData = z
  .object({
    id: EndUserIdField.uuid(),
    identity_id: IdentityIdField.uuid(),
    logged_in: z.boolean(),
    verified: z.boolean().nullable(),
    name: z.string().nullable(),
    image: z.string().nullable(),
    super: z.boolean().nullable(),
    skin: SkinEnum.nullable(),
    beta: z.boolean().nullable(),
    has_password: z.boolean(),
    reply_to: EmailAddressIdField.nullable(),
    contact_id: ContactIdField.nullable(),
  })
  .passthrough()
const EmptyRequestSerializerEmptyRequest = z.object({ _body: z.string() }).partial().passthrough()
const GetUserFromSessionViewResponseData = z
  .object({ data: EndUserIdField.nullable() })
  .passthrough()
const GetUserIdsViewRequestData = z.object({ user_ids: z.array(EndUserIdField) }).passthrough()
const UserIds = z
  .object({
    user_id: EndUserIdField.uuid(),
    identity_id: IdentityIdField.uuid(),
    contact_id: ContactIdField.uuid(),
  })
  .passthrough()
const GetUserIdsViewResponseData = z.object({ users: z.array(UserIds) }).passthrough()
const IdentityToUserViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), identity_id: IdentityIdField.uuid() })
  .passthrough()
const IdentityToUserViewResponseData = z.object({ user_id: EndUserIdField.uuid() }).passthrough()
const ListAttributionsViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), target_ids: z.array(TargetIdField) })
  .passthrough()
const AttributionIdField = z.string()
const Attribution0 = z
  .object({ attribute: Attribute, id: AttributionIdField.uuid(), value: z.string() })
  .passthrough()
const ListAttributionsViewResponseData = z
  .object({ attributions: z.array(Attribution0) })
  .passthrough()
const ListDistinctEmailIdentitiesViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), u_first: z.boolean().optional() })
  .passthrough()
const EmailIdentity = z
  .object({
    id: IdentityIdField.uuid(),
    handle: z.string().nullable(),
    name: z.string().nullable(),
    image: z.string().nullable(),
    email: z.string().nullable(),
    email_address_id: EmailAddressIdField.nullable(),
    email_id: EmailIdField.nullable(),
    email_ses_verified: z.boolean().nullable(),
    email_signature: z.string().nullable(),
    email_stripe_id: z.string().nullable(),
    organization_type: z.string().nullable(),
    organization_id: OrganizationIdField.nullable(),
    is_direct_member: z.boolean(),
    is_staff: z.boolean(),
    is_admin: z.boolean(),
  })
  .passthrough()
const ListDistinctEmailIdentitiesViewResponseData = z
  .object({ data: z.array(EmailIdentity) })
  .passthrough()
const ListIdentitiesViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), identity_ids: z.array(IdentityIdField) })
  .passthrough()
const ListIdentitiesViewResponseData = z.object({ identities: z.array(Identity1) }).passthrough()
const ListIdentitiesByIdsViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), identity_ids: z.array(IdentityIdField) })
  .passthrough()
const ListIdentitiesByIdsViewResponseData = z
  .object({ identities: z.array(Identity2) })
  .passthrough()
const ListIdentityPermissionsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    only_me: z.boolean(),
  })
  .passthrough()
const IdentityPermissionIdField = z.string()
const IdentityPermission = z
  .object({
    id: IdentityPermissionIdField.uuid(),
    identity: IdentityIdField.uuid(),
    role: RoleIdField.uuid(),
    admin: z.boolean(),
    staff: z.boolean(),
  })
  .passthrough()
const ListIdentityPermissionsViewResponseData = z
  .object({ identity_permissions: z.array(IdentityPermission) })
  .passthrough()
const ListMembersViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), organization_identity_id: IdentityIdField.uuid() })
  .passthrough()
const Member0 = z.object({ identity: Identity0, role_id: RoleIdField.uuid() }).passthrough()
const ListMembersViewResponseData = z.object({ members: z.array(Member0) }).passthrough()
const ListNotesViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), target_ids: z.array(TargetIdField) })
  .passthrough()
const ListNotesViewResponseData = z.object({ notes: z.array(Note0) }).passthrough()
const TypesEnum = z.enum([
  'OrganizationCity',
  'OrganizationCountry',
  'OrganizationCounty',
  'OrganizationDepartment',
  'OrganizationNGO',
  'OrganizationPoli',
  'OrganizationState',
])
const ListOrganizationsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_ids: z.array(OrganizationIdField).optional(),
    identity_ids: z.array(IdentityIdField).optional(),
    phone_id: PhoneNumberIdField.uuid().optional(),
    parent_id: OrganizationIdField.uuid().optional(),
    types: z.array(TypesEnum).optional(),
    query: z.string().optional(),
  })
  .passthrough()
const OrganizationQuerySettings = z
  .object({
    seed: z.number().int().nullable(),
    search_temperature: z.number().nullable(),
    chat_temperature: z.number().nullable(),
    voice_temperature: z.number().nullable(),
    chat_prompt: z.string().nullable(),
    search_prompt: z.string().nullable(),
    voice_prompt: z.string().nullable(),
    sms_prompt: z.string().nullable(),
    email_prompt: z.string().nullable(),
  })
  .passthrough()
const Organization0 = z
  .object({
    query_settings: OrganizationQuerySettings,
    information: z.string(),
    id: z.string(),
    identity: z.string(),
    handle: z.string(),
    name: z.string(),
    image: z.string().nullable(),
    logo: z.string().nullable(),
    type: z.string(),
    banner: z.string().nullable(),
    background_color: z.string(),
    font_color: z.string(),
    search_url: z.string().nullable(),
    active: z.boolean(),
    premium_ai: z.boolean(),
    phone_line: z.string().nullable(),
    phone: z.string().nullable(),
    velosimo_integrated: z.boolean(),
  })
  .passthrough()
const ListOrganizationsViewResponseData = z
  .object({ organizations: z.array(Organization0) })
  .passthrough()
const ListParticipantsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    case_id: CaseIdField.uuid(),
    include_messages: z.boolean(),
  })
  .passthrough()
const ListParticipantsViewResponseData = z
  .object({ participants: z.array(Identity2) })
  .passthrough()
const ListQuillDashboardsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: OrganizationIdField.uuid(),
    organization_id: OrganizationIdField.uuid(),
  })
  .passthrough()
const QuillDashboardIdField = z.string()
const QuillDashboard = z
  .object({
    id: QuillDashboardIdField.uuid(),
    quill_name: z.string(),
    title: z.string(),
    created: z.string().datetime({ offset: true }).optional(),
    organization: z.string().uuid(),
    processes: z.array(z.string().uuid()).optional(),
  })
  .passthrough()
const ListQuillDashboardsViewResponseData = z
  .object({ dashboards: z.array(QuillDashboard) })
  .passthrough()
const ListRolesViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    role_ids: z.array(RoleIdField).optional(),
    owner_id: IdentityIdField.uuid().optional(),
  })
  .passthrough()
const Role1 = z.object({ owner: Identity0, name: z.string(), id: RoleIdField.uuid() }).passthrough()
const ListRolesViewResponseData = z.object({ roles: z.array(Role1) }).passthrough()
const ListStaffersViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    identity_ids: z.array(IdentityIdField).optional(),
    limit: z.number().int().optional(),
    query: z.string().optional(),
  })
  .passthrough()
const ListStaffersViewResponseData = z.object({ identities: z.array(Identity0) }).passthrough()
const ListTaggingsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    target_ids: z.array(TargetIdField),
    calculated: z.boolean().optional(),
  })
  .passthrough()
const TaggingIdField = z.string()
const Tagging = z
  .object({
    id: TaggingIdField.uuid(),
    calculated: z.boolean().optional(),
    target: z.string().uuid(),
    tag: z.string().uuid(),
  })
  .passthrough()
const ListTaggingsViewResponseData = z.object({ taggings: z.array(Tagging) }).passthrough()
const ListTagsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    tag_ids: z.array(TagIdField).optional(),
    owner_ids: z.array(IdentityIdField).optional(),
  })
  .passthrough()
const ListTagsViewResponseData = z.object({ tags: z.array(Tag) }).passthrough()
const UserLoginViewRequestData = z
  .object({ email: z.string().email(), password: z.string(), mfa_code: z.string().optional() })
  .passthrough()
const UserLoginViewResponseData = z
  .object({ user_id: EndUserIdField.nullable(), require_mfa: z.boolean() })
  .passthrough()
const UserLogoutViewRequestData = z.object({ user_id: EndUserIdField.uuid() }).passthrough()
const RemoveMemberViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    member_identity_id: IdentityIdField.uuid(),
    member_role_id: RoleIdField.uuid(),
  })
  .passthrough()
const RemovePermissionViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    identity_permission_id: IdentityPermissionIdField.uuid(),
  })
  .passthrough()
const RemoveRoleViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), role_id: RoleIdField.uuid() })
  .passthrough()
const ResetPasswordViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), reset_code: z.string(), new_password: z.string() })
  .passthrough()
const ResetPasswordRequestViewRequestData = z.object({ email: z.string().email() }).passthrough()
const SearchAttributesViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), query: z.string() })
  .passthrough()
const SearchAttributesViewResponseData = z.object({ attributes: z.array(Attribute) }).passthrough()
const SearchTagsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    identity_id: IdentityIdField.uuid().optional(),
    query: z.string().optional(),
    limit: z.number().int().optional(),
  })
  .passthrough()
const SearchTagsViewResponseData = z.object({ tags: z.array(Tag) }).passthrough()
const SetAutoCcCaseEmailsViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), auto_cc_case_emails: z.boolean() })
  .passthrough()
const SetDigestFrequencyViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), digest_frequency: DigestFrequencyEnum })
  .passthrough()
const SetIdentityAutoPayoutViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    auto_payout: z.boolean(),
  })
  .passthrough()
const SetIdentityContactBookViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    contact_book: IdentityIdField.uuid(),
  })
  .passthrough()
const SetNotifyViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), notify: z.boolean() })
  .passthrough()
const SetPasswordViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), new_password: z.string() })
  .passthrough()
const SetPersonBirthdayRequestData = z
  .object({ user_id: EndUserIdField.uuid(), day: z.string() })
  .passthrough()
const SetPersonNameRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    title: z.string().optional(),
    first_name: z.string(),
    last_name: z.string(),
  })
  .passthrough()
const SetSkinViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), skin: SkinEnum })
  .passthrough()
const TagViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    target_id: TargetIdField.uuid(),
    tag_id: TagIdField.uuid(),
  })
  .passthrough()
const UnattributeViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), attribution_id: AttributionIdField.uuid() })
  .passthrough()
const UntagViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    target_id: TargetIdField.uuid(),
    tag_id: TagIdField.uuid(),
  })
  .passthrough()
const UpdateNoteViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), note_id: NoteIdField.uuid(), note: z.string() })
  .passthrough()
const AssetTypeEnum = z.enum([
  'Logo',
  'Banner',
  'EmailAttachment',
  'EmailContent',
  'CaseFile',
  'Bill',
  'FormLetterAttachment',
  'LogFile',
  'AiDocument',
  'DataExport',
  'GISLayer',
  'Misc',
])
const UploadAssetViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    owner_id: IdentityIdField.uuid(),
    file: z.string().url(),
    is_secure: z.boolean().optional().default(true),
    asset_type: AssetTypeEnum.optional(),
    case_handle: z.string().nullish(),
    case_id: z.string().nullish(),
  })
  .passthrough()
const UploadAssetViewResponseData = z
  .object({ key: z.string(), url: z.string(), is_secure: z.boolean(), accessible_url: z.string() })
  .passthrough()
const LocalDBCheckViewResponseData = z.object({ using_local_db: z.boolean() }).passthrough()
const AddChargeViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    bill_id: BillIdField.uuid(),
    name: z.string(),
    description: z.string(),
    amount: z.number().int(),
  })
  .passthrough()
const ChargeIdField = z.string()
const AddChargeViewResponseData = z.object({ charge_id: ChargeIdField.uuid() }).passthrough()
const MethodF1dEnum = z.enum([
  'BankPayment',
  'CardPayment',
  'CashPayment',
  'CheckPayment',
  'MoneyOrderPayment',
  'OtherPayment',
])
const AddPaymentViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    bill_id: BillIdField.uuid(),
    amount: z.number().int().optional(),
    method: MethodF1dEnum,
    payer_id: EndUserIdField.uuid().optional(),
  })
  .passthrough()
const StripeSourceIdField = z.string()
const ConfirmPaymentViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    bill_id: BillIdField.uuid(),
    source_id: StripeSourceIdField.uuid(),
    manual: z.number().int().optional(),
    is_installment: z.boolean(),
    is_bank: z.boolean(),
    is_phone: z.boolean(),
  })
  .passthrough()
const PaymentIdField = z.string()
const ConfirmPaymentViewResponseData = z.object({ payment_id: PaymentIdField.uuid() }).passthrough()
const ConnectStripeViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), organization_identity_id: IdentityIdField.uuid() })
  .passthrough()
const ConnectStripeViewResponseData = z.object({ url: z.string().url() }).passthrough()
const MethodsEnum = z.enum([
  'BankPayment',
  'CardPayment',
  'CashPayment',
  'CheckPayment',
  'MoneyOrderPayment',
  'OtherPayment',
])
const StatusE08Enum = z.enum(['waiting', 'processing', 'succeeded'])
const PaymentListArgs = z
  .object({
    bill_ids: z.array(z.string().uuid()).nullable(),
    start: z.string().datetime({ offset: true }).nullable(),
    end: z.string().datetime({ offset: true }).nullable(),
    methods: z.array(MethodsEnum).nullable(),
    is_stripe: z.boolean().nullable(),
    payment_intent: z.string().nullable(),
    reconciled: z.boolean().nullable(),
    key: z.string().nullable(),
    offset: z.number().int().nullable(),
    limit: z.number().int().nullable(),
    complete: z.boolean().nullable(),
    managed_only: z.boolean(),
    payout_ids: z.array(z.string().uuid()).nullable(),
    process_ids: z.array(z.string().uuid()).nullable(),
    lookup: z.array(z.string().uuid()).nullable(),
    bill_receiver: IdentityIdField.nullable(),
    attempted: z.boolean(),
    no_payout: z.boolean(),
    status: z.array(StatusE08Enum).nullable(),
    payment: PaymentIdField.nullable(),
    payment_code: z.string().nullable(),
    bill_code: z.string().nullable(),
  })
  .partial()
  .passthrough()
const CountPaymentsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    payment_id: PaymentIdField.uuid().optional(),
    step_id: StepIdField.uuid().optional(),
    payment_list_args: PaymentListArgs,
  })
  .passthrough()
const CountPaymentsViewResponseData = z.object({ count: z.number().int() }).passthrough()
const CreateLookupViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    name: z.string(),
    key_name: z.string(),
    key_description: z.string(),
    process_name: z.string(),
    process_description: z.string(),
    bill_step_name: z.string(),
    bill_step_description: z.string(),
    allow_ach: z.boolean(),
  })
  .passthrough()
const LookupIdField = z.string()
const CreateLookupViewResponseData = z.object({ lookup_id: LookupIdField.uuid() }).passthrough()
const CreateLookupEntryViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    lookup_id: LookupIdField.uuid(),
    key: z.string(),
    bill_id: BillIdField.uuid(),
  })
  .passthrough()
const Charge = z
  .object({
    id: ChargeIdField.uuid(),
    bill: BillIdField.uuid(),
    code: z.string().regex(/^[a-z0-9-]+$/),
    name: z.string(),
    description: z.string(),
    amount: z.number().int().gte(-9223372036854776000).lte(9223372036854776000),
    created: z.string().datetime({ offset: true }).optional(),
  })
  .passthrough()
const PayoutIdField = z.string()
const Payment0 = z
  .object({
    id: PaymentIdField.uuid(),
    code: z.string(),
    bill_id: BillIdField.uuid(),
    method: MethodF1dEnum,
    amount: z.number().int(),
    complete: z.boolean(),
    reconciled: z.boolean(),
    created: z.string().datetime({ offset: true }),
    completed: z.string().datetime({ offset: true }).nullable(),
    fee: z.number().int(),
    revenue_object_name: z.string(),
    revenue_object_code: z.string().nullable(),
    revenue_object_description: z.string(),
    bill_name: z.string(),
    bill_code: z.string(),
    bill_account: z.string().nullable(),
    bill_distribution: z.string().nullable(),
    is_stripe: z.boolean(),
    key: z.string().nullable(),
    payout: PayoutIdField.nullable(),
    status: StatusE08Enum,
    attempted: z.string().datetime({ offset: true }).nullable(),
    case_handle: z.string(),
    velosimo_integrated: z.boolean(),
    payer: EndUserIdField.nullable(),
    receiver: IdentityIdField.nullable(),
  })
  .passthrough()
const Bill2 = z
  .object({
    charges: z.array(Charge),
    payments: z.array(Payment0),
    payer: Identity0.nullable(),
    receiver: Identity0,
    process: Process0,
    description: z.string(),
    id: BillIdField.uuid(),
    handle: z.string(),
    created: z.string().datetime({ offset: true }),
    due: z.string().datetime({ offset: true }).nullable(),
    complete: z.boolean(),
    completion_time: z.string().datetime({ offset: true }).nullable(),
    editable: z.boolean(),
    installments: z.number().int(),
    name: z.string(),
    is_stripe: z.boolean(),
    step_id: StepIdField.uuid(),
    bill_step_id: BillStepIdField.uuid(),
    allow_ach: z.boolean(),
    case_id: CaseIdField.uuid(),
    order: z.number().int().nullable(),
    key: z.string().nullable(),
    action: ActionIdField.uuid(),
    item: ItemIdField.uuid(),
  })
  .passthrough()
const LookupEntryIdField = z.string()
const LookupEntry2 = z
  .object({ payment: Bill2, id: LookupEntryIdField.uuid(), key: z.string() })
  .passthrough()
const CreateLookupEntryViewResponseData = z.object({ lookup_entry: LookupEntry2 }).passthrough()
const CreatePayoutViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    payment_ids: z.array(PaymentIdField),
    descriptor: z.string(),
  })
  .passthrough()
const CreatePayoutViewResponseData = z.object({ payout_id: PayoutIdField.uuid() }).passthrough()
const DeleteChargeViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), charge_id: ChargeIdField.uuid() })
  .passthrough()
const EditBillViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    bill_id: BillIdField.uuid(),
    name: z.string().optional(),
    description: z.string().optional(),
  })
  .passthrough()
const FTPAuthViewRequestData = z
  .object({ username: z.string(), password: z.string(), token: z.string() })
  .passthrough()
const FTPAuthViewResponseData = z
  .object({ Role: z.string(), Policy: z.unknown(), HomeDirectory: z.string() })
  .passthrough()
const PaymentFilter = z
  .object({
    reconciled: z.boolean().nullable(),
    status: StatusE08Enum.nullable(),
    manual: z.boolean().nullable(),
  })
  .partial()
  .passthrough()
const BillFilter = z
  .object({
    ids: z.array(z.string().uuid()).nullable(),
    item_ids: z.array(z.string().uuid()).nullable(),
    case_ids: z.array(z.string().uuid()).nullable(),
    process_id: ProcessIdField.nullable(),
    step_id: StepIdField.nullable(),
    receiver_ids: z.array(z.string().uuid()).nullable(),
    name: z.string().nullable(),
    description: z.string().nullable(),
    complete: z.boolean().nullable(),
    completed_after: z.string().datetime({ offset: true }).nullable(),
    completed_before: z.string().datetime({ offset: true }).nullable(),
    payment_filter: PaymentFilter.nullable(),
    is_bill_member: z.boolean(),
  })
  .partial()
  .passthrough()
const GetBillCountViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), bill_filter: BillFilter })
  .passthrough()
const GetBillCountViewResponseData = z.object({ count: z.number().int() }).passthrough()
const LookupKeyTypeEnum = z.enum(['id', 'code', 'handle', 'process'])
const LookupKey = z
  .object({
    type: LookupKeyTypeEnum,
    lookup_id: LookupIdField.nullish(),
    receiver_identity_id: IdentityIdField.nullish(),
    code: z.string().nullish(),
    handle: z.string().nullish(),
    process_id: ProcessIdField.nullish(),
  })
  .passthrough()
const GetLookupViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), lookup_key: LookupKey })
  .passthrough()
const Lookup0 = z
  .object({
    id: LookupIdField.uuid(),
    code: z.string(),
    name: z.string(),
    description: z.string(),
    process_id: ProcessIdField.uuid(),
    step_id: StepIdField.uuid(),
    receiver_identity_id: IdentityIdField.uuid(),
  })
  .passthrough()
const GetLookupViewResponseData = z.object({ lookup: Lookup0 }).passthrough()
const GetPaymentInfoViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    bill_code: z.string(),
    destination: IdentityIdField.uuid(),
  })
  .passthrough()
const GetPaymentInfoViewResponseData = z.object({ bill: Bill2 }).passthrough()
const GetPaymentTotalViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), bill_filter: BillFilter })
  .passthrough()
const GetPaymentTotalViewResponseData = z.object({ total: z.number().int() }).passthrough()
const GranularityEnum = z.enum(['hour', 'day', 'week', 'month'])
const GetPaymentsStatsViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), granularity: GranularityEnum, bill_filter: BillFilter })
  .passthrough()
const PaymentInterval = z
  .object({ time: z.string().datetime({ offset: true }), total: z.number().int() })
  .passthrough()
const GetPaymentsStatsViewResponseData = z.object({ data: z.array(PaymentInterval) }).passthrough()
const GetRevenueBreakdownViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), active: z.boolean() })
  .passthrough()
const RevenueStat = z
  .object({ process_id: ProcessIdField.nullable(), name: z.string(), amount: z.number().int() })
  .passthrough()
const GetRevenueBreakdownViewResponseData = z.object({ data: z.array(RevenueStat) }).passthrough()
const GetRevenueTypesBreakdownViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), step_id: StepIdField.uuid().optional() })
  .passthrough()
const RevenueTypeBreakdown = z
  .object({ method: MethodF1dEnum, total: z.number().int() })
  .passthrough()
const GetRevenueTypesBreakdownViewResponseData = z
  .object({ data: z.array(RevenueTypeBreakdown) })
  .passthrough()
const FormatEnum = z.enum(['nemrc', 'southern-auto', 'southern-manual', 'stormwater'])
const ImportBillsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    lookup_id: LookupIdField.uuid(),
    format: FormatEnum,
    url: z.string(),
  })
  .passthrough()
const ListBillsViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), bill_filter: BillFilter })
  .passthrough()
const ListBillsViewResponseData = z.object({ bills: z.array(Bill2) }).passthrough()
const ListChargeSubstepsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    charge_substep_ids: z.array(ChargeSubstepIdField).optional(),
    expression_ids: z.array(ExpressionIdField).optional(),
  })
  .passthrough()
const ListChargeSubstepsViewResponseData = z
  .object({ charge_substeps: z.array(ChargeSubstep) })
  .passthrough()
const ListLookupEntriesViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    lookup_key: LookupKey,
    limit: z.number().int().optional(),
    offset: z.number().int().optional(),
    bill_filter: BillFilter,
    similar_key: z.string().nullish(),
    digits_key: z.string().optional(),
    exact_key: z.string().optional(),
  })
  .passthrough()
const ListLookupEntriesViewResponseData = z
  .object({ lookup_entries: z.array(LookupEntry2) })
  .passthrough()
const ListLookupsViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), active: z.boolean() })
  .passthrough()
const LookupStats = z
  .object({ total: z.number().int(), paid: z.number().int(), receipts: z.number().int() })
  .passthrough()
const Lookup2 = z
  .object({
    stats: LookupStats,
    id: LookupIdField.uuid(),
    code: z.string(),
    name: z.string(),
    description: z.string(),
    process_id: ProcessIdField.uuid(),
    step_id: StepIdField.uuid(),
    owner: z.string(),
  })
  .passthrough()
const ListLookupsViewResponseData = z.object({ lookups: z.array(Lookup2) }).passthrough()
const ListPaymentExportRulesViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), organization_identity_id: IdentityIdField.uuid() })
  .passthrough()
const PaymentExportRuleIdField = z.string()
const PaymentExportRuleMethodEnum = z.enum([
  'BankPayment',
  'CardPayment',
  'CashPayment',
  'CheckPayment',
  'MoneyOrderPayment',
  'OtherPayment',
])
const PaymentExportRule = z
  .object({
    id: PaymentExportRuleIdField.uuid(),
    organization: OrganizationIdField.uuid(),
    method: PaymentExportRuleMethodEnum,
    is_stripe: z.boolean(),
    code: z.string(),
  })
  .passthrough()
const ListPaymentExportRulesViewResponseData = z
  .object({ payment_export_rules: z.array(PaymentExportRule) })
  .passthrough()
const ListPaymentsViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), payment_list_args: PaymentListArgs })
  .passthrough()
const Contact0 = z
  .object({
    email: z.string(),
    identity_id: IdentityIdField.uuid(),
    name: z.string(),
    image: z.string().nullable(),
    address: Address0.nullable(),
  })
  .passthrough()
const Payment1 = z
  .object({
    payer: Contact0.nullish(),
    id: PaymentIdField.uuid(),
    code: z.string(),
    bill_id: BillIdField.uuid(),
    method: MethodF1dEnum,
    amount: z.number().int(),
    complete: z.boolean(),
    reconciled: z.boolean(),
    created: z.string().datetime({ offset: true }),
    completed: z.string().datetime({ offset: true }).nullable(),
    fee: z.number().int(),
    revenue_object_name: z.string(),
    revenue_object_code: z.string().nullable(),
    revenue_object_description: z.string(),
    bill_name: z.string(),
    bill_code: z.string(),
    bill_account: z.string().nullable(),
    bill_distribution: z.string().nullable(),
    is_stripe: z.boolean(),
    key: z.string().nullable(),
    payout: PayoutIdField.nullable(),
    status: StatusE08Enum,
    attempted: z.string().datetime({ offset: true }).nullable(),
    case_handle: z.string(),
    velosimo_integrated: z.boolean(),
    receiver: Identity1.nullable(),
  })
  .passthrough()
const ListPaymentsViewResponseData = z.object({ data: z.array(Payment1) }).passthrough()
const ListPayoutsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    limit: z.number().int().gte(1).optional(),
    offset: z.number().int().gte(0).optional(),
  })
  .passthrough()
const Payout2 = z
  .object({
    payments: z.array(Payment1),
    id: PayoutIdField.uuid(),
    descriptor: z.string(),
    stripe_id: z.string().nullable(),
    time: z.string().datetime({ offset: true }),
  })
  .passthrough()
const ListPayoutsViewResponseData = z
  .object({ payouts: z.array(Payout2), total_payouts: z.number().int() })
  .passthrough()
const ListStripeSourcesViewRequestDataTypeEnum = z.enum(['StripeSourceBank', 'StripeSourceCard'])
const ListStripeSourcesViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    type: ListStripeSourcesViewRequestDataTypeEnum.optional(),
  })
  .passthrough()
const StripeSourceTypeEnum = z.enum(['StripeSourceBank', 'StripeSourceCard'])
const StripeSource = z
  .object({
    id: StripeSourceIdField.uuid(),
    owner: IdentityIdField.uuid(),
    type: StripeSourceTypeEnum,
    stripe_id: z.string(),
    provider: z.string(),
    last4: z.string(),
    verified: z.boolean(),
  })
  .passthrough()
const ListStripeSourcesViewResponseData = z.object({ sources: z.array(StripeSource) }).passthrough()
const ObtainPaymentIntentViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    bill_id: BillIdField.uuid(),
    manual: z.number().int().optional(),
    is_installment: z.boolean(),
    is_phone: z.boolean(),
    is_bank: z.boolean(),
    save: z.boolean(),
    payer_id: EndUserIdField.uuid().optional(),
  })
  .passthrough()
const Intent = z
  .object({ destination: z.string(), secret: z.string(), intent_id: z.string() })
  .passthrough()
const ObtainPaymentIntentViewResponseData = z.object({ intent: Intent }).passthrough()
const ReconcileViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    payment_ids: z.array(PaymentIdField),
    reconcile: z.boolean(),
  })
  .passthrough()
const SearchBillsViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    start: z.string().datetime({ offset: true }).optional(),
    end: z.string().datetime({ offset: true }).optional(),
    lookup_entry_key: z.string().optional(),
    bill_name: z.string().optional(),
    bill_description: z.string().optional(),
    payment_type: ProcessIdField.uuid().optional(),
    active: z.boolean(),
    has_balance: z.boolean(),
  })
  .passthrough()
const SearchBillsViewResponseData = z.object({ bills: z.array(Bill2) }).passthrough()
const SearchLookupEntriesViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), lookup_id: LookupIdField.uuid(), query: z.string() })
  .passthrough()
const SearchLookupEntriesViewResponseData = z
  .object({ lookup_entry: LookupEntry2.nullable() })
  .partial()
  .passthrough()
const SendBillRequestViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    bill_id: BillIdField.uuid(),
    email: z.string(),
    url: z.string().optional(),
  })
  .passthrough()
const UpsertPaymentExportRuleViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_identity_id: IdentityIdField.uuid(),
    method: MethodF1dEnum,
    is_stripe: z.boolean(),
    code: z.string(),
  })
  .passthrough()
const VerifyStripeSourceViewRequestData = z
  .object({ user_id: EndUserIdField.uuid(), stripe_source_id: StripeSourceIdField.uuid() })
  .passthrough()

export const schemas = {
  EndUserIdField,
  IdentityIdField,
  TypeBb3Enum,
  OwnerTypeEnum,
  AddAIPhoneLineViewRequestData,
  EmptyResponseSerializerEmptyResponse,
  ProcessIdField,
  AddAIProcessSourceViewRequestData,
  AddFAQSourceViewRequestData,
  AddGISLayerSourceViewRequestData,
  DifficultyEnum,
  AddQuestionViewRequestData,
  AITestQuestionIdField,
  AddQuestionViewResponseData,
  AddTransferLineSourceViewRequestData,
  ConversationInitiationViewRequestData,
  ELDynamicVariables,
  ELPromptConfig,
  ELVoiceConfig,
  ELAgentConfig,
  ELConversationConfigOverride,
  ConversationInitiationViewResponseData,
  OrganizationIdField,
  CrawlDomainViewRequestData,
  CrawlDomainsViewRequestData,
  CrawlTaskIdField,
  CrawlTaskLogsViewRequestData,
  ActionEnum,
  ScrapeStatusEnum,
  QuerySourcesLog,
  CrawlTaskLogsViewResponseData,
  InteractionTypeB42Enum,
  CreateChatViewRequestData,
  ChatIdField,
  CreateChatViewResponseData,
  AIPhoneLineIdField,
  DeleteAIPhoneLineViewRequestData,
  DeleteCrawlTaskViewRequestData,
  FAQSourceIdField,
  DeleteFAQSourceViewRequestData,
  GISLayerSourceIdField,
  DeleteGISLayerSourceViewRequestData,
  TransferLineSourceIdField,
  DeleteTransferLineSourceViewRequestData,
  InteractionTypeA02Enum,
  ChatFilter,
  DownloadChatsViewRequestData,
  DownloadChatsViewResponseData,
  DownloadQuerySourcesLogsViewRequestData,
  DownloadQuerySourcesLogsViewResponseData,
  EditAIPhoneLineViewRequestData,
  AIProcessSourceIdField,
  EditAIProcessSourceViewRequestData,
  EditFAQSourceViewRequestData,
  EditGISLayerSourceViewRequestData,
  EditOrganizationAiSettingsViewRequestData,
  Organization,
  EditOrganizationAiSettingsViewResponseData,
  PageIdField,
  EditPageViewRequestData,
  EditQuestionViewRequestData,
  EditTransferLineSourceViewRequestData,
  FindProcessViewRequestData,
  ELProcessInfo,
  FindProcessViewResponseData,
  FindTransferLineViewRequestData,
  ELTransferLineInfo,
  FindTransferLineViewResponseData,
  GeoPoint,
  GenerateResponseViewRequestData,
  QueryTypeEnum,
  QueryAnalysis,
  ResponseIdField,
  ResponseTypeEnum,
  ChatDisplaySource,
  GenerateResponseViewResponseData,
  GenerateResponseStreamViewRequestData,
  GenerateResponseStreamViewResponseData,
  GetAIInteractionLogViewRequestData,
  Query,
  Type01dEnum,
  Response,
  SourceIdField,
  Page,
  Chunk,
  Response1,
  VoiceAICallInfo,
  AIInteractionLog,
  GetAIInteractionLogViewResponseData,
  GetAIProcessSourcesCountViewRequestData,
  GetAIProcessSourcesCountViewResponseData,
  GetChatCountViewRequestData,
  GetChatCountViewResponseData,
  GetFAQSourcesCountViewRequestData,
  GetFAQSourcesCountViewResponseData,
  GetGISLayerSourceViewRequestData,
  GISLayerSource,
  GetGISLayerSourceViewResponseData,
  GetGISLayerSourcesCountViewRequestData,
  GetGISLayerSourcesCountViewResponseData,
  GetOrganizationAiSettingsViewRequestData,
  OrganizationAISettings0,
  GetOrganizationAiSettingsViewResponseData,
  GetPageCountViewRequestData,
  GetPageCountViewResponseData,
  GetTransferLineSourcesCountViewRequestData,
  GetTransferLineSourcesCountViewResponseData,
  ImportGISPolygonsViewRequestData,
  ImportGISPolygonsViewResponseData,
  ListAIInteractionSummariesViewRequestData,
  LastUserMessage,
  AIInteractionSummary,
  ListAIInteractionSummariesViewResponseData,
  ListAIPhoneLinesViewRequestData,
  AIPhoneLine,
  ListAIPhoneLinesViewResponseData,
  ListAIProcessSourcesViewRequestData,
  AIProcessSource,
  ListAIProcessSourcesViewResponseData,
  ByFieldEnum,
  DirectionEnum,
  CrawlTaskSort,
  CrawlTasksListArgs,
  ListCrawlTasksViewRequestData,
  CrawlTask,
  ListCrawlTasksViewResponseData,
  ListFAQSourcesViewRequestData,
  FAQSource,
  ListFAQSourcesViewResponseData,
  ListGISLayerSourcesViewRequestData,
  ListGISLayerSourcesViewResponseData,
  ListPagesViewRequestData,
  ListPagesViewResponseData,
  ListSourcesViewRequestData,
  ListSourcesViewResponseData,
  ListSourcesCountViewRequestData,
  ListSourcesCountViewResponseData,
  ListSuggestionsViewRequestData,
  SuggestionIdField,
  Suggestion,
  ListSuggestionsViewResponseData,
  ListQuestionsViewRequestData,
  AITestQuestion,
  ListQuestionsViewResponseData,
  ListTransferLineSourcesViewRequestData,
  TransferLineSource,
  ListTransferLineSourcesViewResponseData,
  LoadQuestionsFromTemplateViewRequestData,
  LoadQuestionsFromTemplateViewResponseData,
  ELToolCall,
  ELToolResult,
  ELConversationTurn,
  ELTranscriptData,
  LogConversationViewRequestData,
  RemoveGISPolygonsViewRequestData,
  RemoveQuestionViewRequestData,
  RetrieveSourcesViewRequestData,
  ELChunkInfo,
  RetrieveSourcesViewResponseData,
  ScrapePagesViewRequestData,
  SetImpressionViewRequestData,
  SetSuggestionsViewRequestData,
  SetSuggestionsViewResponseData,
  TranslateTextViewRequestData,
  TranslateTextViewResponseData,
  UpdateCrawlTaskViewRequestData,
  UploadDocumentsViewRequestData,
  UploadDocumentsViewResponseData,
  UpsertPageViewRequestData,
  UpsertPageViewResponseData,
  CaseIdField,
  ItemIdField,
  AddCaseItemViewRequestData,
  AddCaseMemberViewRequestData,
  LogIdField,
  AddCityworksCommentViewRequestData,
  FormIdField,
  InputIdField,
  CommentThreadIdField,
  AddCommentViewRequestData,
  CommentIdField,
  Comment,
  AddCommentViewResponseData,
  AttachProcessViewRequestData,
  NotificationIdField,
  BumpNotificationViewRequestData,
  CopyProcessViewRequestData,
  CopyProcessViewResponseData,
  SectionStepIdField,
  FormStepIdField,
  CopySectionSubStepViewRequestData,
  InputStepIdField,
  ValueTypeEnum,
  DatetimeStep,
  IdentityStep,
  InputStepSub0,
  BoolValueIdField,
  ValueIdField,
  BoolValue0,
  DatetimeValueIdField,
  DatetimeValue0,
  FileValueIdField,
  FileValue0,
  IdentityValueIdField,
  IdentityValue0,
  LocationValueIdField,
  LocationIdField,
  AddressIdField,
  LocationValue0,
  NumberValueIdField,
  NumberValue0,
  TextValueIdField,
  TextValue0,
  Value0,
  InputStepChoice1,
  StyleEnum,
  InputSubstep2,
  CopySectionSubStepViewResponseData,
  StepCreate,
  CreateBillStepViewRequestData,
  BillStepIdField,
  StepIdField,
  CreateBillStepViewResponseData,
  StatusesEnum,
  CreateCaseViewRequestData,
  CreateCaseViewResponseData,
  CreateCasesTabViewRequestData,
  CasesTabIdField,
  CreateCasesTabViewResponseData,
  CreateCityworksSRViewRequestData,
  CreateDefaultCasesTabsViewRequestData,
  CreateExpressionViewRequestDataTypeEnum,
  CreateExpressionViewRequestDataOperatorEnum,
  ExpressionIdField,
  CreateExpressionViewRequestData,
  ExpressionOperatorEnum,
  Expression,
  CreateExpressionViewResponseData,
  CreateFormStepViewRequestData,
  CreateFormStepViewResponseData,
  CreateFormStyleElementViewRequestDataTypeEnum,
  CreateFormStyleElementViewRequestData,
  FormStyleElementIdField,
  FormStyleElementTypeEnum,
  FormStyleElement,
  CreateFormStyleElementViewResponseData,
  ValueCreate,
  InputCreate,
  CreateInputViewRequestData,
  CreateInputViewResponseData,
  CreateInputStepViewRequestData,
  CreateInputStepViewResponseData,
  MediumEnum,
  Attachment,
  CreateMessageViewRequestData,
  MessageIdField,
  CreateMessageViewResponseData,
  Status2efEnum,
  CreateProcessViewRequestData,
  CreateProcessViewResponseData,
  CreateRelationViewRequestData,
  CreateSectionStepViewRequestData,
  CreateSectionStepViewResponseData,
  CreateTaskViewRequestData,
  TaskIdField,
  CreateTaskViewResponseData,
  CreateTestCaseViewRequestData,
  CreateTestCaseViewResponseData,
  CreateValueViewRequestData,
  CreateValueViewResponseData,
  DeleteAssignmentViewRequestData,
  DeleteBillStepViewRequestData,
  DeleteCaseViewRequestData,
  DeleteCasesTabViewRequestData,
  DeleteCommentViewRequestData,
  DeleteDraftsViewRequestData,
  DeleteExpressionsViewRequestData,
  DeleteFormStepViewRequestData,
  DeleteFormStyleElementViewRequestData,
  DeleteInputSubstepViewRequestData,
  SectionIdField,
  DeleteSectionViewRequestData,
  DeleteSectionSubstepViewRequestData,
  DeleteStepAssignmentViewRequestData,
  MessageTemplateIdField,
  DeleteTemplateViewRequestData,
  ProcessStatusesEnum,
  NumberComparisonOperatorEnum,
  NumberComparison,
  CaseFilter,
  DownloadCasesViewRequestData,
  DownloadCasesViewResponseData,
  ChargeSubStepCreate,
  EditBillStepViewRequestData,
  ChargeSubstepIdField,
  ChargeSubstep,
  TagIdField,
  CalculatedTag,
  BillStep1,
  EditBillStepViewResponseData,
  EditCaseViewRequestData,
  EditCasesTabViewRequestData,
  DatetimeStepIdField,
  EditDateTimeStepViewRequestData,
  EditDueDateViewRequestData,
  EditExpressionNameViewRequestData,
  EditFormStepViewRequestData,
  EditFormStyleElementViewRequestData,
  EditFormStyleElementViewResponseData,
  IdentityStepIdField,
  EditIdentityStepViewRequestData,
  EditInputStepViewRequestData,
  EditProcessViewRequestData,
  EditSectionSubStepViewRequestData,
  EditStepViewRequestData,
  EditTaskViewRequestData,
  DocumentStepIdField,
  GenerateDocumentViewRequestData,
  GenerateDocumentViewResponseData,
  GetBillStepViewRequestData,
  GetBillStepViewResponseData,
  GetCaseViewRequestData,
  ContactIdField,
  Identity0,
  Address0,
  Location0,
  ContactEntryIdField,
  ContactEmailIdField,
  EmailAddressIdField,
  ContactEmail0,
  NoteIdField,
  TargetIdField,
  Note0,
  Identity2,
  Tag,
  Case2,
  GetCaseViewResponseData,
  GetCaseCountViewRequestData,
  GetCaseCountViewResponseData,
  GetCaseIdViewRequestData,
  GetCaseIdViewResponseData,
  GetCaseOwnerViewRequestData,
  GetCaseOwnerViewResponseData,
  GetDocumentStepsViewRequestData,
  DocumentTemplateIdField,
  DocumentTemplate,
  DocumentStep,
  GetDocumentStepsViewResponseData,
  GetDocumentTemplateFieldsViewRequestData,
  GetDocumentTemplateFieldsViewResponseData,
  GetFormStepCountViewRequestData,
  GetFormStepCountViewResponseData,
  FormStepTimeFilter,
  GetFormStepTimeViewRequestData,
  GetFormStepTimeViewResponseData,
  ProcessInfoArgs,
  GetProcessViewRequestData,
  FormStep0,
  SectionSubstep0,
  ApplyWithoutVerificationEnum,
  Process3,
  GetProcessViewResponseData,
  ProcessCaseListArgs,
  GetProcessCaseCountViewRequestData,
  GetProcessCaseCountViewResponseData,
  GetTaskViewRequestData,
  Task1,
  GetTaskViewResponseData,
  ImportEmailViewRequestData,
  ImportEmailViewResponseData,
  InstantiateStepsViewRequestData,
  ListAnnotatedDocumentsViewRequestData,
  AnnotatedDocumentIdField,
  AnnotatedDocument,
  ListAnnotatedDocumentsViewResponseData,
  ListCaseAutomationEventLogsViewRequestData,
  CaseAutomationEventLogIdField,
  EventTypeEnum,
  CaseAutomationEventLog,
  ListCaseAutomationEventLogsViewResponseData,
  ListCaseMembersViewRequestData,
  ListCaseMembersViewResponseData,
  CaseStatusChangeIdField,
  ListCaseStatusChangesViewRequestData,
  CaseStatusChange0,
  ListCaseStatusChangesViewResponseData,
  SortEnum,
  ListCasesViewRequestData,
  ListCasesViewResponseData,
  ListCasesTabsViewRequestData,
  CasesTab,
  CasesConfigTypeEnum,
  CasesConfig,
  CasesConfig1,
  CasesTab1,
  ListCasesTabsViewResponseData,
  ListCasesWithLocationViewRequestData,
  CaseWithLocation,
  ListCasesWithLocationViewResponseData,
  ListCommentsViewRequestData,
  CommentThread,
  ListCommentsViewResponseData,
  ListDraftsViewRequestData,
  Draft,
  ListDraftsViewResponseData,
  ListExpressionsViewRequestData,
  ListExpressionsViewResponseData,
  ListFormStepsViewRequestData,
  ListFormStepsViewResponseData,
  ListFormsViewRequestData,
  Form0,
  ListFormsViewResponseData,
  InputStepFilter,
  ListInputSubStepsViewRequestData,
  ListInputSubStepsViewResponseData,
  InputFilter,
  ListInputsViewRequestData,
  Input1,
  ListInputsViewResponseData,
  ListItemsViewRequestData,
  BillIdField,
  ActionIdField,
  Bill0,
  Message0,
  ListItemsViewResponseData,
  ListMessagesViewRequestData,
  HandleIdField,
  Case,
  Log1,
  EmailAddress0,
  EmailIdField,
  EmailParticipant0TypeEnum,
  EmailParticipant0,
  Email1,
  ListMessagesViewResponseData,
  ListNearbyCasesViewRequestData,
  ListNearbyCasesViewResponseData,
  NotificationFilter,
  ListNotificationsViewRequestData,
  Notification1,
  ListNotificationsViewResponseData,
  ListProcessCasesViewRequestData,
  ProcessCase3,
  ListProcessCasesViewResponseData,
  ListProcessSummariesViewRequestData,
  ProcessSummary,
  ListProcessSummariesViewResponseData,
  PhoneNumberIdField,
  ProcessFilter,
  ListProcessesViewRequestData,
  Process0,
  ListProcessesViewResponseData,
  ListProcessesStatsViewRequestData,
  Process1,
  ListProcessesStatsViewResponseData,
  ListRelationsViewRequestData,
  RelationIdField,
  Relation,
  ListRelationsViewResponseData,
  ListSectionSubStepsViewRequestData,
  ListSectionSubStepsViewResponseData,
  ListSectionsViewRequestData,
  Section,
  ListSectionsViewResponseData,
  ListStepAssignmentsViewRequestData,
  StepNotificationIdField,
  StepNotification,
  ListStepAssignmentsViewResponseData,
  ListTasksViewRequestData,
  ListTasksViewResponseData,
  ListTemplatesViewRequestData,
  MessageTemplate0,
  ListTemplatesViewResponseData,
  ListValuesViewRequestData,
  ListValuesViewResponseData,
  MarkCaseReadViewRequestData,
  NotifyArgs,
  NotifyViewRequestData,
  RemoveCaseMemberViewRequestData,
  ResolveThreadViewRequestData,
  Type6b9Enum,
  FormDataNode,
  FormNodeCreate,
  SaveOrSubmitFormViewRequestData,
  SearchCasesViewRequestData,
  SearchCasesViewResponseData,
  SearchCasesCountViewRequestData,
  SearchCasesCountViewResponseData,
  SearchProcessesViewRequestData,
  SearchProcessesViewResponseData,
  VelosimoEventEnum,
  SendVelosimoCaseViewRequestData,
  SetActionCompleteViewRequestData,
  SetActionInReviewViewRequestData,
  SetCaseStatusViewRequestData,
  StepAssignmentCreate,
  SetStepAssignmentsViewRequestData,
  SetStepAssignmentsViewResponseData,
  StartProcessViewRequestData,
  StartProcessViewResponseData,
  CasesConfigUpsert,
  UpsertCasesConfigViewRequestData,
  UpsertTemplateViewRequestData,
  UpsertTemplateViewResponseData,
  AddAddressViewRequestData,
  AddPhoneNumberViewRequestData,
  ContactFilter,
  CountContactsViewRequestData,
  CountContactsViewResponseData,
  CreateAddressViewRequestData,
  CreateAddressViewResponseData,
  AttributeIdField,
  AttributionCreate,
  CreateContactViewRequestData,
  CreateContactViewResponseData,
  CreateContactFromCaseViewRequestData,
  CreateContactFromCaseViewResponseData,
  CreateEmailAddressViewRequestData,
  CreateRelatedContactViewRequestData,
  DeleteContactViewRequestData,
  DeleteRelatedContactViewRequestData,
  DownloadContactsViewRequestData,
  DownloadContactsViewResponseData,
  DraftEmailViewRequestData,
  DraftEmailViewResponseData,
  VerifyAdmin,
  VerifyApply,
  OrganizationTypeEnum,
  Identity1,
  VerifyPayment,
  VerifyStandard,
  VerifyThread,
  VerifyOptionsTypeEnum,
  VerifyLogin,
  VerifyOptions,
  EmailSignupViewRequestData,
  EmailSignupViewResponseData,
  GetContactEntryViewRequestData,
  ContactEntry0,
  GetContactEntryViewResponseData,
  GetContactEntryExpandedViewRequestData,
  PhoneNumber0,
  Attribute,
  Attribution,
  ContactEntry2,
  Person,
  GetContactEntryExpandedViewResponseData,
  GetContactEntryIdViewRequestData,
  GetContactEntryIdViewResponseData,
  GetContactEntryIdentityIdsViewRequestData,
  GetContactEntryIdentityIdsViewResponseData,
  GetContactsHeaderViewRequestData,
  GetContactsHeaderViewResponseData,
  GetMyLocationViewRequestData,
  GetMyLocationViewResponseData,
  GetOrganizationLocationViewRequestData,
  GetOrganizationLocationViewResponseData,
  HandleInboundEmailViewRequestData,
  NameImportConfig,
  AddressImportConfig,
  ImportContactsViewRequestData,
  ImportContactsViewResponseData,
  ListAddressesViewRequestData,
  ListAddressesViewResponseData,
  ListContactEmailsViewRequestData,
  ListContactEmailsViewResponseData,
  ListContactEntriesViewRequestData,
  ListContactEntriesViewResponseData,
  ListContactEntriesAddressPointsViewRequestData,
  ListContactEntriesAddressPointsViewResponseData,
  ListContactIdentitiesViewRequestData,
  ListContactIdentitiesViewResponseData,
  ListContactOwnersViewRequestData,
  ListContactOwnersViewResponseData,
  ListContactRelationsViewRequestData,
  RelatedContactEntry2,
  ListContactRelationsViewResponseData,
  ListEmailAddressesViewRequestData,
  ListEmailAddressesViewResponseData,
  EmailFilter,
  ListEmailsViewRequestData,
  ListEmailsViewResponseData,
  ListPhoneNumbersViewRequestData,
  ListPhoneNumbersViewResponseData,
  ObtainEmailAddressViewRequestData,
  ObtainEmailAddressViewResponseData,
  QuickCreateAddressViewRequestData,
  QuickCreateAddressViewResponseData,
  RemoveAddressViewRequestData,
  RemoveEmailAddressViewRequestData,
  RemovePhoneNumberViewRequestData,
  ReverseSearchAddressesViewRequestData,
  ReverseSearchAddressesViewResponseData,
  SearchAddressesViewRequestData,
  SearchAddressesViewResponseData,
  SearchContactEntriesViewRequestData,
  SearchContactEntriesViewResponseData,
  SearchContactsViewRequestData,
  SearchContactsViewResponseData,
  SearchEmailsViewRequestData,
  EmailAddressResult,
  SearchEmailsViewResponseData,
  SendEmailViewRequestData,
  SendEmailViewResponseData,
  SendMassEmailViewRequestData,
  SendVerificationEmailViewRequestData,
  SendVerificationEmailViewResponseData,
  SetEmailSignatureViewRequestData,
  SetPrimaryEmailAddressViewRequestData,
  SetReplyToViewRequestData,
  VerifyEmailViewRequestData,
  VerifyLoginViewRequestData,
  VerifyLoginViewResponseData,
  RoleIdField,
  AddAdminViewRequestData,
  AddPermissionViewRequestData,
  AddRoleViewRequestData,
  AddRoleViewResponseData,
  BreakdownContactTagsViewRequestData,
  Tag0,
  BreakdownContactTagsViewResponseData,
  BreakdownTagsViewRequestData,
  BreakdownTagsViewResponseData,
  ChangePasswordViewRequestData,
  CreateAttributionViewRequestData,
  CreateDepartmentViewRequestData,
  CreateDepartmentViewResponseData,
  CreateNoteViewRequestData,
  CreateNoteViewResponseData,
  CreateTagViewRequestData,
  CreateTagViewResponseData,
  DeleteNoteViewRequestData,
  DeleteTagViewRequestData,
  EditContactBirthdayViewRequestData,
  EditContactImageViewRequestData,
  EditContactImageViewResponseData,
  EditContactNameViewRequestData,
  EditRoleViewRequestData,
  EditTagViewRequestData,
  GetAccountViewRequestData,
  DigestFrequencyEnum,
  GetAccountViewResponseData,
  GetIdentityViewRequestData,
  GetIdentityViewResponseData,
  GetIdentityBasicViewRequestData,
  GetIdentityBasicViewResponseData,
  GetNoteViewRequestData,
  GetNoteViewResponseData,
  GetUserViewRequestData,
  SkinEnum,
  GetUserViewResponseData,
  EmptyRequestSerializerEmptyRequest,
  GetUserFromSessionViewResponseData,
  GetUserIdsViewRequestData,
  UserIds,
  GetUserIdsViewResponseData,
  IdentityToUserViewRequestData,
  IdentityToUserViewResponseData,
  ListAttributionsViewRequestData,
  AttributionIdField,
  Attribution0,
  ListAttributionsViewResponseData,
  ListDistinctEmailIdentitiesViewRequestData,
  EmailIdentity,
  ListDistinctEmailIdentitiesViewResponseData,
  ListIdentitiesViewRequestData,
  ListIdentitiesViewResponseData,
  ListIdentitiesByIdsViewRequestData,
  ListIdentitiesByIdsViewResponseData,
  ListIdentityPermissionsViewRequestData,
  IdentityPermissionIdField,
  IdentityPermission,
  ListIdentityPermissionsViewResponseData,
  ListMembersViewRequestData,
  Member0,
  ListMembersViewResponseData,
  ListNotesViewRequestData,
  ListNotesViewResponseData,
  TypesEnum,
  ListOrganizationsViewRequestData,
  OrganizationQuerySettings,
  Organization0,
  ListOrganizationsViewResponseData,
  ListParticipantsViewRequestData,
  ListParticipantsViewResponseData,
  ListQuillDashboardsViewRequestData,
  QuillDashboardIdField,
  QuillDashboard,
  ListQuillDashboardsViewResponseData,
  ListRolesViewRequestData,
  Role1,
  ListRolesViewResponseData,
  ListStaffersViewRequestData,
  ListStaffersViewResponseData,
  ListTaggingsViewRequestData,
  TaggingIdField,
  Tagging,
  ListTaggingsViewResponseData,
  ListTagsViewRequestData,
  ListTagsViewResponseData,
  UserLoginViewRequestData,
  UserLoginViewResponseData,
  UserLogoutViewRequestData,
  RemoveMemberViewRequestData,
  RemovePermissionViewRequestData,
  RemoveRoleViewRequestData,
  ResetPasswordViewRequestData,
  ResetPasswordRequestViewRequestData,
  SearchAttributesViewRequestData,
  SearchAttributesViewResponseData,
  SearchTagsViewRequestData,
  SearchTagsViewResponseData,
  SetAutoCcCaseEmailsViewRequestData,
  SetDigestFrequencyViewRequestData,
  SetIdentityAutoPayoutViewRequestData,
  SetIdentityContactBookViewRequestData,
  SetNotifyViewRequestData,
  SetPasswordViewRequestData,
  SetPersonBirthdayRequestData,
  SetPersonNameRequestData,
  SetSkinViewRequestData,
  TagViewRequestData,
  UnattributeViewRequestData,
  UntagViewRequestData,
  UpdateNoteViewRequestData,
  AssetTypeEnum,
  UploadAssetViewRequestData,
  UploadAssetViewResponseData,
  LocalDBCheckViewResponseData,
  AddChargeViewRequestData,
  ChargeIdField,
  AddChargeViewResponseData,
  MethodF1dEnum,
  AddPaymentViewRequestData,
  StripeSourceIdField,
  ConfirmPaymentViewRequestData,
  PaymentIdField,
  ConfirmPaymentViewResponseData,
  ConnectStripeViewRequestData,
  ConnectStripeViewResponseData,
  MethodsEnum,
  StatusE08Enum,
  PaymentListArgs,
  CountPaymentsViewRequestData,
  CountPaymentsViewResponseData,
  CreateLookupViewRequestData,
  LookupIdField,
  CreateLookupViewResponseData,
  CreateLookupEntryViewRequestData,
  Charge,
  PayoutIdField,
  Payment0,
  Bill2,
  LookupEntryIdField,
  LookupEntry2,
  CreateLookupEntryViewResponseData,
  CreatePayoutViewRequestData,
  CreatePayoutViewResponseData,
  DeleteChargeViewRequestData,
  EditBillViewRequestData,
  FTPAuthViewRequestData,
  FTPAuthViewResponseData,
  PaymentFilter,
  BillFilter,
  GetBillCountViewRequestData,
  GetBillCountViewResponseData,
  LookupKeyTypeEnum,
  LookupKey,
  GetLookupViewRequestData,
  Lookup0,
  GetLookupViewResponseData,
  GetPaymentInfoViewRequestData,
  GetPaymentInfoViewResponseData,
  GetPaymentTotalViewRequestData,
  GetPaymentTotalViewResponseData,
  GranularityEnum,
  GetPaymentsStatsViewRequestData,
  PaymentInterval,
  GetPaymentsStatsViewResponseData,
  GetRevenueBreakdownViewRequestData,
  RevenueStat,
  GetRevenueBreakdownViewResponseData,
  GetRevenueTypesBreakdownViewRequestData,
  RevenueTypeBreakdown,
  GetRevenueTypesBreakdownViewResponseData,
  FormatEnum,
  ImportBillsViewRequestData,
  ListBillsViewRequestData,
  ListBillsViewResponseData,
  ListChargeSubstepsViewRequestData,
  ListChargeSubstepsViewResponseData,
  ListLookupEntriesViewRequestData,
  ListLookupEntriesViewResponseData,
  ListLookupsViewRequestData,
  LookupStats,
  Lookup2,
  ListLookupsViewResponseData,
  ListPaymentExportRulesViewRequestData,
  PaymentExportRuleIdField,
  PaymentExportRuleMethodEnum,
  PaymentExportRule,
  ListPaymentExportRulesViewResponseData,
  ListPaymentsViewRequestData,
  Contact0,
  Payment1,
  ListPaymentsViewResponseData,
  ListPayoutsViewRequestData,
  Payout2,
  ListPayoutsViewResponseData,
  ListStripeSourcesViewRequestDataTypeEnum,
  ListStripeSourcesViewRequestData,
  StripeSourceTypeEnum,
  StripeSource,
  ListStripeSourcesViewResponseData,
  ObtainPaymentIntentViewRequestData,
  Intent,
  ObtainPaymentIntentViewResponseData,
  ReconcileViewRequestData,
  SearchBillsViewRequestData,
  SearchBillsViewResponseData,
  SearchLookupEntriesViewRequestData,
  SearchLookupEntriesViewResponseData,
  SendBillRequestViewRequestData,
  UpsertPaymentExportRuleViewRequestData,
  VerifyStripeSourceViewRequestData,
}

const endpoints = makeApi([
  {
    method: 'post',
    path: '/ai/add-ai-phone-line/',
    alias: 'ai_addAIPhoneLine',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddAIPhoneLineViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/add-ai-process-source/',
    alias: 'ai_addAIProcessSource',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddAIProcessSourceViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/add-faq-source/',
    alias: 'ai_addFAQSource',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddFAQSourceViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/add-gis-layer-source/',
    alias: 'ai_addGISLayerSource',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddGISLayerSourceViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/add-test-question/',
    alias: 'ai_addQuestion',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddQuestionViewRequestData,
      },
    ],
    response: AddQuestionViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/add-transfer-line-source/',
    alias: 'ai_addTransferLineSource',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddTransferLineSourceViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/conversation-initiation/',
    alias: 'ai_conversationInitiation',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ConversationInitiationViewRequestData,
      },
    ],
    response: ConversationInitiationViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/crawl-domain/',
    alias: 'ai_crawlDomain',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CrawlDomainViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/crawl-domains/',
    alias: 'ai_crawlDomains',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CrawlDomainsViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/crawl-task-logs/',
    alias: 'ai_crawlTaskLogs',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CrawlTaskLogsViewRequestData,
      },
    ],
    response: CrawlTaskLogsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/create-chat/',
    alias: 'ai_createChat',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateChatViewRequestData,
      },
    ],
    response: CreateChatViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/delete-ai-phone-line/',
    alias: 'ai_deleteAIPhoneLine',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteAIPhoneLineViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/delete-crawl-task/',
    alias: 'ai_deleteCrawlTask',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteCrawlTaskViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/delete-faq-source/',
    alias: 'ai_deleteFAQSource',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteFAQSourceViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/delete-gis-layer-source/',
    alias: 'ai_deleteGISLayerSource',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteGISLayerSourceViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/delete-transfer-line-source/',
    alias: 'ai_deleteTransferLineSource',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteTransferLineSourceViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/download-chats/',
    alias: 'ai_downloadChats',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DownloadChatsViewRequestData,
      },
    ],
    response: z.object({ email_recipient: z.string().email() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/download-query-sources-logs/',
    alias: 'ai_downloadQuerySourcesLogs',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DownloadQuerySourcesLogsViewRequestData,
      },
    ],
    response: z.object({ email_recipient: z.string().email() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/edit-ai-phone-line/',
    alias: 'ai_editAIPhoneLine',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditAIPhoneLineViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/edit-ai-process-source/',
    alias: 'ai_editAIProcessSource',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditAIProcessSourceViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/edit-faq-source/',
    alias: 'ai_editFAQSource',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditFAQSourceViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/edit-gis-layer-source/',
    alias: 'ai_editGISLayerSource',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditGISLayerSourceViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/edit-organization-ai-settings/',
    alias: 'ai_editOrganizationAiSettings',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditOrganizationAiSettingsViewRequestData,
      },
    ],
    response: EditOrganizationAiSettingsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/edit-page/',
    alias: 'ai_editPage',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditPageViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/edit-test-question/',
    alias: 'ai_editQuestion',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditQuestionViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/edit-transfer-line-source/',
    alias: 'ai_editTransferLineSource',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditTransferLineSourceViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/find-process/',
    alias: 'ai_findProcess',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: FindProcessViewRequestData,
      },
    ],
    response: FindProcessViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/find-transfer-line/',
    alias: 'ai_findTransferLine',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: FindTransferLineViewRequestData,
      },
    ],
    response: FindTransferLineViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/generate-response-stream/',
    alias: 'ai_generateResponseStream',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GenerateResponseStreamViewRequestData,
      },
    ],
    response: GenerateResponseStreamViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/generate-response/',
    alias: 'ai_generateResponse',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GenerateResponseViewRequestData,
      },
    ],
    response: GenerateResponseViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/get-ai-interaction-log/',
    alias: 'ai_getAIInteractionLog',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetAIInteractionLogViewRequestData,
      },
    ],
    response: GetAIInteractionLogViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/get-ai-process-sources-count/',
    alias: 'ai_getAIProcessSourcesCount',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetAIProcessSourcesCountViewRequestData,
      },
    ],
    response: z.object({ count: z.number().int() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/get-chat-count/',
    alias: 'ai_getChatCount',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetChatCountViewRequestData,
      },
    ],
    response: z.object({ count: z.number().int() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/get-faq-sources-count/',
    alias: 'ai_getFAQSourcesCount',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetFAQSourcesCountViewRequestData,
      },
    ],
    response: z.object({ count: z.number().int() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/get-gis-layer-source/',
    alias: 'ai_getGISLayerSource',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetGISLayerSourceViewRequestData,
      },
    ],
    response: GetGISLayerSourceViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/get-gis-layer-sources-count/',
    alias: 'ai_getGISLayerSourcesCount',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetGISLayerSourcesCountViewRequestData,
      },
    ],
    response: z.object({ count: z.number().int() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/get-organization-ai-settings/',
    alias: 'ai_getOrganizationAiSettings',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetOrganizationAiSettingsViewRequestData,
      },
    ],
    response: GetOrganizationAiSettingsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/get-page-count/',
    alias: 'ai_getPageCount',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetPageCountViewRequestData,
      },
    ],
    response: z.object({ data: z.number().int() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/get-transfer-line-sources-count/',
    alias: 'ai_getTransferLineSourcesCount',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetTransferLineSourcesCountViewRequestData,
      },
    ],
    response: z.object({ count: z.number().int() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/hooks/backup-inbound-call-router/',
    alias: 'ai_hooks_backup_inbound_call_router_create',
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/ai/hooks/call-status-change/',
    alias: 'ai_hooks_call_status_change_create',
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/ai/hooks/inbound-call-router/',
    alias: 'ai_hooks_inbound_call_router_create',
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/ai/hooks/inbound-sms/',
    alias: 'ai_hooks_inbound_sms_create',
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/ai/hooks/voice-beta',
    alias: 'ai_hooks_voice_beta_create',
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/ai/hooks/voice-stable',
    alias: 'ai_hooks_voice_stable_create',
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/ai/hooks/voice-stable-action-or-play/:chat_id/:org_id/:current_language/:turn_key',
    alias: 'ai_hooks_voice_stable_action_or_play_create',
    requestFormat: 'json',
    parameters: [
      {
        name: 'chat_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'current_language',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'org_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'turn_key',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: 'post',
    path: '/ai/hooks/voice-stable-collect/:chat_id/:org_id/:current_language',
    alias: 'ai_hooks_voice_stable_collect_create',
    requestFormat: 'json',
    parameters: [
      {
        name: 'chat_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'current_language',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'org_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: 'post',
    path: '/ai/hooks/voice-stable-gather/:chat_id/:org_id/:current_language',
    alias: 'ai_hooks_voice_stable_gather_create',
    description: `Sets up the Gather for the next user input`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'chat_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'current_language',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'org_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: 'post',
    path: '/ai/hooks/voice-stable-no-input/:chat_id/:org_id/:current_language',
    alias: 'ai_hooks_voice_stable_no_input_create',
    requestFormat: 'json',
    parameters: [
      {
        name: 'chat_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'current_language',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'org_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: 'post',
    path: '/ai/import-gis-polygons/',
    alias: 'ai_importGISPolygons',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ImportGISPolygonsViewRequestData,
      },
    ],
    response: ImportGISPolygonsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-ai-interaction-summaries/',
    alias: 'ai_listAIInteractionSummaries',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListAIInteractionSummariesViewRequestData,
      },
    ],
    response: ListAIInteractionSummariesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-ai-phone-lines/',
    alias: 'ai_listAIPhoneLines',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListAIPhoneLinesViewRequestData,
      },
    ],
    response: ListAIPhoneLinesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-ai-process-sources/',
    alias: 'ai_listAIProcessSources',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListAIProcessSourcesViewRequestData,
      },
    ],
    response: ListAIProcessSourcesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-crawl-tasks/',
    alias: 'ai_listCrawlTasks',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListCrawlTasksViewRequestData,
      },
    ],
    response: ListCrawlTasksViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-faq-sources/',
    alias: 'ai_listFAQSources',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListFAQSourcesViewRequestData,
      },
    ],
    response: ListFAQSourcesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-gis-layer-sources/',
    alias: 'ai_listGISLayerSources',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListGISLayerSourcesViewRequestData,
      },
    ],
    response: ListGISLayerSourcesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-pages/',
    alias: 'ai_listPages',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListPagesViewRequestData,
      },
    ],
    response: ListPagesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-sources-count/',
    alias: 'ai_listSourcesCount',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListSourcesCountViewRequestData,
      },
    ],
    response: z.object({ count: z.number().int() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-sources/',
    alias: 'ai_listSources',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListSourcesViewRequestData,
      },
    ],
    response: ListSourcesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-suggestions/',
    alias: 'ai_listSuggestions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListSuggestionsViewRequestData,
      },
    ],
    response: ListSuggestionsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-test-questions/',
    alias: 'ai_listQuestions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListQuestionsViewRequestData,
      },
    ],
    response: ListQuestionsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-transfer-line-sources/',
    alias: 'ai_listTransferLineSources',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListTransferLineSourcesViewRequestData,
      },
    ],
    response: ListTransferLineSourcesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/load-test-questions-from-template/',
    alias: 'ai_loadQuestionsFromTemplate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: LoadQuestionsFromTemplateViewRequestData,
      },
    ],
    response: LoadQuestionsFromTemplateViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/log-conversation/',
    alias: 'ai_logConversation',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: LogConversationViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/remove-gis-polygons/',
    alias: 'ai_removeGISPolygons',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RemoveGISPolygonsViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/remove-test-question/',
    alias: 'ai_removeQuestion',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RemoveQuestionViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/retrieve-sources/',
    alias: 'ai_retrieveSources',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RetrieveSourcesViewRequestData,
      },
    ],
    response: RetrieveSourcesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/scrape-pages/',
    alias: 'ai_scrapePages',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ScrapePagesViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/send-process-sms/',
    alias: 'ai_send_process_sms_create',
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/ai/set-impression/',
    alias: 'ai_setImpression',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetImpressionViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/set-suggestions/',
    alias: 'ai_setSuggestions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetSuggestionsViewRequestData,
      },
    ],
    response: SetSuggestionsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/transfer-call/',
    alias: 'ai_transfer_call_create',
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/ai/translate-text/',
    alias: 'ai_translateText',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: TranslateTextViewRequestData,
      },
    ],
    response: z.object({ translated_text: z.string() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/update-crawl-task/',
    alias: 'ai_updateCrawlTask',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateCrawlTaskViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/upload-documents/',
    alias: 'ai_uploadDocuments',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UploadDocumentsViewRequestData,
      },
    ],
    response: UploadDocumentsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/upsert-page/',
    alias: 'ai_upsertPage',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpsertPageViewRequestData,
      },
    ],
    response: UpsertPageViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/users/login',
    alias: 'api_users_login_create',
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/cases/add-case-item/',
    alias: 'cases_addCaseItem',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddCaseItemViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/add-case-member/',
    alias: 'cases_addCaseMember',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddCaseMemberViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/add-cityworks-comment/',
    alias: 'cases_addCityworksComment',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddCityworksCommentViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/add-comment/',
    alias: 'cases_addComment',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddCommentViewRequestData,
      },
    ],
    response: AddCommentViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/attach-process/',
    alias: 'cases_attachProcess',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AttachProcessViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/bump-notification/',
    alias: 'cases_bumpNotification',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: BumpNotificationViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/copy-process/',
    alias: 'cases_copyProcess',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CopyProcessViewRequestData,
      },
    ],
    response: CopyProcessViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/copy-section-substep/',
    alias: 'cases_copySectionSubStep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CopySectionSubStepViewRequestData,
      },
    ],
    response: CopySectionSubStepViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/create-bill-step/',
    alias: 'cases_createBillStep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateBillStepViewRequestData,
      },
    ],
    response: CreateBillStepViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/create-case/',
    alias: 'cases_createCase',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateCaseViewRequestData,
      },
    ],
    response: CreateCaseViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/create-cases-tab/',
    alias: 'cases_createCasesTab',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateCasesTabViewRequestData,
      },
    ],
    response: CreateCasesTabViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/create-cityworks-sr/',
    alias: 'cases_createCityworksSR',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateCityworksSRViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/create-default-cases-tabs/',
    alias: 'cases_createDefaultCasesTabs',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateDefaultCasesTabsViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/create-expression/',
    alias: 'cases_createExpression',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateExpressionViewRequestData,
      },
    ],
    response: CreateExpressionViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/create-form-step/',
    alias: 'cases_createFormStep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateFormStepViewRequestData,
      },
    ],
    response: CreateFormStepViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/create-form-style-element',
    alias: 'cases_createFormStyleElement',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateFormStyleElementViewRequestData,
      },
    ],
    response: CreateFormStyleElementViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/create-input-step/',
    alias: 'cases_createInputStep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateInputStepViewRequestData,
      },
    ],
    response: CreateInputStepViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/create-input/',
    alias: 'cases_createInput',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateInputViewRequestData,
      },
    ],
    response: CreateInputViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/create-message/',
    alias: 'cases_createMessage',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateMessageViewRequestData,
      },
    ],
    response: CreateMessageViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/create-process/',
    alias: 'cases_createProcess',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateProcessViewRequestData,
      },
    ],
    response: CreateProcessViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/create-relation/',
    alias: 'cases_createRelation',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateRelationViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/create-section-step/',
    alias: 'cases_createSectionStep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateSectionStepViewRequestData,
      },
    ],
    response: CreateSectionStepViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/create-task/',
    alias: 'cases_createTask',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateTaskViewRequestData,
      },
    ],
    response: CreateTaskViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/create-test-case/',
    alias: 'cases_createTestCase',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateTestCaseViewRequestData,
      },
    ],
    response: CreateTestCaseViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/create-value/',
    alias: 'cases_createValue',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateValueViewRequestData,
      },
    ],
    response: CreateValueViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/delete-assignment/',
    alias: 'cases_deleteAssignment',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteAssignmentViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/delete-bill-step/',
    alias: 'cases_deleteBillStep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteBillStepViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/delete-case/',
    alias: 'cases_deleteCase',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteCaseViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/delete-cases-tab/',
    alias: 'cases_deleteCasesTab',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteCasesTabViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/delete-comment/',
    alias: 'cases_deleteComment',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteCommentViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/delete-drafts/',
    alias: 'cases_deleteDrafts',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteDraftsViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/delete-expressions/',
    alias: 'cases_deleteExpressions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteExpressionsViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/delete-form-step/',
    alias: 'cases_deleteFormStep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteFormStepViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/delete-form-style-element/',
    alias: 'cases_deleteFormStyleElement',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteFormStyleElementViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/delete-input-substep/',
    alias: 'cases_deleteInputSubstep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteInputSubstepViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/delete-section-substep/',
    alias: 'cases_deleteSectionSubstep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteSectionSubstepViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/delete-section/',
    alias: 'cases_deleteSection',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteSectionViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/delete-step-assignment/',
    alias: 'cases_deleteStepAssignment',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteStepAssignmentViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/delete-template/',
    alias: 'cases_deleteTemplate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteTemplateViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/download-cases/',
    alias: 'cases_downloadCases',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DownloadCasesViewRequestData,
      },
    ],
    response: z.object({ email_recipient: z.string().email() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/edit-bill-step/',
    alias: 'cases_editBillStep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditBillStepViewRequestData,
      },
    ],
    response: EditBillStepViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/edit-case/',
    alias: 'cases_editCase',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditCaseViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/edit-cases-tab/',
    alias: 'cases_editCasesTab',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditCasesTabViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/edit-datetime-step/',
    alias: 'cases_editDateTimeStep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditDateTimeStepViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/edit-due-date/',
    alias: 'cases_editDueDate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditDueDateViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/edit-expression-name/',
    alias: 'cases_editExpressionName',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditExpressionNameViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/edit-form-step/',
    alias: 'cases_editFormStep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditFormStepViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/edit-form-style-element/',
    alias: 'cases_editFormStyleElement',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditFormStyleElementViewRequestData,
      },
    ],
    response: EditFormStyleElementViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/edit-identity-step/',
    alias: 'cases_editIdentityStep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditIdentityStepViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/edit-input-step/',
    alias: 'cases_editInputStep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditInputStepViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/edit-process/',
    alias: 'cases_editProcess',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditProcessViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/edit-section-substep/',
    alias: 'cases_editSectionSubStep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditSectionSubStepViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/edit-step/',
    alias: 'cases_editStep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditStepViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/edit-task/',
    alias: 'cases_editTask',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditTaskViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/generate-document/',
    alias: 'cases_generateDocument',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GenerateDocumentViewRequestData,
      },
    ],
    response: z.object({ document_url: z.string() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/get-bill-step/',
    alias: 'cases_getBillStep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetBillStepViewRequestData,
      },
    ],
    response: GetBillStepViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/get-case-count/',
    alias: 'cases_getCaseCount',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetCaseCountViewRequestData,
      },
    ],
    response: GetCaseCountViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/get-case-id/',
    alias: 'cases_getCaseId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetCaseIdViewRequestData,
      },
    ],
    response: GetCaseIdViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/get-case-owner/',
    alias: 'cases_getCaseOwner',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetCaseOwnerViewRequestData,
      },
    ],
    response: GetCaseOwnerViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/get-case/',
    alias: 'cases_getCase',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetCaseViewRequestData,
      },
    ],
    response: GetCaseViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/get-document-steps/',
    alias: 'cases_getDocumentSteps',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetDocumentStepsViewRequestData,
      },
    ],
    response: GetDocumentStepsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/get-document-template-fields/',
    alias: 'cases_getDocumentTemplateFields',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetDocumentTemplateFieldsViewRequestData,
      },
    ],
    response: z.object({ data: z.object({}).partial().passthrough() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/get-form-step-count/',
    alias: 'cases_getFormStepCount',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetFormStepCountViewRequestData,
      },
    ],
    response: z.object({ count: z.number().int() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/get-form-step-time/',
    alias: 'cases_getFormStepTime',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetFormStepTimeViewRequestData,
      },
    ],
    response: z.object({ average_duration: z.string().nullable() }).partial().passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/get-process-case-count/',
    alias: 'cases_getProcessCaseCount',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetProcessCaseCountViewRequestData,
      },
    ],
    response: z.object({ count: z.number().int() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/get-process/',
    alias: 'cases_getProcess',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetProcessViewRequestData,
      },
    ],
    response: GetProcessViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/get-task/',
    alias: 'cases_getTask',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetTaskViewRequestData,
      },
    ],
    response: GetTaskViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/import-email/',
    alias: 'cases_importEmail',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ImportEmailViewRequestData,
      },
    ],
    response: ImportEmailViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/instantiate-steps/',
    alias: 'cases_instantiateSteps',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: InstantiateStepsViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-annotated-documents/',
    alias: 'cases_listAnnotatedDocuments',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListAnnotatedDocumentsViewRequestData,
      },
    ],
    response: ListAnnotatedDocumentsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-case-automation-event-logs/',
    alias: 'cases_listCaseAutomationEventLogs',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListCaseAutomationEventLogsViewRequestData,
      },
    ],
    response: ListCaseAutomationEventLogsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-case-members/',
    alias: 'cases_listCaseMembers',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListCaseMembersViewRequestData,
      },
    ],
    response: ListCaseMembersViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-case-status-changes/',
    alias: 'cases_listCaseStatusChanges',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListCaseStatusChangesViewRequestData,
      },
    ],
    response: ListCaseStatusChangesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-cases-tabs/',
    alias: 'cases_listCasesTabs',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListCasesTabsViewRequestData,
      },
    ],
    response: ListCasesTabsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-cases-with-locations/',
    alias: 'cases_listCasesWithLocation',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListCasesWithLocationViewRequestData,
      },
    ],
    response: ListCasesWithLocationViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-cases/',
    alias: 'cases_listCases',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListCasesViewRequestData,
      },
    ],
    response: ListCasesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-comments/',
    alias: 'cases_listComments',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListCommentsViewRequestData,
      },
    ],
    response: ListCommentsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-drafts/',
    alias: 'cases_listDrafts',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListDraftsViewRequestData,
      },
    ],
    response: ListDraftsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-expressions/',
    alias: 'cases_listExpressions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListExpressionsViewRequestData,
      },
    ],
    response: ListExpressionsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-form-steps/',
    alias: 'cases_listFormSteps',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListFormStepsViewRequestData,
      },
    ],
    response: ListFormStepsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-forms/',
    alias: 'cases_listForms',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListFormsViewRequestData,
      },
    ],
    response: ListFormsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-input-substeps/',
    alias: 'cases_listInputSubSteps',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListInputSubStepsViewRequestData,
      },
    ],
    response: ListInputSubStepsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-inputs/',
    alias: 'cases_listInputs',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListInputsViewRequestData,
      },
    ],
    response: ListInputsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-items/',
    alias: 'cases_listItems',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListItemsViewRequestData,
      },
    ],
    response: ListItemsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-messages/',
    alias: 'cases_listMessages',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListMessagesViewRequestData,
      },
    ],
    response: ListMessagesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-nearby-cases/',
    alias: 'cases_listNearbyCases',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListNearbyCasesViewRequestData,
      },
    ],
    response: ListNearbyCasesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-notifications/',
    alias: 'cases_listNotifications',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListNotificationsViewRequestData,
      },
    ],
    response: ListNotificationsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-process-cases/',
    alias: 'cases_listProcessCases',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListProcessCasesViewRequestData,
      },
    ],
    response: ListProcessCasesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-process-summaries/',
    alias: 'cases_listProcessSummaries',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListProcessSummariesViewRequestData,
      },
    ],
    response: ListProcessSummariesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-processes-stats/',
    alias: 'cases_listProcessesStats',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListProcessesStatsViewRequestData,
      },
    ],
    response: ListProcessesStatsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-processes/',
    alias: 'cases_listProcesses',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListProcessesViewRequestData,
      },
    ],
    response: ListProcessesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-relations/',
    alias: 'cases_listRelations',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListRelationsViewRequestData,
      },
    ],
    response: ListRelationsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-section-substeps/',
    alias: 'cases_listSectionSubSteps',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListSectionSubStepsViewRequestData,
      },
    ],
    response: ListSectionSubStepsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-sections/',
    alias: 'cases_listSections',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListSectionsViewRequestData,
      },
    ],
    response: ListSectionsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-step-assignments',
    alias: 'cases_listStepAssignments',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListStepAssignmentsViewRequestData,
      },
    ],
    response: ListStepAssignmentsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-tasks/',
    alias: 'cases_listTasks',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListTasksViewRequestData,
      },
    ],
    response: ListTasksViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-templates/',
    alias: 'cases_listTemplates',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListTemplatesViewRequestData,
      },
    ],
    response: ListTemplatesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-values/',
    alias: 'cases_listValues',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListValuesViewRequestData,
      },
    ],
    response: ListValuesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/mark-case-read/',
    alias: 'cases_markCaseRead',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: MarkCaseReadViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/notify/',
    alias: 'cases_notify',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: NotifyViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/print-case/',
    alias: 'cases_print_case_create',
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/cases/remove-case-member/',
    alias: 'cases_removeCaseMember',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RemoveCaseMemberViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/resolve-thread/',
    alias: 'cases_resolveThread',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ResolveThreadViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/save-or-submit-form/',
    alias: 'cases_saveOrSubmitForm',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SaveOrSubmitFormViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/search-cases-count/',
    alias: 'cases_searchCasesCount',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SearchCasesCountViewRequestData,
      },
    ],
    response: z.object({ count: z.number().int() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/search-cases/',
    alias: 'cases_searchCases',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SearchCasesViewRequestData,
      },
    ],
    response: SearchCasesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/search-processes/',
    alias: 'cases_searchProcesses',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SearchProcessesViewRequestData,
      },
    ],
    response: SearchProcessesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/send-velosimo-case/',
    alias: 'cases_sendVelosimoCase',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SendVelosimoCaseViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/set-action-complete/',
    alias: 'cases_setActionComplete',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetActionCompleteViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/set-action-in-review/',
    alias: 'cases_setActionInReview',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetActionInReviewViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/set-case-status/',
    alias: 'cases_setCaseStatus',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetCaseStatusViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/set-step-assignments/',
    alias: 'cases_setStepAssignments',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetStepAssignmentsViewRequestData,
      },
    ],
    response: SetStepAssignmentsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/start-process/',
    alias: 'cases_startProcess',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: StartProcessViewRequestData,
      },
    ],
    response: StartProcessViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/upsert-cases-config/',
    alias: 'cases_upsertCasesConfig',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpsertCasesConfigViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/upsert-template/',
    alias: 'cases_upsertTemplate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpsertTemplateViewRequestData,
      },
    ],
    response: UpsertTemplateViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/add-address/',
    alias: 'contacts_addAddress',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddAddressViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/add-phone-number/',
    alias: 'contacts_addPhoneNumber',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddPhoneNumberViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/count-contacts/',
    alias: 'contacts_countContacts',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CountContactsViewRequestData,
      },
    ],
    response: z.object({ num_contacts: z.number().int() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/create-address/',
    alias: 'contacts_createAddress',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateAddressViewRequestData,
      },
    ],
    response: CreateAddressViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/create-contact-from-case/',
    alias: 'contacts_createContactFromCase',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateContactFromCaseViewRequestData,
      },
    ],
    response: CreateContactFromCaseViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/create-contact/',
    alias: 'contacts_createContact',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateContactViewRequestData,
      },
    ],
    response: CreateContactViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/create-email-address/',
    alias: 'contacts_createEmailAddress',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateEmailAddressViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/create-related-contact/',
    alias: 'contacts_createRelatedContact',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateRelatedContactViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/delete-contact/',
    alias: 'contacts_deleteContact',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteContactViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/delete-related-contact/',
    alias: 'contacts_deleteRelatedContact',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteRelatedContactViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/download-contacts/',
    alias: 'contacts_downloadContacts',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DownloadContactsViewRequestData,
      },
    ],
    response: z.object({ email_recipient: z.string().email() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/draft-email/',
    alias: 'contacts_draftEmail',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DraftEmailViewRequestData,
      },
    ],
    response: DraftEmailViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/email-signup/',
    alias: 'contacts_emailSignup',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EmailSignupViewRequestData,
      },
    ],
    response: EmailSignupViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/get-contact-entry-expanded/',
    alias: 'contacts_getContactEntryExpanded',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetContactEntryExpandedViewRequestData,
      },
    ],
    response: GetContactEntryExpandedViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/get-contact-entry-id/',
    alias: 'contacts_getContactEntryId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetContactEntryIdViewRequestData,
      },
    ],
    response: GetContactEntryIdViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/get-contact-entry-identity-ids/',
    alias: 'contacts_getContactEntryIdentityIds',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetContactEntryIdentityIdsViewRequestData,
      },
    ],
    response: GetContactEntryIdentityIdsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/get-contact-entry/',
    alias: 'contacts_getContactEntry',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetContactEntryViewRequestData,
      },
    ],
    response: GetContactEntryViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/get-contacts-header/',
    alias: 'contacts_getContactsHeader',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetContactsHeaderViewRequestData,
      },
    ],
    response: GetContactsHeaderViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/get-email-address/',
    alias: 'contacts_get_email_address_create',
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/contacts/get-email-signature/',
    alias: 'contacts_get_email_signature_create',
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/contacts/get-my-location/',
    alias: 'contacts_getMyLocation',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetMyLocationViewRequestData,
      },
    ],
    response: GetMyLocationViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/get-organization-location/',
    alias: 'contacts_getOrganizationLocation',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetOrganizationLocationViewRequestData,
      },
    ],
    response: GetOrganizationLocationViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/handle-inbound-email/',
    alias: 'contacts_handleInboundEmail',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: HandleInboundEmailViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/import-contacts/',
    alias: 'contacts_importContacts',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ImportContactsViewRequestData,
      },
    ],
    response: z.object({ email_address_conflict: z.boolean() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/list-addresses/',
    alias: 'contacts_listAddresses',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListAddressesViewRequestData,
      },
    ],
    response: ListAddressesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/list-contact-emails/',
    alias: 'contacts_listContactEmails',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListContactEmailsViewRequestData,
      },
    ],
    response: ListContactEmailsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/list-contact-entries-address-points/',
    alias: 'contacts_listContactEntriesAddressPoints',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListContactEntriesAddressPointsViewRequestData,
      },
    ],
    response: ListContactEntriesAddressPointsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/list-contact-entries/',
    alias: 'contacts_listContactEntries',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListContactEntriesViewRequestData,
      },
    ],
    response: ListContactEntriesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/list-contact-identities/',
    alias: 'contacts_listContactIdentities',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListContactIdentitiesViewRequestData,
      },
    ],
    response: ListContactIdentitiesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/list-contact-owners/',
    alias: 'contacts_listContactOwners',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListContactOwnersViewRequestData,
      },
    ],
    response: ListContactOwnersViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/list-contact-relations/',
    alias: 'contacts_listContactRelations',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListContactRelationsViewRequestData,
      },
    ],
    response: ListContactRelationsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/list-email-addresses/',
    alias: 'contacts_listEmailAddresses',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListEmailAddressesViewRequestData,
      },
    ],
    response: ListEmailAddressesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/list-email-participants/',
    alias: 'contacts_list_email_participants_create',
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/contacts/list-emails/',
    alias: 'contacts_listEmails',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListEmailsViewRequestData,
      },
    ],
    response: ListEmailsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/list-phone-numbers/',
    alias: 'contacts_listPhoneNumbers',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListPhoneNumbersViewRequestData,
      },
    ],
    response: ListPhoneNumbersViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/obtain-email-address/',
    alias: 'contacts_obtainEmailAddress',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ObtainEmailAddressViewRequestData,
      },
    ],
    response: ObtainEmailAddressViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/quick-create-address/',
    alias: 'contacts_quickCreateAddress',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: QuickCreateAddressViewRequestData,
      },
    ],
    response: QuickCreateAddressViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/remove-address/',
    alias: 'contacts_removeAddress',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RemoveAddressViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/remove-email-address/',
    alias: 'contacts_removeEmailAddress',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RemoveEmailAddressViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/remove-phone-number/',
    alias: 'contacts_removePhoneNumber',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RemovePhoneNumberViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/reverse-search-addresses/',
    alias: 'contacts_reverseSearchAddresses',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ReverseSearchAddressesViewRequestData,
      },
    ],
    response: ReverseSearchAddressesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/search-addresses/',
    alias: 'contacts_searchAddresses',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SearchAddressesViewRequestData,
      },
    ],
    response: SearchAddressesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/search-contact-entries/',
    alias: 'contacts_searchContactEntries',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SearchContactEntriesViewRequestData,
      },
    ],
    response: SearchContactEntriesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/search-contacts/',
    alias: 'contacts_searchContacts',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SearchContactsViewRequestData,
      },
    ],
    response: SearchContactsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/search-emails/',
    alias: 'contacts_searchEmails',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SearchEmailsViewRequestData,
      },
    ],
    response: SearchEmailsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/send_verification_email/',
    alias: 'contacts_sendVerificationEmail',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SendVerificationEmailViewRequestData,
      },
    ],
    response: z.object({ success: z.boolean() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/send-email/',
    alias: 'contacts_sendEmail',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SendEmailViewRequestData,
      },
    ],
    response: SendEmailViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/send-mass-email/',
    alias: 'contacts_sendMassEmail',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SendMassEmailViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/set-email-signature/',
    alias: 'contacts_setEmailSignature',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetEmailSignatureViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/set-primary-email-address/',
    alias: 'contacts_setPrimaryEmailAddress',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetPrimaryEmailAddressViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/set-reply-to/',
    alias: 'contacts_setReplyTo',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetReplyToViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/verify-email/',
    alias: 'contacts_verifyEmail',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: VerifyEmailViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/verify-login/',
    alias: 'contacts_verifyLogin',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: VerifyLoginViewRequestData,
      },
    ],
    response: VerifyLoginViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/add-admin/',
    alias: 'core_addAdmin',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddAdminViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/add-permission/',
    alias: 'core_addPermission',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddPermissionViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/add-role/',
    alias: 'core_addRole',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddRoleViewRequestData,
      },
    ],
    response: AddRoleViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/breakdown-contact-tags/',
    alias: 'core_breakdownContactTags',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: BreakdownContactTagsViewRequestData,
      },
    ],
    response: BreakdownContactTagsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/breakdown-tags/',
    alias: 'core_breakdownTags',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: BreakdownTagsViewRequestData,
      },
    ],
    response: BreakdownTagsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/change-password/',
    alias: 'core_changePassword',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ChangePasswordViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/create-attribution/',
    alias: 'core_createAttribution',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateAttributionViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/create-department/',
    alias: 'core_createDepartment',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateDepartmentViewRequestData,
      },
    ],
    response: CreateDepartmentViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/create-note/',
    alias: 'core_createNote',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateNoteViewRequestData,
      },
    ],
    response: CreateNoteViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/create-tag/',
    alias: 'core_createTag',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateTagViewRequestData,
      },
    ],
    response: CreateTagViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/delete-note/',
    alias: 'core_deleteNote',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteNoteViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/delete-tag/',
    alias: 'core_deleteTag',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteTagViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/edit-contact-birthday/',
    alias: 'core_editContactBirthday',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditContactBirthdayViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/edit-contact-image/',
    alias: 'core_editContactImage',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditContactImageViewRequestData,
      },
    ],
    response: z.object({ image: z.string() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/edit-contact-name/',
    alias: 'core_editContactName',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditContactNameViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/edit-role/',
    alias: 'core_editRole',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditRoleViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/edit-tag/',
    alias: 'core_editTag',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditTagViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/get-account/',
    alias: 'core_getAccount',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetAccountViewRequestData,
      },
    ],
    response: GetAccountViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/get-identity-basic/',
    alias: 'core_getIdentityBasic',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetIdentityBasicViewRequestData,
      },
    ],
    response: GetIdentityBasicViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/get-identity/',
    alias: 'core_getIdentity',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetIdentityViewRequestData,
      },
    ],
    response: GetIdentityViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/get-note/',
    alias: 'core_getNote',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetNoteViewRequestData,
      },
    ],
    response: GetNoteViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/get-person-name/',
    alias: 'core_get_person_name_create',
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/core/get-user-from-session/',
    alias: 'core_getUserFromSession',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ _body: z.string() }).partial().passthrough(),
      },
    ],
    response: GetUserFromSessionViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/get-user-ids/',
    alias: 'core_getUserIds',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetUserIdsViewRequestData,
      },
    ],
    response: GetUserIdsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/get-user/',
    alias: 'core_getUser',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetUserViewRequestData,
      },
    ],
    response: GetUserViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/identity-to-user/',
    alias: 'core_identityToUser',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: IdentityToUserViewRequestData,
      },
    ],
    response: IdentityToUserViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/list-attributions/',
    alias: 'core_listAttributions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListAttributionsViewRequestData,
      },
    ],
    response: ListAttributionsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/list-distinct-email-identities/',
    alias: 'core_listDistinctEmailIdentities',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListDistinctEmailIdentitiesViewRequestData,
      },
    ],
    response: ListDistinctEmailIdentitiesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/list-identities-by-ids/',
    alias: 'core_listIdentitiesByIds',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListIdentitiesByIdsViewRequestData,
      },
    ],
    response: ListIdentitiesByIdsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/list-identities/',
    alias: 'core_listIdentities',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListIdentitiesViewRequestData,
      },
    ],
    response: ListIdentitiesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/list-identity-permissions/',
    alias: 'core_listIdentityPermissions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListIdentityPermissionsViewRequestData,
      },
    ],
    response: ListIdentityPermissionsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/list-members/',
    alias: 'core_listMembers',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListMembersViewRequestData,
      },
    ],
    response: ListMembersViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/list-notes/',
    alias: 'core_listNotes',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListNotesViewRequestData,
      },
    ],
    response: ListNotesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/list-organizations/',
    alias: 'core_listOrganizations',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListOrganizationsViewRequestData,
      },
    ],
    response: ListOrganizationsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/list-participants/',
    alias: 'core_listParticipants',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListParticipantsViewRequestData,
      },
    ],
    response: ListParticipantsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/list-quill-dashboards/',
    alias: 'core_listQuillDashboards',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListQuillDashboardsViewRequestData,
      },
    ],
    response: ListQuillDashboardsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/list-roles/',
    alias: 'core_listRoles',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListRolesViewRequestData,
      },
    ],
    response: ListRolesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/list-staffers/',
    alias: 'core_listStaffers',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListStaffersViewRequestData,
      },
    ],
    response: ListStaffersViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/list-taggings/',
    alias: 'core_listTaggings',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListTaggingsViewRequestData,
      },
    ],
    response: ListTaggingsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/list-tags/',
    alias: 'core_listTags',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListTagsViewRequestData,
      },
    ],
    response: ListTagsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/login/',
    alias: 'core_userLogin',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UserLoginViewRequestData,
      },
    ],
    response: UserLoginViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/logout/',
    alias: 'core_userLogout',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UserLogoutViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/remove-member/',
    alias: 'core_removeMember',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RemoveMemberViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/remove-permission/',
    alias: 'core_removePermission',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RemovePermissionViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/remove-role/',
    alias: 'core_removeRole',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RemoveRoleViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/reset-password-request/',
    alias: 'core_resetPasswordRequest',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ email: z.string().email() }).passthrough(),
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/reset-password/',
    alias: 'core_resetPassword',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ResetPasswordViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/search-attributes/',
    alias: 'core_searchAttributes',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SearchAttributesViewRequestData,
      },
    ],
    response: SearchAttributesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/search-tags/',
    alias: 'core_searchTags',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SearchTagsViewRequestData,
      },
    ],
    response: SearchTagsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/set-auto-cc-case-emails/',
    alias: 'core_setAutoCcCaseEmails',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetAutoCcCaseEmailsViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/set-contacts-owner',
    alias: 'core_set_contacts_owner_create',
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/core/set-digest-frequency/',
    alias: 'core_setDigestFrequency',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetDigestFrequencyViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/set-identity-auto-payout/',
    alias: 'core_setIdentityAutoPayout',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetIdentityAutoPayoutViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/set-identity-contact-book/',
    alias: 'core_setIdentityContactBook',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetIdentityContactBookViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/set-notify/',
    alias: 'core_setNotify',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetNotifyViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/set-password/',
    alias: 'core_setPassword',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetPasswordViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/set-person-birthday/',
    alias: 'core_setPersonBirthday',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetPersonBirthdayRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/set-person-name/',
    alias: 'core_setPersonName',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetPersonNameRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/set-skin/',
    alias: 'core_setSkin',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetSkinViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/tag/',
    alias: 'core_tag',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: TagViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/unattribute/',
    alias: 'core_unattribute',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UnattributeViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/untag/',
    alias: 'core_untag',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UntagViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/update-note/',
    alias: 'core_updateNote',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateNoteViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/upload-asset/',
    alias: 'core_uploadAsset',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UploadAssetViewRequestData,
      },
    ],
    response: UploadAssetViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/db-check/',
    alias: 'api_app_localDBCheck',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ _body: z.string() }).partial().passthrough(),
      },
    ],
    response: z.object({ using_local_db: z.boolean() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/health/',
    alias: 'health_retrieve',
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/hooks/firecrawl/crawl',
    alias: 'hooks_firecrawl_crawl_create',
    description: `Handles webhook notifications from Firecrawl API.`,
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/hooks/handle-cityworks-event/',
    alias: 'hooks_handle_cityworks_event_create',
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/hooks/stripe/process-event',
    alias: 'hooks_stripe_process_event_create',
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/hooks/twilio/confirm-payment/:bill_id/',
    alias: 'hooks_twilio_confirm_payment_create',
    description: `Handles payment confirmation and initiates secure payment collection`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'bill_id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: 'post',
    path: '/hooks/twilio/find-bill/',
    alias: 'hooks_twilio_find_bill_create',
    description: `Processes the lookup code and finds the bill`,
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/hooks/twilio/payment-status/',
    alias: 'hooks_twilio_payment_status_create',
    description: `Webhook for Twilio payment status callbacks`,
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/hooks/twilio/payment-wait/:payment_id/',
    alias: 'hooks_twilio_payment_wait_create',
    description: `Checks payment status and provides appropriate response`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'payment_id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: 'post',
    path: '/hooks/twilio/process-payment/:bill_id/',
    alias: 'hooks_twilio_process_payment_create',
    description: `Processes payment after Twilio Pay completes`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'bill_id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: 'post',
    path: '/hooks/twilio/welcome/',
    alias: 'hooks_twilio_welcome_create',
    description: `Initial entry point for Twilio calls - presents welcome message and collects lookup code`,
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'get',
    path: '/hooks/velosimo/list-cases',
    alias: 'hooks_velosimo_list_cases_retrieve',
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'get',
    path: '/hooks/velosimo/list-processes',
    alias: 'hooks_velosimo_list_processes_retrieve',
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'get',
    path: '/hooks/velosimo/list-tenant-processes',
    alias: 'hooks_velosimo_list_tenant_processes_retrieve',
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/hooks/velosimo/upsert-cases',
    alias: 'hooks_velosimo_upsert_cases_create',
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/payments/add-charge/',
    alias: 'payments_addCharge',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddChargeViewRequestData,
      },
    ],
    response: AddChargeViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/add-payment/',
    alias: 'payments_addPayment',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddPaymentViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/confirm-payment/',
    alias: 'payments_confirmPayment',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ConfirmPaymentViewRequestData,
      },
    ],
    response: ConfirmPaymentViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/connect-stripe/',
    alias: 'payments_connectStripe',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ConnectStripeViewRequestData,
      },
    ],
    response: z.object({ url: z.string().url() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/count-payments/',
    alias: 'payments_countPayments',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CountPaymentsViewRequestData,
      },
    ],
    response: z.object({ count: z.number().int() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/create-lookup-entry/',
    alias: 'payments_createLookupEntry',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateLookupEntryViewRequestData,
      },
    ],
    response: CreateLookupEntryViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/create-lookup/',
    alias: 'payments_createLookup',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateLookupViewRequestData,
      },
    ],
    response: CreateLookupViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/create-payout/',
    alias: 'payments_createPayout',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreatePayoutViewRequestData,
      },
    ],
    response: CreatePayoutViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/delete-charge/',
    alias: 'payments_deleteCharge',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteChargeViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/delete-payment/',
    alias: 'payments_delete_payment_create',
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/payments/edit-bill/',
    alias: 'payments_editBill',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditBillViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/ftp-auth/',
    alias: 'payments_fTPAuth',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: FTPAuthViewRequestData,
      },
    ],
    response: FTPAuthViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/get-bill-count/',
    alias: 'payments_getBillCount',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetBillCountViewRequestData,
      },
    ],
    response: z.object({ count: z.number().int() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/get-lookup/',
    alias: 'payments_getLookup',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetLookupViewRequestData,
      },
    ],
    response: GetLookupViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/get-payment-info/',
    alias: 'payments_getPaymentInfo',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetPaymentInfoViewRequestData,
      },
    ],
    response: GetPaymentInfoViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/get-payment-total/',
    alias: 'payments_getPaymentTotal',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetPaymentTotalViewRequestData,
      },
    ],
    response: z.object({ total: z.number().int() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/get-payments-stats/',
    alias: 'payments_getPaymentsStats',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetPaymentsStatsViewRequestData,
      },
    ],
    response: GetPaymentsStatsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/get-revenue-breakdown/',
    alias: 'payments_getRevenueBreakdown',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetRevenueBreakdownViewRequestData,
      },
    ],
    response: GetRevenueBreakdownViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/get-revenue-types-breakdown/',
    alias: 'payments_getRevenueTypesBreakdown',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetRevenueTypesBreakdownViewRequestData,
      },
    ],
    response: GetRevenueTypesBreakdownViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/import-bills/',
    alias: 'payments_importBills',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ImportBillsViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/list-bills/',
    alias: 'payments_listBills',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListBillsViewRequestData,
      },
    ],
    response: ListBillsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/list-charge-substeps',
    alias: 'payments_listChargeSubsteps',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListChargeSubstepsViewRequestData,
      },
    ],
    response: ListChargeSubstepsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/list-lookup-entries/',
    alias: 'payments_listLookupEntries',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListLookupEntriesViewRequestData,
      },
    ],
    response: ListLookupEntriesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/list-lookups/',
    alias: 'payments_listLookups',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListLookupsViewRequestData,
      },
    ],
    response: ListLookupsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/list-payment-export-rules/',
    alias: 'payments_listPaymentExportRules',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListPaymentExportRulesViewRequestData,
      },
    ],
    response: ListPaymentExportRulesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/list-payments/',
    alias: 'payments_listPayments',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListPaymentsViewRequestData,
      },
    ],
    response: ListPaymentsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/list-payouts/',
    alias: 'payments_listPayouts',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListPayoutsViewRequestData,
      },
    ],
    response: ListPayoutsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/list-sources/',
    alias: 'payments_listStripeSources',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListStripeSourcesViewRequestData,
      },
    ],
    response: ListStripeSourcesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/obtain-payment-intent/',
    alias: 'payments_obtainPaymentIntent',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ObtainPaymentIntentViewRequestData,
      },
    ],
    response: ObtainPaymentIntentViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/reconcile/',
    alias: 'payments_reconcile',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ReconcileViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/search-bills/',
    alias: 'payments_searchBills',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SearchBillsViewRequestData,
      },
    ],
    response: SearchBillsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/search-lookup-entries/',
    alias: 'payments_searchLookupEntries',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SearchLookupEntriesViewRequestData,
      },
    ],
    response: SearchLookupEntriesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/send-bill-request/',
    alias: 'payments_sendBillRequest',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SendBillRequestViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/upsert-payment-export-rule/',
    alias: 'payments_upsertPaymentExportRule',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpsertPaymentExportRuleViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/verify-stripe-source/',
    alias: 'payments_verifyStripeSource',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: VerifyStripeSourceViewRequestData,
      },
    ],
    response: z
      .object({ status: z.string().default('ok') })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/schema/',
    alias: 'schema_retrieve',
    description: `OpenApi3 schema for this API. Format can be selected via content negotiation.

- YAML: application/vnd.oai.openapi
- JSON: application/vnd.oai.openapi+json`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'format',
        type: 'Query',
        schema: z.enum(['json', 'yaml']).optional(),
      },
      {
        name: 'lang',
        type: 'Query',
        schema: z
          .enum([
            'af',
            'ar',
            'ar-dz',
            'ast',
            'az',
            'be',
            'bg',
            'bn',
            'br',
            'bs',
            'ca',
            'ckb',
            'cs',
            'cy',
            'da',
            'de',
            'dsb',
            'el',
            'en',
            'en-au',
            'en-gb',
            'eo',
            'es',
            'es-ar',
            'es-co',
            'es-mx',
            'es-ni',
            'es-ve',
            'et',
            'eu',
            'fa',
            'fi',
            'fr',
            'fy',
            'ga',
            'gd',
            'gl',
            'he',
            'hi',
            'hr',
            'hsb',
            'hu',
            'hy',
            'ia',
            'id',
            'ig',
            'io',
            'is',
            'it',
            'ja',
            'ka',
            'kab',
            'kk',
            'km',
            'kn',
            'ko',
            'ky',
            'lb',
            'lt',
            'lv',
            'mk',
            'ml',
            'mn',
            'mr',
            'ms',
            'my',
            'nb',
            'ne',
            'nl',
            'nn',
            'os',
            'pa',
            'pl',
            'pt',
            'pt-br',
            'ro',
            'ru',
            'sk',
            'sl',
            'sq',
            'sr',
            'sr-latn',
            'sv',
            'sw',
            'ta',
            'te',
            'tg',
            'th',
            'tk',
            'tr',
            'tt',
            'udm',
            'uk',
            'ur',
            'uz',
            'vi',
            'zh-hans',
            'zh-hant',
          ])
          .optional(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
])

export const api = new Zodios(endpoints)

export function createApiClient(baseUrl: string, options?: ZodiosOptions) {
  return new Zodios(baseUrl, endpoints, options)
}
